import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { payCarePass } from "../../services/carePassPayment";
import { buildAuthOverlayUrl, AUTH_ACTIONS } from "../../utils/authNavigation";
import {
  buildServiceSelection,
  CAREHUB_HIRE_STEP,
  getCareHubHireWizardProgress,
  getServiceStepTitle,
  getSlotHint,
  migrateCareHubHireWizardStep,
  offersPoliceVerificationForShift,
  resolveCareHubRole,
} from "../../config/careHubBookingConfig";
import { showToast } from "../../utils/toast";
import {
  formatUserFacingError,
  notifyUserFacingError,
  USER_FACING_ERRORS,
} from "../../utils/userFacingErrors";
import DuplicateActiveEngagementModal, {
  isDuplicateActiveEngagementError,
} from "../customer/DuplicateActiveEngagementModal";
import { isCustomerProfileComplete } from "../../utils/customerJourney";
import {
  buildRecurringOccurrences,
  CAREHUB_RECURRING_OCCURRENCE_COUNT,
  clampDaysServiceRequired,
  DAYS_SERVICE_REQUIRED_MAX,
  DAYS_SERVICE_REQUIRED_MIN,
  formatOccurrenceSummary,
  recurringPatternIsAuto,
  recurringPatternNeedsDatePicker,
  recurringPatternNeedsWeeklyDay,
  todayIsoLocal,
  addDaysIso,
  WEEKDAY_NAMES,
} from "../../utils/careHubBookingRecurrence";
import {
  formatCareHubSlotLabel,
  formatCareHubTime,
  getCustomerBookingBlocksForDuration,
  getFixedBlocksForDuration,
  getShiftBookingBlocks,
  isFixedBlockCoveredBySlots,
} from "../../config/careHubRosterConfig";
import { normalizeGender } from "../../utils/genderNormalize";
import { caregiverGenderFilterFromPatient } from "../../utils/patientMarketplaceSearch";
import { resolveNpidCode, formatNcidForDisplay } from "../../utils/ncidClient";
import ProfileAvatar from "../shared/ProfileAvatar";
import CareHubBookingServiceStep from "./CareHubBookingServiceStep";
import BookingCarePassStep from "./BookingCarePassStep";
import PatientSelectStep, {
  BookingSummaryStep,
  BookingConfirmedStep,
  BookingFlowSidebar,
  formatInr,
} from "./PatientBookingSteps";
import DigitalCareAgreementPreview from "./DigitalCareAgreementPreview";
import PoliceRecordVerificationCard from "../customer/PoliceRecordVerificationCard";
import {
  shouldShowHourlyServicePackages,
} from "../../config/careRoleGuidelines";
import { SEARCH_SHIFT_OPTIONS } from "../../config/marketplaceFilterEngine";
import { patientHasValidCarePassForShift, carePassMissingForBookingMessage, getCarePassDaysRemaining } from "../../config/careShiftWindows";
import { unwrapPatientSubscriptionStatus } from "../../utils/marketplaceCarePassGate";
import { isCareTypeBookingEnabled } from "../../config/careServiceAvailability";
import {
  buildDigitalCareAgreement,
  generateAgreementRef,
} from "../../config/digitalCareAgreement";
import { getShiftDailyRateInr, resolveShiftSalaryBilling } from "../../utils/caregiverShiftPricing";
import { caregiverOffersAttendantPackage } from "../../utils/attendantPackageOffer";
import {
  fetchHourlySlotsForAllDates,
  fetchMatchesAvailableForOccurrences,
  resolveHourlyMarketplaceDates,
  unwrapRecurringAvailabilityResult,
  validateCaregiverAvailableForOccurrences,
} from "../../config/hourlyMarketplaceSearch";
import {
  assertCaregiverAvailableOnDates,
  fetchBookingCalendarAvailability,
  isCalendarDateSelectable,
} from "../../utils/caregiverDateAvailability";
import BookingAvailabilityCalendar from "./BookingAvailabilityCalendar";
import SorryMessageModal from "../shared/SorryMessageModal";
import "./CareAttendantBookingDialog.css";
import "./BookingFlowLayout.css";

const ACCEPTED_BOOKING_STATUSES = new Set(["ACCEPTED", "CONFIRMED", "IN_PROGRESS", "RESCHEDULED"]);
const DECLINED_BOOKING_STATUSES = new Set(["REJECTED", "CANCELLED", "OPS_ESCALATION", "DECLINED"]);

const SHIFT_OVERLAP_PREFERENCE_MESSAGE =
  "Due to shift overlapping Care Attendant is not accepting the request. Kindly, select another Care Attendant from the preference list";

/** Prefer the real backend reason over a generic "caregiver unavailable" toast. */
function hireAvailabilityErrorMessage(err, fallback = "Unable to continue this booking.") {
  const code = String(err?.code || err?.body?.code || err?.data?.code || "");
  const msg = String(err?.message || err?.body?.message || "").trim();
  if (
    code === "BOOKING_START_TIME_PAST" ||
    code === "PAST_TIME" ||
    /already started or passed|cannot be in the past|time has already passed/i.test(msg)
  ) {
    return (
      msg ||
      "That visit time has already passed. Choose a later start date or time for your booking."
    );
  }
  if (
    code === "OUTSIDE_WORKING_HOURS" ||
    code === "CAREGIVER_ON_LEAVE" ||
    code === "CAREGIVER_ENGAGED" ||
    code === "CAREGIVER_BLOCKED_ON_DATE"
  ) {
    return msg || fallback;
  }
  if (
    code === "SESSION_OVERLAP" ||
    code === "CAREGIVER_OVERLAP" ||
    code === "RECURRENCE_CONFLICT" ||
    code === "CARE_GIVER_SLOT_UNAVAILABLE" ||
    /overlaps an existing|no longer available for the selected|not available for all selected/i.test(msg)
  ) {
    return (
      msg ||
      "This caregiver is no longer available for the selected date and time. Please choose another available caregiver."
    );
  }
  return msg || fallback;
}

function isRemappedSlotTakenError(err) {
  const code = String(err?.code || err?.body?.code || err?.data?.code || "");
  const msg = String(err?.message || "");
  if (
    code === "BOOKING_START_TIME_PAST" ||
    code === "PAST_TIME" ||
    code === "OUTSIDE_WORKING_HOURS" ||
    code === "CAREGIVER_ON_LEAVE"
  ) {
    return false;
  }
  return (
    code === "SESSION_OVERLAP" ||
    code === "CAREGIVER_OVERLAP" ||
    code === "RECURRENCE_CONFLICT" ||
    code === "CARE_GIVER_SLOT_UNAVAILABLE" ||
    /overlaps an existing booking|no longer available for the selected|not available for all selected/i.test(
      msg
    )
  );
}

const BOOKING_DRAFT_KEY = "careHubBookingDraft";

const PATTERNS = [
  {
    id: "instant",
    badge: "Express · 60 min",
    badgeTone: "express",
    title: "Instant booking",
    description: "Dispatches an active caregiver to arrive within 1 hour.",
  },
  {
    id: "single",
    badge: "Scheduled",
    badgeTone: "scheduled",
    title: "Single appointment",
    description: "Book a custom date and time slot in advance.",
  },
  {
    id: "recurring",
    badge: "Recurring",
    badgeTone: "subscription",
    title: "Recurring booking",
    description: "Auto-reserves future slots daily, weekly, or on alternate days.",
  },
];

const RECURRING_OPTIONS = [
  { id: "daily", label: "Daily" },
  { id: "alternate", label: "Alternate Days" },
  { id: "weekly", label: "Weekly" },
  { id: "weekdays", label: "Weekdays" },
  { id: "custom", label: "Custom Dates" },
];

function formatDisplayDate(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return String(iso);
  return `${d} - ${m} - ${y}`;
}

/** Normalize HH:MM / H:MM / HH:MM:SS → HH:MM */
function normalizeClockHm(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  const hh = String(Math.min(23, Number(match[1]))).padStart(2, "0");
  const mm = String(Math.min(59, Number(match[2]))).padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * PG `date` values often arrive as UTC midnights (e.g. IST calendar day → previous UTC evening).
 * Convert to YYYY-MM-DD in the booking timezone.
 */
function serviceDateToIsoDate(value, timeZone = "Asia/Kolkata") {
  if (value == null || value === "") return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const d = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(d.getTime())) {
    const sliced = String(value).slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(sliced) ? sliced : null;
  }
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

function extractCustomerBookingsList(res) {
  if (Array.isArray(res?.bookings)) return res.bookings;
  if (Array.isArray(res?.data?.bookings)) return res.data.bookings;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res)) return res;
  return [];
}

function isOpenCareHubHireBooking(hit) {
  if (!hit || typeof hit !== "object") return false;
  const status = String(hit.status || hit.bookingStatus || "").toUpperCase();
  if (DECLINED_BOOKING_STATUSES.has(status) || status === "COMPLETED") return false;
  if (!ACCEPTED_BOOKING_STATUSES.has(status) && status !== "PENDING") return false;
  const details = hit.details_json || hit.details || {};
  const deploy = details.customerPortalDeployment || details.deployment || {};
  if (deploy.dutyDeployed || details.agreementAcceptedAt) return false;
  // Expired CareHub hold — do not treat as an open hire that must be resumed.
  const holdExpiresAt = details.holdExpiresAt ? Date.parse(String(details.holdExpiresAt)) : NaN;
  if (
    Number.isFinite(holdExpiresAt) &&
    holdExpiresAt <= Date.now() &&
    (status === "PENDING" || status === "RESCHEDULED")
  ) {
    return false;
  }
  return (
    details.careHubGuest === true ||
    details.careHubGuest === "true" ||
    details.customerSelectedCaregiver === true ||
    details.customerSelectedCaregiver === "true" ||
    (details.careHub && typeof details.careHub === "object")
  );
}

/** True when a persisted CareHub hire is a 24h live-in duty. */
function isLiveInCareHubHire(hitOrConfirmed) {
  if (!hitOrConfirmed || typeof hitOrConfirmed !== "object") return false;
  const details =
    hitOrConfirmed.details_json ||
    hitOrConfirmed.details ||
    hitOrConfirmed.booking?.details_json ||
    hitOrConfirmed.booking?.details ||
    {};
  const shift = String(
    hitOrConfirmed.searchShift ||
      details.searchShift ||
      details?.agreement?.shiftSchedule ||
      details?.schedule?.searchShift ||
      ""
  ).toLowerCase();
  if (shift === "24h" || shift.includes("24") || shift.includes("live")) return true;
  const serviceId = String(details.serviceId || hitOrConfirmed.serviceId || "").toLowerCase();
  if (serviceId.includes("24h") || serviceId.includes("live")) return true;
  const duration = Number(
    details.schedule?.durationMinutes || details.durationMinutes || hitOrConfirmed.durationMinutes || 0
  );
  if (duration >= 24 * 60) return true;
  const start = normalizeClockHm(hitOrConfirmed.startTime || details.schedule?.startTime);
  const end = normalizeClockHm(
    hitOrConfirmed.endTime || details.schedule?.endTime || hitOrConfirmed.startTime
  );
  return Boolean(start && end && start === end && (shift || serviceId || duration));
}

/** Resume an open hire only when it matches this wizard's live-in vs hourly context. */
function openHireMatchesWizardContext(hit, { isLiveIn = false, searchShift = "" } = {}) {
  if (!isOpenCareHubHireBooking(hit)) return false;
  const hireIsLiveIn = isLiveInCareHubHire(hit);
  if (Boolean(isLiveIn) !== hireIsLiveIn) return false;
  const details = hit.details_json || hit.details || {};
  const bookingShift = String(details.searchShift || searchShift || "").toLowerCase();
  const wizardShift = String(searchShift || "").toLowerCase();
  if (wizardShift && bookingShift && wizardShift !== bookingShift) {
    // Allow resume when both are non-live-in hourly-family without exact token match.
    if (hireIsLiveIn || wizardShift === "24h") return false;
  }
  return true;
}

/** True when UI slot matches the accepted/pending care request times. */
function confirmedBookingMatchesSlot(confirmed, slot) {
  if (!confirmed || !slot) return false;
  const bookedStart = normalizeClockHm(confirmed.startTime);
  if (!bookedStart) return false;
  const slotStart = normalizeClockHm(slot.start);
  if (bookedStart !== slotStart) return false;
  const bookedEnd = normalizeClockHm(confirmed.endTime || confirmed.startTime);
  const slotEnd = normalizeClockHm(slot.end || slot.start);
  const confirmedLiveIn = isLiveInCareHubHire(confirmed) || bookedEnd === bookedStart;
  const slotLiveIn = Boolean(slotStart && slotEnd && slotStart === slotEnd);
  // Live-in chips use end === start; hourly 2h chips must not match a live-in open hire.
  if (confirmedLiveIn !== slotLiveIn) return false;
  if (confirmedLiveIn) return true;
  return bookedEnd === slotEnd;
}

function isOwnActiveBookingSlot(confirmed, caregiverId, slot) {
  if (!confirmed?.bookingId || !slot) return false;
  if (confirmed.caregiverDeclined) return false;
  if (!confirmed.caregiverAccepted && !confirmed.awaitingCaregiverAcceptance) return false;
  if (
    caregiverId != null &&
    String(confirmed.requestedCaregiverId) !== String(caregiverId)
  ) {
    return false;
  }
  return confirmedBookingMatchesSlot(confirmed, slot);
}

/** Keep the customer's own pending/accepted slot selectable after the booking holds it. */
function applyOwnBookingSlotOverride(slotList, matchList, confirmed, { isLiveIn = false } = {}) {
  if (!confirmed?.bookingId || !confirmed.startTime) {
    return { slots: slotList, matches: matchList };
  }
  if (confirmed.caregiverDeclined) {
    return { slots: slotList, matches: matchList };
  }
  if (!confirmed.caregiverAccepted && !confirmed.awaitingCaregiverAcceptance) {
    return { slots: slotList, matches: matchList };
  }
  // Do not inject a 24h start chip into an hourly 2-hour slot grid (or vice versa).
  if (Boolean(isLiveIn) !== isLiveInCareHubHire(confirmed)) {
    return { slots: slotList, matches: matchList };
  }
  const start = normalizeClockHm(confirmed.startTime);
  const end = normalizeClockHm(confirmed.endTime || confirmed.startTime) || start;
  if (!start) return { slots: slotList, matches: matchList };

  // With an open hire, unlock the booked start and keep other starts selectable for reschedule
  // once the API has excluded this booking (they arrive as available). Own start is always on.
  const slots = (slotList || []).map((s) =>
    confirmedBookingMatchesSlot(confirmed, s) ? { ...s, available: true } : s
  );
  const hasSlot = slots.some((s) => confirmedBookingMatchesSlot(confirmed, s));
  if (!hasSlot) {
    slots.push({
      id: `${start}-${end}`,
      start,
      end,
      label: formatCareHubSlotLabel(start, end),
      available: true,
    });
    slots.sort((a, b) => String(a.start).localeCompare(String(b.start)));
  }

  const cgId = String(confirmed.requestedCaregiverId || "");
  let matches = (matchList || []).map((m) => {
    if (String(m.caregiverId) !== cgId) return m;
    const already = (m.slots || []).some((s) => slotMatchesSelection(s, { start, end }));
    if (already) {
      return {
        ...m,
        slots: (m.slots || []).map((s) =>
          slotMatchesSelection(s, { start, end }) ? { ...s, available: true } : s
        ),
      };
    }
    return {
      ...m,
      slots: [...(m.slots || []), { startTime: start, endTime: end, start, end, available: true }],
    };
  });
  if (cgId && !matches.some((m) => String(m.caregiverId) === cgId)) {
    matches = [
      {
        caregiverId: cgId,
        name: confirmed.caregiverName || "Selected caregiver",
        slots: [{ startTime: start, endTime: end, start, end, available: true }],
        isPreferredFallback: true,
      },
      ...matches,
    ];
  }

  return { slots, matches };
}

function readBookingTimesFromRow(hit) {
  if (!hit || typeof hit !== "object") {
    return { startTime: null, endTime: null, serviceDate: null };
  }
  const details = hit.details_json || hit.details || {};
  const schedule = details.schedule || {};
  const startTime = normalizeClockHm(
    hit.start_time || hit.startTime || schedule.startTime || details.startTime || ""
  );
  const endTime = normalizeClockHm(
    hit.end_time || hit.endTime || schedule.endTime || details.endTime || startTime || ""
  );
  const serviceDate = serviceDateToIsoDate(
    hit.service_date || hit.serviceDate || hit.date || schedule.date || details.serviceDate || ""
  );
  return {
    startTime: /^\d{2}:\d{2}$/.test(startTime) ? startTime : null,
    endTime: /^\d{2}:\d{2}$/.test(endTime) ? endTime : null,
    serviceDate,
  };
}

function buildConfirmedBookingFromRow(hit, fallbackCaregiverId = null) {
  if (!hit) return null;
  const status = String(hit.status || hit.bookingStatus || "").toUpperCase();
  const accepted = ACCEPTED_BOOKING_STATUSES.has(status);
  const times = readBookingTimesFromRow(hit);
  const details = hit.details_json || hit.details || {};
  const reselectRequired = Boolean(details.careHubDispatch?.reselectRequired);
  const excludedCaregiverIds = Array.isArray(details.matching?.excludedCaregiverIds)
    ? details.matching.excludedCaregiverIds
    : [];
  const caregiverCleared =
    (hit.caregiver_id == null && hit.caregiverId == null) &&
    (reselectRequired || excludedCaregiverIds.length > 0);
  const caregiverDeclined = reselectRequired || caregiverCleared;
  const cgId =
    hit.caregiver_id ??
    hit.caregiverId ??
    details.requestedCaregiverId ??
    details.careHubDispatch?.releasedCaregiverId ??
    fallbackCaregiverId ??
    null;
  return {
    booking: hit,
    bookingId: hit.id || hit.bookingId,
    requestedCaregiverId: cgId,
    caregiverName: hit.caregiver_name || hit.caregiverName || details.caregiverName || "",
    patientName: hit.patient_name || hit.patientName || details.patientName || "",
    dateLabel: times.serviceDate ? formatDisplayDate(times.serviceDate) : "",
    timeLabel: times.startTime ? `Start ${formatCareHubTime(times.startTime)}` : "",
    startTime: times.startTime,
    endTime: times.endTime || times.startTime,
    serviceDate: times.serviceDate,
    searchShift: details.searchShift || details?.agreement?.shiftSchedule || null,
    serviceId: details.serviceId || null,
    durationMinutes: details.schedule?.durationMinutes || details.durationMinutes || null,
    subscriptionLabel: details.planCode || "Service subscription",
    prvInitiated: false,
    awaitingCaregiverAcceptance: status === "PENDING" && !accepted && !caregiverDeclined,
    caregiverAccepted: accepted,
    caregiverDeclined,
    reselectRequired,
    excludedCaregiverIds,
    holdExpiresAt:
      details.holdExpiresAt ||
      details.acceptDeadlineAt ||
      hit.holdExpiresAt ||
      hit.acceptDeadlineAt ||
      null,
  };
}

function parseSlotKey(key) {
  const match = String(key || "").match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
  if (!match) return null;
  return { start: match[1], end: match[2] };
}

/** 24h live-in uses the same clock time next day — do not send that as a same-day end. */
function isLiveInClockTimes(startTime, endTime, liveIn) {
  if (liveIn) return true;
  const start = String(startTime || "").slice(0, 5);
  const end = String(endTime || "").slice(0, 5);
  return Boolean(start) && start === end;
}

function slotsFromKeySet(slotSet, { preferFixedBlocks = false, durationMinutes = 120, searchShift = "" } = {}) {
  if (preferFixedBlocks) {
    return getFixedBlocksForDuration(durationMinutes, searchShift)
      .filter(
        (block) =>
          slotSet.has(`${block.start}-${block.end}`) ||
          isFixedBlockCoveredBySlots(block.start, block.end, slotSet)
      )
      .map((block) => ({
        id: `${block.start}-${block.end}`,
        start: block.start,
        end: block.end,
        label: block.label || formatCareHubSlotLabel(block.start, block.end),
      }));
  }

  return [...slotSet]
    .map((key) => {
      const parsed = parseSlotKey(key);
      if (!parsed) return null;
      const startMin = Number(parsed.start.slice(0, 2)) * 60 + Number(parsed.start.slice(3, 5));
      const endMin = Number(parsed.end.slice(0, 2)) * 60 + Number(parsed.end.slice(3, 5));
      if (!Number.isFinite(startMin) || !Number.isFinite(endMin) || endMin <= startMin) return null;
      return {
        id: key,
        start: parsed.start,
        end: parsed.end,
        label: formatCareHubSlotLabel(parsed.start, parsed.end),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start.localeCompare(b.start));
}

function resolveMatchGender(match) {
  return normalizeGender(match?.gender);
}

/** Marketplace Hire Now target is only "selected" if it still fits this patient. */
function preferredCaregiverFitsPatient(caregiver, lockedCaregiverGender) {
  if (!caregiver) return false;
  if (!lockedCaregiverGender) return true;
  const gender = resolveMatchGender(caregiver);
  if (!gender) return true;
  return gender === lockedCaregiverGender;
}

function slotIsAvailableInSet(slotSet, block) {
  return (
    slotSet.has(`${block.start}-${block.end}`) ||
    isFixedBlockCoveredBySlots(block.start, block.end, slotSet)
  );
}

function buildSlotKeySet(matches = []) {
  const slotSet = new Set();
  for (const match of matches) {
    for (const slot of match?.slots || []) {
      const start = String(slot.startTime || slot.start || "").slice(0, 5);
      const end = String(slot.endTime || slot.end || "").slice(0, 5);
      if (!start || !end) continue;
      slotSet.add(`${start}-${end}`);
    }
  }
  return slotSet;
}

/**
 * Build the customer slot grid. When preferredCaregiverId is present, availability
 * follows that caregiver so blocked hours are greyed out. Day-blocked caregivers
 * are omitted from matches upstream and never appear in the list.
 */
function collectSlotsFromMatches(
  matches = [],
  {
    serviceRole = "attendant",
    durationMinutes = 120,
    searchShift = "",
    preferRawSlots = false,
    preferredCaregiverId = null,
  } = {}
) {
  const preferredId = preferredCaregiverId != null ? String(preferredCaregiverId) : "";
  const preferredMatch = preferredId
    ? matches.find((m) => String(m.caregiverId) === preferredId)
    : null;
  // Selected caregiver drives grey/available; otherwise any match with open windows.
  const availabilityMatches = preferredMatch ? [preferredMatch] : matches;
  const slotSet = buildSlotKeySet(availabilityMatches);

  const liveIn =
    preferRawSlots ||
    String(searchShift).toLowerCase() === "24h" ||
    Number(durationMinutes) >= 24 * 60;

  if (liveIn) {
    const blocks = getShiftBookingBlocks("24h") || [];
    if (blocks.length) {
      return blocks.map((block) => {
        const key = `${block.start}-${block.end || block.start}`;
        // Live-in API keys are start===end (e.g. 10:00-10:00). Do not treat an
        // hourly "10:00-12:00" key as a free 24h start — that made the date look
        // open and then failed at confirm with caregiver already booked.
        const available =
          slotSet.has(key) || slotSet.has(`${block.start}-${block.start}`);
        return {
          id: key,
          start: block.start,
          end: block.end || block.start,
          label: block.label || `Start ${formatCareHubTime(block.start)}`,
          available,
        };
      });
    }
    return [...slotSet]
      .map((key) => {
        const parsed = parseSlotKey(key);
        if (!parsed) return null;
        return {
          id: key,
          start: parsed.start,
          end: parsed.end,
          label: `Start ${formatCareHubTime(parsed.start)}`,
          available: true,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.start.localeCompare(b.start));
  }

  if (serviceRole === "attendant" || getShiftBookingBlocks(searchShift)?.length) {
    return getCustomerBookingBlocksForDuration(durationMinutes, searchShift).map((block) => ({
      id: `${block.start}-${block.end}`,
      start: block.start,
      end: block.end,
      label: block.label || formatCareHubSlotLabel(block.start, block.end),
      available: slotIsAvailableInSet(slotSet, block),
    }));
  }

  return slotsFromKeySet(slotSet, {
    preferFixedBlocks: false,
    durationMinutes,
    searchShift,
  });
}

function resolveCaregiverVisitFeeInr(caregiverOrMatch, searchShift, fallbackFee = null) {
  // 12h / 24h: only caregiver Pricing & Plans (shiftPricing). Never trust stale catalog estimates.
  const fromShift = getShiftDailyRateInr(caregiverOrMatch, searchShift);
  if (fromShift != null) return fromShift;

  const shiftKey = String(searchShift || "").trim().toLowerCase();
  const isShiftHire =
    shiftKey === "24h" ||
    shiftKey === "12_day" ||
    shiftKey === "12_night" ||
    shiftKey.includes("24") ||
    shiftKey.includes("12");
  if (isShiftHire) {
    // Do not fall back to estimatedCaregiverFeeInr / catalog invents for shift hires.
    return null;
  }

  const fromMatch = Number(caregiverOrMatch?.estimatedCaregiverFeeInr);
  if (Number.isFinite(fromMatch) && fromMatch > 0) return Math.round(fromMatch);
  const fromFallback = Number(fallbackFee);
  if (Number.isFinite(fromFallback) && fromFallback > 0) return Math.round(fromFallback);
  return null;
}

function buildPreferredMatchEntry(caregiver, caregiverFee, slots = [], searchShift = "") {
  const shiftPricing = caregiver?.shiftPricing || caregiver?.profileExtended?.shiftPricing || null;
  const fee = resolveCaregiverVisitFeeInr(
    {
      ...caregiver,
      shiftPricing,
      // Only pass estimated fee for non-shift hires; shift fees come from shiftPricing.
      estimatedCaregiverFeeInr: null,
    },
    searchShift,
    null
  );
  return {
    caregiverId: caregiver.id || caregiver.caregiverId,
    name: caregiver.name || caregiver.fullName || "Selected caregiver",
    gender: resolveMatchGender(caregiver) || null,
    rating: caregiver.rating ?? caregiver.publicRating ?? 0,
    distanceKm: caregiver.distanceKm ?? caregiver.distance_km ?? null,
    travelBufferMin: 25,
    skills: caregiver.skills || caregiver.certifications || [],
    estimatedCaregiverFeeInr: fee,
    shiftPricing,
    feeSource: fee != null ? "shiftPricing" : null,
    profileImageUrl: caregiver.profileImageUrl || caregiver.avatarUrl,
    npid: resolveNpidCode(caregiver.npid || caregiver.providerId) || "",
    slots,
    isPreferredFallback: slots.length === 0,
  };
}

function slotMatchesSelection(slot, selected) {
  if (!slot || !selected) return false;
  const start = String(slot.startTime || slot.start || "").slice(0, 5);
  const end = String(slot.endTime || slot.end || "").slice(0, 5);
  return start === selected.start && end === selected.end;
}

function caregiverHasSelectedSlot(match, selectedSlot) {
  if (!selectedSlot) return false;
  if ((match?.slots || []).some((s) => slotMatchesSelection(s, selectedSlot))) return true;

  const slotSet = new Set();
  for (const slot of match?.slots || []) {
    const start = String(slot.startTime || slot.start || "").slice(0, 5);
    const end = String(slot.endTime || slot.end || "").slice(0, 5);
    if (start && end) slotSet.add(`${start}-${end}`);
  }
  return isFixedBlockCoveredBySlots(selectedSlot.start, selectedSlot.end, slotSet);
}

/** Matchmaker row shape so hire wizard slot step recognizes a pre-picked marketplace caregiver. */
function buildHireMatchRowForSlot(caregiver, slot) {
  const seededId = caregiver?.id || caregiver?.caregiverId;
  if (!seededId) return null;
  const start = String(slot?.start || slot?.startTime || "").slice(0, 5);
  const end = String(slot?.end || slot?.endTime || slot?.start || slot?.startTime || "").slice(
    0,
    5
  );
  if (!start) return null;
  return {
    ...caregiver,
    id: seededId,
    caregiverId: seededId,
    name: caregiver?.name || caregiver?.fullName || "Care Attendant",
    slots: [
      {
        startTime: start,
        endTime: end,
        start,
        end,
        available: true,
      },
    ],
  };
}

function seedMarketplaceHireMatchState({ caregiver, slot, setMatches, setSelectedCaregiver, setSlotValidatedFor }) {
  const row = buildHireMatchRowForSlot(caregiver, slot);
  if (!row) return false;
  setMatches([row]);
  setSelectedCaregiver(row);
  if (setSlotValidatedFor && slot?.start) {
    setSlotValidatedFor({
      caregiverId: String(row.caregiverId),
      start: String(slot.start || slot.startTime || "").slice(0, 5),
      end: String(slot.end || slot.endTime || slot.start || slot.startTime || "").slice(0, 5),
    });
  }
  return true;
}

function readBookingDraft() {
  try {
    const raw = sessionStorage.getItem(BOOKING_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeBookingDraft(draft) {
  // Stamp the currently authenticated account (null for a guest/pre-login draft) so
  // a later login by a *different* account can detect and refuse to resume it.
  const ownerUserId = api.getCurrentUserIdFromToken ? api.getCurrentUserIdFromToken() : null;
  sessionStorage.setItem(BOOKING_DRAFT_KEY, JSON.stringify({ ...draft, ownerUserId }));
}

function clearBookingDraft() {
  sessionStorage.removeItem(BOOKING_DRAFT_KEY);
}

/** Prefer draft/URL id only when it belongs to this account's patient list. */
function pickOwnedPatientId(list, preferredId) {
  const patients = Array.isArray(list) ? list : [];
  const preferred = String(preferredId || "").trim();
  if (preferred && patients.some((p) => String(p?.patientId || "").trim() === preferred)) {
    return preferred;
  }
  if (patients.length === 1) return String(patients[0]?.patientId || "").trim();
  const primary = patients.find((p) => p?.isPrimary);
  if (primary?.patientId) return String(primary.patientId).trim();
  return "";
}

function extractPatientsList(res) {
  const list = res?.patients || res?.data?.patients || [];
  return Array.isArray(list) ? list : [];
}

const SHIFT_ICON_PROPS = {
  viewBox: "0 0 24 24",
  width: 24,
  height: 24,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

/** Customer-facing names for the booking shift types. */
const SHIFT_CHOICE_META = {
  hourly: {
    label: "Hourly Care",
    hint: "A visit of a few hours at a time you choose",
    icon: (
      <svg {...SHIFT_ICON_PROPS}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    ),
  },
  "12_day": {
    label: "12-Hour Day",
    hint: "Day shift — the caregiver stays for 12 hours",
    icon: (
      <svg {...SHIFT_ICON_PROPS}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </svg>
    ),
  },
  "12_night": {
    label: "12-Hour Night",
    hint: "Night shift — the caregiver stays for 12 hours",
    icon: (
      <svg {...SHIFT_ICON_PROPS}>
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
      </svg>
    ),
  },
  "24h": {
    label: "24-Hour Live-In",
    hint: "The caregiver lives in and cares round the clock",
    icon: (
      <svg {...SHIFT_ICON_PROPS}>
        <path d="M3 10.5L12 3l9 7.5" />
        <path d="M5 9.5V21h14V9.5" />
        <path d="M10 21v-6h4v6" />
      </svg>
    ),
  },
};

function shiftChoiceMeta(value, fallbackLabel = "") {
  const key = String(value || "").trim().toLowerCase();
  return SHIFT_CHOICE_META[key] || { label: fallbackLabel || "Care service", hint: "", icon: SHIFT_CHOICE_META.hourly.icon };
}

/** Customer service pin only — never fall back to caregiver coordinates. */
function parseServiceCoords(latValue, lngValue) {
  const serviceLat = Number(latValue);
  const serviceLng = Number(lngValue);
  if (!Number.isFinite(serviceLat) || !Number.isFinite(serviceLng)) return null;
  if (serviceLat < -90 || serviceLat > 90 || serviceLng < -180 || serviceLng > 180) return null;
  return { lat: serviceLat, lng: serviceLng };
}

export default function CareHubBookingDialog({
  open,
  caregiver,
  onClose,
  lat = null,
  lng = null,
  addressLabel = "",
  resumeDraft = null,
  /**
   * Shift the customer explicitly chose before Hire Now ("hourly" | "12_day" | "12_night" | "24h").
   * Empty = not chosen: the wizard opens on a mandatory service/shift selection (never defaults).
   */
  searchShift: searchShiftProp = "",
  /** Shift choices offered in the wizard (enabled platform shifts). Defaults to all. */
  shiftOptions = null,
  /**
   * Optional: parent handles a switch to Hourly Care itself (e.g. marketplace time-slot search).
   * Called with the new shift; the parent is expected to close the wizard.
   */
  onChooseHourlyShift = null,
  /** Patient already chosen for marketplace search — skip re-select on Hire Now. */
  initialPatientId = "",
  /**
   * Prefill from hourly marketplace pre-search (package / pattern / date / slot).
   * When skipShiftRedo + selectedSlot are set, wizard lands on SLOTS after patient/pass.
   */
  searchPrefill = null,
  /** Hourly marketplace: return to pre-search popup instead of SHIFT calendar UI. */
  onBackToHourlySearch = null,
  /** Keep parent resumeDraft in sync when the user navigates back inside the wizard. */
  onDraftSync = null,
  /** Resume a declined CareHub hire by booking id (shows replacement alternatives in SLOTS). */
  reselectBookingId = null,
}) {
  const navigate = useNavigate();
  const careRole = useMemo(() => resolveCareHubRole(caregiver), [caregiver]);
  const wizardShiftOptions = useMemo(() => {
    const base = Array.isArray(shiftOptions) && shiftOptions.length ? shiftOptions : SEARCH_SHIFT_OPTIONS;
    const list = careRole === "physio" ? base.filter((o) => o.value === "hourly") : base;
    return list.length ? list : SEARCH_SHIFT_OPTIONS.filter((o) => o.value === "hourly");
  }, [shiftOptions, careRole]);
  // Shift type is a mandatory, editable booking attribute owned by the wizard.
  const [searchShift, setWizardShift] = useState(() => String(searchShiftProp || "").trim());
  const [shiftChosen, setShiftChosen] = useState(() => Boolean(String(searchShiftProp || "").trim()));
  const [shiftPickerOpen, setShiftPickerOpen] = useState(false);
  const [shiftPickerValue, setShiftPickerValue] = useState(() => String(searchShiftProp || "").trim());
  /** Customer switched shift inside the wizard — the marketplace slot prefill no longer applies. */
  const [shiftChangedInWizard, setShiftChangedInWizard] = useState(false);
  const shiftRecheckRef = useRef(false);
  const [step, setStep] = useState(1);
  const [duplicateEngagement, setDuplicateEngagement] = useState(null);
  const [packageKey, setPackageKey] = useState("hygiene_personal");
  const [tierId, setTierId] = useState("t2");
  const [procedureId, setProcedureId] = useState("injection");
  const [procedureQuery, setProcedureQuery] = useState("");
  const [sessionId, setSessionId] = useState("short_assessment");
  const [pattern, setPattern] = useState("single");
  const [recurringPattern, setRecurringPattern] = useState("daily");
  const [daysServiceRequired, setDaysServiceRequired] = useState(1);
  const [targetDate, setTargetDate] = useState(todayIsoLocal());
  const [selectedDates, setSelectedDates] = useState([]);
  const [weeklyDayOfWeek, setWeeklyDayOfWeek] = useState(1);
  const [customDateInput, setCustomDateInput] = useState(todayIsoLocal());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [guidelinesAcknowledged, setGuidelinesAcknowledged] = useState(false);
  const [agreementAcknowledged, setAgreementAcknowledged] = useState(false);
  const [agreementRef, setAgreementRef] = useState(() => generateAgreementRef());

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    api
      .getCareHubAgreementReference()
      .then((res) => {
        const ref = res?.ref || res?.data?.ref;
        if (!cancelled && ref) setAgreementRef(String(ref));
      })
      .catch(() => {
        /* keep client fallback ref for preview */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);
  const [matches, setMatches] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  /** Set only after live validate succeeds for the current slot + caregiver pair. */
  const [slotValidatedFor, setSlotValidatedFor] = useState(null);
  /** Pin used for matchmaker + estimate (locked when slots are generated / restored). */
  const [serviceCoords, setServiceCoords] = useState(() => parseServiceCoords(lat, lng));
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [validatingSlot, setValidatingSlot] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [patientSubscription, setPatientSubscription] = useState(null);
  const [prvRequired, setPrvRequired] = useState(null);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [replacementCaregivers, setReplacementCaregivers] = useState([]);
  const [loadingReplacements, setLoadingReplacements] = useState(false);
  const [acceptPollBusy, setAcceptPollBusy] = useState(false);
  const [holdCountdownLabel, setHoldCountdownLabel] = useState("");
  const [deployAddressOk, setDeployAddressOk] = useState(false);
  const [deployBusy, setDeployBusy] = useState(false);
  const [shiftOverlapModalOpen, setShiftOverlapModalOpen] = useState(false);
  const [calendarDaysByDate, setCalendarDaysByDate] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState("");
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [calendarReloadKey, setCalendarReloadKey] = useState(0);
  /** Prevents lat/lng/prefill churn from wiping mid-hire / Care Pass resume state. */
  const bookingInitSessionRef = useRef("");
  /** Ensures Care Pass / login resume runs once per open — not again on every slot pick. */
  const carePassResumeHandledRef = useRef("");
  const marketplaceHireValidatedRef = useRef("");
  /** Blocks stale init/resume async from re-opening Care Pass after the user tapped Back. */
  const userWizardBackRef = useRef(false);
  const lastAvailabilityContextRef = useRef({ patientId: "", serviceKey: "" });
  /** After the customer changes patient/service, do not restore the old marketplace slot. */
  const slotPrefillDiscardedRef = useRef(false);
  const slotsGenerationRef = useRef(0);

  const isLoggedIn = Boolean(api.getToken?.());

  function clearGeneratedAvailability({ discardPrefill = true } = {}) {
    slotsGenerationRef.current += 1;
    setAvailableSlots([]);
    setSelectedSlot(null);
    setMatches([]);
    setReplacementCaregivers([]);
    setSelectedCaregiver(null);
    setSlotValidatedFor(null);
    marketplaceHireValidatedRef.current = "";
    if (discardPrefill) slotPrefillDiscardedRef.current = true;
  }

  function showShiftOverlapPreferencePopup() {
    setShiftOverlapModalOpen(true);
  }

  function closeShiftOverlapPreferencePopup({ returnToPreferences = false } = {}) {
    setShiftOverlapModalOpen(false);
    if (returnToPreferences) onClose?.();
  }

  useEffect(() => {
    if (!open) {
      userWizardBackRef.current = false;
      setShiftOverlapModalOpen(false);
      return undefined;
    }
    document.body.classList.add("cabd-open");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.classList.remove("cabd-open");
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    // Keep live status while a care request exists (pending OR accepted).
    // If the booking was cancelled/deleted, drop stale "Accepted" UI immediately.
    const trackingOnSlots =
      step === CAREHUB_HIRE_STEP.SLOTS &&
      confirmedBooking?.bookingId &&
      (confirmedBooking?.awaitingCaregiverAcceptance || confirmedBooking?.caregiverAccepted);
    if (!open || !trackingOnSlots) {
      return undefined;
    }
    let cancelled = false;

    async function pollAcceptance() {
      setAcceptPollBusy(true);
      try {
        const res = await api.getCustomerBookings();
        const list = Array.isArray(res?.bookings)
          ? res.bookings
          : Array.isArray(res?.data?.bookings)
            ? res.data.bookings
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res)
                ? res
                : [];
        const targetId = String(confirmedBooking.bookingId);
        const hit = list.find((b) => String(b?.id || b?.bookingId) === targetId);
        if (cancelled) return;
        if (!hit) {
          setConfirmedBooking(null);
          clearSelectionValidation();
          clearBookingDraft();
          showToast.info("That care request is no longer active. Send a new request to continue.");
          return;
        }
        const status = String(hit.status || hit.bookingStatus || "").toUpperCase();
        const accepted = ACCEPTED_BOOKING_STATUSES.has(status);
        const details = hit.details_json || hit.details || {};
        const holdExpiresAt =
          details.holdExpiresAt ||
          details.acceptDeadlineAt ||
          hit.holdExpiresAt ||
          hit.acceptDeadlineAt ||
          confirmedBooking.holdExpiresAt ||
          null;
        const holdExpired =
          details.holdStatus === "EXPIRED" ||
          (holdExpiresAt &&
            Number.isFinite(new Date(holdExpiresAt).getTime()) &&
            Date.now() > new Date(holdExpiresAt).getTime() &&
            !accepted &&
            status === "PENDING");
        const reselectRequired = Boolean(details?.careHubDispatch?.reselectRequired);
        const caregiverCleared =
          hit.caregiver_id == null &&
          hit.caregiverId == null &&
          Boolean(confirmedBooking.requestedCaregiverId);
        const declined =
          holdExpired ||
          DECLINED_BOOKING_STATUSES.has(status) ||
          (!accepted && status === "PENDING" && (reselectRequired || caregiverCleared));

        if (DECLINED_BOOKING_STATUSES.has(status)) {
          setConfirmedBooking(null);
          clearSelectionValidation();
          clearBookingDraft();
          showToast.info("That care request was cancelled or closed. Send a new request to continue.");
          return;
        }

        setConfirmedBooking((prev) => {
          if (!prev) return prev;
          const wasAwaiting = prev.awaitingCaregiverAcceptance;
          if (holdExpired && wasAwaiting) {
            showToast.info(
              "Your booking hold has expired. Please select the caregiver and time again."
            );
            clearBookingDraft();
            return null;
          }
          const times = readBookingTimesFromRow(hit);
          const excludedCaregiverIds = Array.isArray(details?.matching?.excludedCaregiverIds)
            ? details.matching.excludedCaregiverIds
            : prev.excludedCaregiverIds || [];
          const next = {
            ...prev,
            booking: hit,
            holdExpiresAt: holdExpiresAt || prev.holdExpiresAt || null,
            awaitingCaregiverAcceptance: !accepted && !declined,
            caregiverAccepted: accepted,
            caregiverDeclined: declined,
            reselectRequired: reselectRequired || declined,
            excludedCaregiverIds,
            startTime: times.startTime || prev.startTime || null,
            endTime: times.endTime || prev.endTime || null,
            serviceDate: times.serviceDate || prev.serviceDate || null,
            timeLabel:
              times.startTime && times.startTime !== prev.startTime
                ? `Start ${formatCareHubTime(times.startTime)}`
                : prev.timeLabel,
          };
          if (wasAwaiting && accepted) {
            showToast.success("Caregiver accepted your request. You can proceed.");
          } else if (wasAwaiting && declined) {
            showToast.info(
              "Care Attendant declined. Choose an alternative below for the same date and time."
            );
          }
          return next;
        });
        if (accepted) {
          const times = readBookingTimesFromRow(hit);
          const start = normalizeClockHm(times.startTime || confirmedBooking.startTime);
          const end = normalizeClockHm(
            times.endTime || confirmedBooking.endTime || times.startTime || confirmedBooking.startTime
          );
          if (start) {
            setAvailableSlots((prev) =>
              (prev || []).map((s) =>
                normalizeClockHm(s.start) === start ? { ...s, available: true } : s
              )
            );
            setSelectedSlot((prev) => {
              if (prev && normalizeClockHm(prev.start) === start) return prev;
              return {
                id: `${start}-${end || start}`,
                start,
                end: end || start,
                label: formatCareHubSlotLabel(start, end || start),
                available: true,
              };
            });
            const cgId = String(
              hit.caregiver_id || hit.caregiverId || confirmedBooking.requestedCaregiverId || ""
            );
            if (cgId) {
              setSelectedCaregiver((prev) => {
                if (prev && String(prev.caregiverId) === cgId) return prev;
                const fromMatches = matches.find((m) => String(m.caregiverId) === cgId);
                return fromMatches || prev;
              });
              markSelectionValidated(cgId, {
                start,
                end: end || start,
              });
            }
          }
        }
      } catch (err) {
        console.warn("Care request status poll failed:", err?.message || err);
      } finally {
        if (!cancelled) setAcceptPollBusy(false);
      }
    }

    pollAcceptance();
    const timer = setInterval(pollAcceptance, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    open,
    step,
    confirmedBooking?.bookingId,
    confirmedBooking?.awaitingCaregiverAcceptance,
    confirmedBooking?.caregiverAccepted,
  ]);

  // UI-only countdown from authoritative holdExpiresAt (never used to release the hold).
  useEffect(() => {
    const expiresAt = confirmedBooking?.holdExpiresAt;
    const awaiting = confirmedBooking?.awaitingCaregiverAcceptance;
    if (!open || !awaiting || !expiresAt) {
      setHoldCountdownLabel("");
      return undefined;
    }
    let expiredHandled = false;
    function tick() {
      const endMs = new Date(expiresAt).getTime();
      if (!Number.isFinite(endMs)) {
        setHoldCountdownLabel("");
        return;
      }
      const remaining = Math.max(0, Math.floor((endMs - Date.now()) / 1000));
      const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
      const ss = String(remaining % 60).padStart(2, "0");
      setHoldCountdownLabel(`${mm}:${ss}`);
      if (remaining <= 0 && !expiredHandled) {
        expiredHandled = true;
        setHoldCountdownLabel("");
        setConfirmedBooking((prev) => {
          // Never drop an already-accepted request when the hold clock hits zero.
          if (!prev || prev.caregiverAccepted || !prev.awaitingCaregiverAcceptance) {
            return prev;
          }
          setSelectedCaregiver(null);
          clearSelectionValidation();
          showToast.info(
            "Your booking hold has expired. Please select the caregiver and time again."
          );
          try {
            generateSlots();
          } catch {
            /* ignore */
          }
          return null;
        });
      }
    }
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [
    open,
    confirmedBooking?.holdExpiresAt,
    confirmedBooking?.awaitingCaregiverAcceptance,
  ]);

  const serviceSelection = useMemo(
    () =>
      buildServiceSelection({
        role: careRole,
        packageKey,
        tierId,
        procedureId,
        sessionId,
        caregiver,
        searchShift,
      }),
    [careRole, packageKey, tierId, procedureId, sessionId, caregiver, searchShift]
  );
  const isLiveIn = Number(serviceSelection.durationMinutes) >= 24 * 60 || searchShift === "24h";
  const offerPoliceVerification = offersPoliceVerificationForShift(
    searchShift,
    serviceSelection.durationMinutes
  );

  // Early heads-up: the marketplace list's "available" badge is a coarse, date/time-scoped
  // guess (see marketplace.service.js listMarketplaceCaregivers) — it is not slot-accurate for
  // whatever date/pattern the customer ends up picking here. Re-check this specific caregiver
  // against the default date as soon as they're selected, so a stale/optimistic list badge
  // surfaces as an early, non-blocking heads-up instead of a surprise once they reach the
  // slot grid. This does not replace generateSlots()'s own authoritative check later.
  const preSelectCheckedForRef = useRef("");
  useEffect(() => {
    const caregiverId = caregiver?.id || caregiver?.caregiverId;
    if (!open || !caregiverId || confirmedBooking || reselectBookingId) {
      preSelectCheckedForRef.current = "";
      return undefined;
    }
    const checkKey = String(caregiverId);
    if (preSelectCheckedForRef.current === checkKey) return undefined;
    preSelectCheckedForRef.current = checkKey;

    let cancelled = false;
    (async () => {
      const pin =
        parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng);
      if (!pin) return;
      try {
        const res = await api.listAvailableSlots({
          caregiverId,
          date: targetDate || todayIsoLocal(),
          duration: serviceSelection.durationMinutes,
          serviceRole: serviceSelection.serviceRole,
          serviceId: serviceSelection.serviceId,
          lat: pin.lat,
          lng: pin.lng,
          instantOnly: pattern === "instant",
        });
        // generateSlots() already ran (or is running) for this caregiver — it owns messaging now.
        if (cancelled || slotsGenerationRef.current > 0) return;
        const slots = Array.isArray(res?.slots) ? res.slots : [];
        if (slots.length) return;
        const peerName = caregiver?.name || caregiver?.fullName || "This caregiver";
        const messageByReason = {
          ON_LEAVE: `${peerName} is on leave today. Pick another date to see their next available slot, or choose another caregiver.`,
          NO_WORKING_HOURS: `${peerName} has no working hours today. Pick another date, or choose another caregiver.`,
          CAREGIVER_ENGAGED: `${peerName} already has a booking today. Pick another date, or choose another caregiver.`,
          SESSION_OVERLAP: `${peerName} already has a booking covering today. Pick another date, or choose another caregiver.`,
          RADIUS_EXCEEDED: `${peerName} is outside their working radius for your location. Choose another caregiver nearby.`,
          PACKAGE_NOT_OFFERED: `${peerName} does not offer this care package today. Try another package or caregiver.`,
          INSTANT_BOOKING_CLOSED: `${peerName} is not open for instant booking right now. Try a scheduled slot instead.`,
        };
        showToast.info(
          messageByReason[String(res?.excludedReason || "")] ||
            `${peerName} has no open slots for today — pick a date to see when they're next free, or choose another caregiver.`
        );
      } catch {
        /* best-effort heads-up only — generateSlots() still runs the authoritative check later */
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per caregiver selection on default date, not on every coord/pattern tweak
  }, [open, caregiver?.id, caregiver?.caregiverId, confirmedBooking, reselectBookingId, targetDate]);

  // Hourly hire must not resume a prior 24h acceptance (and vice versa).
  useEffect(() => {
    if (!open) return;
    if (!confirmedBooking?.bookingId) return;
    if (Boolean(isLiveIn) === isLiveInCareHubHire(confirmedBooking)) return;
    setConfirmedBooking(null);
    setSelectedSlot(null);
    setSelectedCaregiver(null);
    clearSelectionValidation();
    // Do not clear draft here — it may belong to another shift's open hire.
  }, [open, isLiveIn, confirmedBooking?.bookingId, searchShift]);

  async function handleAgreementContinue() {
    if (!confirmedBooking?.bookingId) return;
    if (!agreementAcknowledged || !deployAddressOk) {
      showToast.error("Confirm the service address and accept the e-agreement to continue.");
      return;
    }
    setDeployBusy(true);
    try {
      const res = await api.confirmBookingDeployment(confirmedBooking.bookingId, {
        addressConfirmed: true,
        agreementAccepted: true,
      });
      setConfirmedBooking((prev) =>
        prev
          ? {
              ...prev,
              booking: res?.booking || prev.booking,
              deployment: res?.customerPortalDeployment || res?.data?.customerPortalDeployment,
            }
          : prev
      );
      if (offerPoliceVerification) {
        setStep(CAREHUB_HIRE_STEP.PRV);
        showToast.success("Agreement accepted. Choose police verification to finish.");
      } else {
        clearBookingDraft();
        setStep(CAREHUB_HIRE_STEP.DONE);
        showToast.success("Agreement accepted. Hiring is complete.");
      }
    } catch (err) {
      showToast.error(err?.message || "Unable to confirm agreement.");
    } finally {
      setDeployBusy(false);
    }
  }

  // Today is always bookable (past-slot filtering happens at the time-slot level,
  // not by blocking the whole day) — see collectSlotsFromMatches / backend validateSlot.
  const minTargetDate = useMemo(() => todayIsoLocal(), []);

  const maxWindowDate = addDaysIso(todayIsoLocal(), 6);
  const maxTargetDate = pattern === "instant" ? todayIsoLocal() : maxWindowDate;

  const calendarCaregiverId = String(
    caregiver?.id || caregiver?.caregiverId || selectedCaregiver?.caregiverId || ""
  ).trim();

  const calendarPin = useMemo(
    () => parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng),
    [serviceCoords?.lat, serviceCoords?.lng, lat, lng]
  );

  useEffect(() => {
    // SHIFT and SLOTS share one schedule screen — keep calendar warm on both.
    if (
      !open ||
      (step !== CAREHUB_HIRE_STEP.SHIFT && step !== CAREHUB_HIRE_STEP.SLOTS) ||
      !calendarCaregiverId
    ) {
      return undefined;
    }
    let cancelled = false;
    setCalendarLoading(true);
    setCalendarError("");
    setCalendarLoaded(false);

    (async () => {
      const result = await fetchBookingCalendarAvailability(api, {
        caregiverId: calendarCaregiverId,
        fromDate: todayIsoLocal(),
        toDate: maxWindowDate,
        duration: serviceSelection.durationMinutes,
        serviceRole: serviceSelection.serviceRole || careRole,
        serviceId: serviceSelection.serviceId,
        lat: calendarPin?.lat,
        lng: calendarPin?.lng,
        instantOnly: pattern === "instant",
        excludeBookingId:
          confirmedBooking?.bookingId || readBookingDraft()?.bookingId || undefined,
      });
      if (cancelled) return;
      if (!result.ok) {
        setCalendarDaysByDate({});
        setCalendarError(result.error || "Unable to load caregiver availability");
        setCalendarLoaded(false);
        setCalendarLoading(false);
        return;
      }
      setCalendarDaysByDate(result.daysByDate || {});
      setCalendarError("");
      setCalendarLoaded(true);
      setCalendarLoading(false);

      const pickFirstAvailable = () => {
        let cursor = minTargetDate;
        while (cursor && cursor <= maxTargetDate) {
          if (
            isCalendarDateSelectable(cursor, {
              daysByDate: result.daysByDate,
              minDate: minTargetDate,
              maxDate: maxTargetDate,
              availabilityLoaded: true,
            })
          ) {
            return cursor;
          }
          cursor = addDaysIso(cursor, 1);
        }
        return null;
      };

      if (pattern === "instant") return;

      setTargetDate((prev) => {
        if (
          isCalendarDateSelectable(prev, {
            daysByDate: result.daysByDate,
            minDate: minTargetDate,
            maxDate: maxTargetDate,
            availabilityLoaded: true,
          })
        ) {
          return prev;
        }
        return pickFirstAvailable() || prev;
      });
      setSelectedDates((prev) =>
        (prev || []).filter((iso) =>
          isCalendarDateSelectable(iso, {
            daysByDate: result.daysByDate,
            minDate: minTargetDate,
            maxDate: maxWindowDate,
            availabilityLoaded: true,
          })
        )
      );
      setCustomDateInput((prev) => {
        if (
          isCalendarDateSelectable(prev, {
            daysByDate: result.daysByDate,
            minDate: minTargetDate,
            maxDate: maxWindowDate,
            availabilityLoaded: true,
          })
        ) {
          return prev;
        }
        return pickFirstAvailable() || prev;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    step,
    calendarCaregiverId,
    maxWindowDate,
    maxTargetDate,
    minTargetDate,
    serviceSelection.durationMinutes,
    serviceSelection.serviceRole,
    serviceSelection.serviceId,
    careRole,
    calendarPin?.lat,
    calendarPin?.lng,
    pattern,
    confirmedBooking?.bookingId,
    calendarReloadKey,
    packageKey,
    tierId,
    procedureId,
    sessionId,
    searchShift,
  ]);

  const caregiverFee =
    serviceSelection.caregiverFeeInr ??
    selectedCaregiver?.estimatedCaregiverFeeInr ??
    null;
  // Platform booking fee is disabled for customers (always ₹0 at confirm).
  const platformFee = 0;
  const platformGst = 0;
  const totalPayNow = 0;
  const selectedPatient = patients.find(
    (p) => String(p.patientId || "") === String(selectedPatientId || "")
  );
  const lockedCaregiverGender = useMemo(
    () => caregiverGenderFilterFromPatient(selectedPatient),
    [selectedPatient]
  );
  const preferredHireCaregiver = useMemo(
    () =>
      preferredCaregiverFitsPatient(caregiver, lockedCaregiverGender) ? caregiver : null,
    [caregiver, lockedCaregiverGender]
  );

  async function loadReplacementSuggestions(bookingId) {
    if (!bookingId) return;
    setLoadingReplacements(true);
    try {
      const res = await api.getBookingReplacementSuggestions(bookingId, { limit: 3 });
      const rows = (Array.isArray(res?.replacements) ? res.replacements : [])
        .map((match) => ({
          ...match,
          gender: resolveMatchGender(match) || null,
        }))
        .filter((match) => {
          if (lockedCaregiverGender && resolveMatchGender(match) !== lockedCaregiverGender) {
            return false;
          }
          return caregiverOffersAttendantPackage(match, {
            packageKey,
            serviceId: serviceSelection.serviceId,
          });
        });
      setReplacementCaregivers(rows.slice(0, 3));
      if (rows.length) {
        setMatches((prev) => {
          const byId = new Map((prev || []).map((m) => [String(m.caregiverId), m]));
          rows.forEach((m) => byId.set(String(m.caregiverId), m));
          return [...byId.values()];
        });
        setSelectedCaregiver(null);
        clearSelectionValidation();
      }
    } catch (err) {
      console.warn("Replacement suggestions failed:", err?.message || err);
      setReplacementCaregivers([]);
    } finally {
      setLoadingReplacements(false);
    }
  }

  useEffect(() => {
    if (!open || !confirmedBooking?.caregiverDeclined || !confirmedBooking?.bookingId) {
      return undefined;
    }
    loadReplacementSuggestions(confirmedBooking.bookingId);
    return undefined;
    // Refetch when another caregiver rejects (excluded list grows).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    confirmedBooking?.caregiverDeclined,
    confirmedBooking?.bookingId,
    (confirmedBooking?.excludedCaregiverIds || []).join(","),
  ]);

  const subscriptionUntilLabel = patientSubscription?.activeUntil
    ? formatDisplayDate(
        typeof patientSubscription.activeUntil === "string"
          ? patientSubscription.activeUntil.slice(0, 10)
          : new Date(patientSubscription.activeUntil).toISOString().slice(0, 10)
      )
    : patientSubscription?.endsAt
      ? formatDisplayDate(
          typeof patientSubscription.endsAt === "string"
            ? patientSubscription.endsAt.slice(0, 10)
            : new Date(patientSubscription.endsAt).toISOString().slice(0, 10)
        )
      : null;
  const showHourlyServicePackages = shouldShowHourlyServicePackages({
    serviceRole: careRole,
    searchShift,
  });
  const serviceStepTitle = getServiceStepTitle(careRole, {
    searchShift,
    showPackages: showHourlyServicePackages || careRole === "physio",
  });

  const slots = availableSlots;

  const bookingOccurrences = useMemo(() => {
    if (!targetDate) return [];
    if (pattern !== "recurring" || !selectedSlot) {
      return [{ serviceDate: targetDate, startTime: selectedSlot?.start, endTime: selectedSlot?.end }];
    }
    const dayCount = clampDaysServiceRequired(daysServiceRequired, 1);
    return buildRecurringOccurrences({
      recurringPattern,
      startDate: targetDate,
      startTime: selectedSlot.start,
      endTime: selectedSlot.end,
      selectedDates,
      weeklyDayOfWeek,
      count: dayCount,
    });
  }, [
    pattern,
    recurringPattern,
    targetDate,
    selectedSlot,
    selectedDates,
    weeklyDayOfWeek,
    daysServiceRequired,
  ]);

  const occurrenceSummary = useMemo(
    () => formatOccurrenceSummary(bookingOccurrences.filter((o) => o.serviceDate)),
    [bookingOccurrences]
  );

  const bookingCareDays = useMemo(() => {
    if (pattern !== "recurring") return 1;
    const fromOccurrences = new Set(
      bookingOccurrences
        .map((o) => String(o?.serviceDate || "").slice(0, 10))
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    ).size;
    return Math.max(1, clampDaysServiceRequired(daysServiceRequired, 1), fromOccurrences || 0);
  }, [pattern, bookingOccurrences, daysServiceRequired]);

  const visitCaregiverFeeInr = resolveCaregiverVisitFeeInr(
    {
      ...(selectedCaregiver || {}),
      estimatedCaregiverFeeInr:
        estimate?.invoice?.caregiverFeeInr ?? selectedCaregiver?.estimatedCaregiverFeeInr,
    },
    searchShift,
    null
  );
  const totalCaregiverFeeInr =
    visitCaregiverFeeInr != null ? visitCaregiverFeeInr * bookingCareDays : null;

  function formatCaregiverPackageFeeLabel(visitFeeInr, days = bookingCareDays) {
    if (visitFeeInr == null) return "As set by caregiver";
    const visit = Math.round(Number(visitFeeInr) || 0);
    const dayCount = Math.max(1, Number(days) || 1);
    const total = visit * dayCount;
    if (dayCount <= 1) return `${formatInr(total)} / visit`;
    return `${formatInr(total)} total (${formatInr(visit)}/day × ${dayCount} days)`;
  }

  const bookingSidebar = (
    <BookingFlowSidebar
      caregiverName={selectedCaregiver?.name}
      caregiverFeeLabel={formatCaregiverPackageFeeLabel(visitCaregiverFeeInr, bookingCareDays)}
      patientName={selectedPatient?.name || selectedPatientId}
      patientMeta={selectedPatient?.age ? `Age ${selectedPatient.age}` : selectedPatientId || null}
      subscriptionLabel={patientSubscription?.planCode || "Service subscription"}
      subscriptionActive={Boolean(patientSubscription?.active)}
      subscriptionUntil={subscriptionUntilLabel}
      verificationLabel={
        offerPoliceVerification
          ? "After duty deployment (12h / 24h)"
          : "Not required for hourly"
      }
      totalLabel={totalPayNow > 0 ? formatInr(totalPayNow) : "Pay after service"}
      totalHint={
        totalCaregiverFeeInr == null
          ? "Caregiver fee paid after service"
          : bookingCareDays > 1
            ? `Caregiver fee ${formatInr(totalCaregiverFeeInr)} total · paid after service`
            : "Caregiver fee paid after service"
      }
      secureNote={step >= CAREHUB_HIRE_STEP.CONFIRM}
    />
  );

  const carePassDaysRemaining = useMemo(
    () => getCarePassDaysRemaining(patientSubscription),
    [patientSubscription]
  );
  const carePassCoversBooking = useMemo(
    () => patientHasValidCarePassForShift(patientSubscription, searchShift, bookingCareDays),
    [patientSubscription, searchShift, bookingCareDays]
  );

  const isHourlySearchShift = String(searchShift || "").toLowerCase() === "hourly";
  const shiftSalaryBilling = useMemo(() => {
    if (isHourlySearchShift) {
      return { mode: null, dailyRateInr: null, monthlySalaryInr: null };
    }
    const source = selectedCaregiver || caregiver || {};
    return resolveShiftSalaryBilling({
      caregiver: source,
      searchShift,
      bookingDays: bookingCareDays,
    });
  }, [isHourlySearchShift, selectedCaregiver, caregiver, searchShift, bookingCareDays]);

  const caregiverHourlyRateInr = Number(
    selectedCaregiver?.hourlyRateInr ??
      selectedCaregiver?.hourlyRateExpectation ??
      selectedCaregiver?.hourlyRate ??
      caregiver?.hourlyRateInr ??
      caregiver?.hourlyRateExpectation ??
      caregiver?.hourlyRate ??
      caregiver?.hourly_rate_expectation
  );

  const digitalAgreement = useMemo(
    () =>
      buildDigitalCareAgreement({
        agreementRef,
        serviceRole: serviceSelection.serviceRole || careRole,
        searchShift,
        serviceLabel: serviceSelection.serviceLabel,
        packageKey,
        serviceId: serviceSelection.serviceId,
        caregiverName: selectedCaregiver?.name || caregiver?.name || caregiver?.fullName,
        startDate: bookingOccurrences[0]?.serviceDate || targetDate,
        startTime: bookingOccurrences[0]?.startTime || selectedSlot?.start || null,
        endTime: bookingOccurrences[0]?.endTime || selectedSlot?.end || null,
        patientName: selectedPatient?.name || selectedPatientId,
        patientId: selectedPatient?.patientId || selectedPatientId,
        patientAadhaarMasked: selectedPatient?.documents?.aadhaar?.numberMasked || "",
        serviceAddress: addressLabel || selectedPatient?.address || "Address to be confirmed",
        // ≤30 days: per-day from Pricing & Packages; >30 days: expected monthly pay.
        salaryBillingMode: shiftSalaryBilling.mode,
        dailyRateInr: shiftSalaryBilling.dailyRateInr,
        monthlySalaryInr: shiftSalaryBilling.monthlySalaryInr,
        hourlyRateInr:
          Number.isFinite(caregiverHourlyRateInr) && caregiverHourlyRateInr > 0
            ? caregiverHourlyRateInr
            : null,
        visitFeeInr:
          estimate?.invoice?.caregiverFeeInr ??
          selectedCaregiver?.estimatedCaregiverFeeInr ??
          caregiverFee,
        acknowledgedAt: agreementAcknowledged ? new Date().toISOString() : null,
      }),
    [
      agreementRef,
      serviceSelection.serviceRole,
      serviceSelection.serviceLabel,
      serviceSelection.serviceId,
      packageKey,
      careRole,
      searchShift,
      selectedCaregiver,
      caregiver,
      bookingOccurrences,
      targetDate,
      selectedSlot,
      selectedPatient,
      selectedPatientId,
      addressLabel,
      shiftSalaryBilling,
      caregiverHourlyRateInr,
      estimate,
      caregiverFee,
      agreementAcknowledged,
    ]
  );

  function applyDraft(draft) {
    if (!draft) return;
    setStep(migrateCareHubHireWizardStep(draft.step, draft.wizardVersion));
    setPackageKey(draft.packageKey || "hygiene_personal");
    setTierId(draft.tierId || "t2");
    setProcedureId(draft.procedureId || "injection");
    setSessionId(draft.sessionId || "short_assessment");
    setPattern(draft.pattern || "single");
    setRecurringPattern(draft.recurringPattern || "daily");
    setDaysServiceRequired(clampDaysServiceRequired(draft.daysServiceRequired, 1));
    {
      const minAllowed = todayIsoLocal();
      const draftDate = serviceDateToIsoDate(draft.targetDate) || minAllowed;
      setTargetDate(draftDate >= minAllowed ? draftDate : minAllowed);
    }
    setSelectedDates(Array.isArray(draft.selectedDates) ? draft.selectedDates : []);
    setWeeklyDayOfWeek(draft.weeklyDayOfWeek ?? 1);
    setSelectedSlot(draft.selectedSlot || null);
    setMatches(Array.isArray(draft.matches) ? draft.matches : []);
    setAvailableSlots(Array.isArray(draft.availableSlots) ? draft.availableSlots : []);
    setSelectedCaregiver(draft.selectedCaregiver || null);
    if (draft.slotValidatedFor?.caregiverId && draft.slotValidatedFor?.start) {
      setSlotValidatedFor({
        caregiverId: String(draft.slotValidatedFor.caregiverId),
        start: String(draft.slotValidatedFor.start).slice(0, 5),
        end: String(draft.slotValidatedFor.end || draft.slotValidatedFor.start).slice(0, 5),
      });
    }
    setEstimate(draft.estimate || null);
    setName(draft.name || "");
    setPhone(draft.phone || "");
    setSelectedPatientId(draft.patientId || "");
    setGuidelinesAcknowledged(Boolean(draft.guidelinesAcknowledged));
    setAgreementAcknowledged(Boolean(draft.agreementAcknowledged));
    if (draft.agreementRef) setAgreementRef(draft.agreementRef);
    if (typeof draft.prvRequired === "boolean") setPrvRequired(draft.prvRequired);
    const fromDraft = parseServiceCoords(draft.lat, draft.lng);
    const fromProps = parseServiceCoords(lat, lng);
    // Prefer the pin captured at hire time so a remounted parent (default Gurugram) cannot steal it.
    setServiceCoords(fromDraft || fromProps);
  }

  function buildDraft(extra = {}) {
    return {
      caregiverId: caregiver?.id || caregiver?.caregiverId,
      packageKey,
      tierId,
      procedureId,
      sessionId,
      pattern,
      recurringPattern,
      daysServiceRequired,
      targetDate,
      selectedDates,
      weeklyDayOfWeek,
      selectedSlot,
      selectedCaregiver,
      matches,
      slotValidatedFor,
      availableSlots,
      estimate,
      name,
      phone,
      step,
      wizardVersion: 3,
      lat: serviceCoords?.lat ?? lat,
      lng: serviceCoords?.lng ?? lng,
      addressLabel,
      patientId: selectedPatientId,
      prvRequired,
      guidelinesAcknowledged,
      agreementAcknowledged,
      agreementRef,
      // Persist open hire so reopen can resume Accept → Confirm for THIS booking only.
      bookingId: confirmedBooking?.bookingId || null,
      searchShift: searchShift || null,
      ...extra,
    };
  }

  function resolveResumeStepAfterCarePass(rawStep) {
    const n = migrateCareHubHireWizardStep(rawStep, 3);
    if (
      n === CAREHUB_HIRE_STEP.SLOTS ||
      n === CAREHUB_HIRE_STEP.CONFIRM ||
      n === CAREHUB_HIRE_STEP.SHIFT ||
      n === CAREHUB_HIRE_STEP.AGREEMENT
    ) {
      return n;
    }
    return CAREHUB_HIRE_STEP.SHIFT;
  }

  function syncDraftToParent() {
    if (typeof onDraftSync !== "function") return;
    try {
      onDraftSync(readBookingDraft());
    } catch {
      /* ignore */
    }
  }

  function leaveCarePassStep({ resumeTo = CAREHUB_HIRE_STEP.PATIENT } = {}) {
    writeBookingDraft(
      buildDraft({
        step: resumeTo,
        resumeAfterCarePass: false,
        resumeToPayment: false,
        resumeStepAfterCarePass: null,
        resumeAfterLogin: false,
      })
    );
    syncDraftToParent();
    carePassResumeHandledRef.current = "";
    setStep(resumeTo);
  }

  /** Divert to Care Pass without wiping selections; remember where to resume. */
  function goToCarePassStep({ fromStep = step, message = null, patientId = null } = {}) {
    if (userWizardBackRef.current) return;
    const resumeTo = resolveResumeStepAfterCarePass(
      fromStep === CAREHUB_HIRE_STEP.CARE_PASS ? CAREHUB_HIRE_STEP.SHIFT : fromStep
    );
    const pid = String(patientId || selectedPatientId || "").trim();
    writeBookingDraft(
      buildDraft({
        step: CAREHUB_HIRE_STEP.CARE_PASS,
        resumeStepAfterCarePass: resumeTo,
        resumeAfterCarePass: true,
        resumeToPayment: true,
        patientId: pid,
        guidelinesAcknowledged: true,
        caregiverName: caregiver?.name || caregiver?.fullName || "",
      })
    );
    if (pid && pid !== selectedPatientId) setSelectedPatientId(pid);
    if (message) showToast.info(message);
    setStep(CAREHUB_HIRE_STEP.CARE_PASS);
  }

  /**
   * After Care Pass activation: refresh BE entitlement, revalidate slot, resume prior step.
   */
  async function resumeBookingAfterCarePassPurchase({ patientId } = {}) {
    const pid = String(patientId || selectedPatientId || "").trim();
    const draft = readBookingDraft() || {};
    const hasPrefillSlot = Boolean(
      (searchPrefill?.skipShiftRedo && searchPrefill?.selectedSlot) ||
        selectedSlot ||
        draft.selectedSlot
    );
    const resumeTo = resolveResumeStepAfterCarePass(
      draft.resumeStepAfterCarePass ||
        (hasPrefillSlot ? CAREHUB_HIRE_STEP.SLOTS : CAREHUB_HIRE_STEP.SHIFT)
    );

    // Restore hourly search prefill so Hire continues without re-picking date/slot.
    if (searchPrefill?.skipShiftRedo) {
      applySearchPrefillToState(searchPrefill);
    }

    let sub = null;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      sub = await refreshPatientSubscription(pid);
      if (patientHasValidCarePassForShift(sub, searchShift, bookingCareDays)) break;
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
    if (!patientHasValidCarePassForShift(sub, searchShift, bookingCareDays)) {
      showToast.info(
        carePassMissingForBookingMessage(sub, searchShift, bookingCareDays) ||
          "Care Pass activation is still pending. Stay on this step or retry shortly."
      );
      writeBookingDraft(
        buildDraft({
          step: CAREHUB_HIRE_STEP.CARE_PASS,
          resumeStepAfterCarePass: resumeTo,
          resumeAfterCarePass: true,
          resumeToPayment: true,
          patientId: pid,
        })
      );
      setStep(CAREHUB_HIRE_STEP.CARE_PASS);
      return false;
    }

    // Keep selections; clear interrupt flags so init cannot snap back to CARE_PASS.
    writeBookingDraft(
      buildDraft({
        step: resumeTo,
        resumeStepAfterCarePass: null,
        resumeAfterCarePass: false,
        resumeToPayment: false,
        patientId: pid,
      })
    );

    const slot = selectedSlot || draft.selectedSlot || null;
    const cg = selectedCaregiver || draft.selectedCaregiver || null;
    if (slot && !selectedSlot) setSelectedSlot(slot);
    if (cg && !selectedCaregiver) setSelectedCaregiver(cg);

    if (
      (resumeTo === CAREHUB_HIRE_STEP.SLOTS ||
        resumeTo === CAREHUB_HIRE_STEP.CONFIRM ||
        resumeTo === CAREHUB_HIRE_STEP.AGREEMENT) &&
      slot &&
      cg
    ) {
      try {
        await validateSlotAvailability({
          caregiverId: cg.caregiverId || cg.id,
          slot,
          serviceDate: bookingOccurrences[0]?.serviceDate || targetDate || draft.targetDate,
        });
        markSelectionValidated(cg.caregiverId || cg.id, slot);
        if (resumeTo === CAREHUB_HIRE_STEP.CONFIRM || resumeTo === CAREHUB_HIRE_STEP.AGREEMENT) {
          await loadEstimateAndGoPay(cg);
        } else {
          setStep(CAREHUB_HIRE_STEP.SLOTS);
        }
        return true;
      } catch (err) {
        showToast.error(
          err?.message ||
            "Your selected time is no longer available. Choose another slot — your other booking details were kept."
        );
        setSelectedSlot(null);
        setSlotValidatedFor(null);
        setEstimate(null);
        writeBookingDraft(
          buildDraft({
            step: CAREHUB_HIRE_STEP.SLOTS,
            selectedSlot: null,
            resumeAfterCarePass: false,
            resumeToPayment: false,
            patientId: pid,
          })
        );
        setStep(CAREHUB_HIRE_STEP.SLOTS);
        return false;
      }
    }

    setStep(resumeTo);
    return true;
  }

  function applySearchPrefillToState(prefill, { includeSlot = true } = {}) {
    if (!prefill || typeof prefill !== "object") return false;
    if (prefill.packageKey) setPackageKey(prefill.packageKey);
    if (prefill.tierId) setTierId(prefill.tierId);
    if (prefill.procedureId) setProcedureId(prefill.procedureId);
    if (prefill.sessionId) setSessionId(prefill.sessionId);
    if (prefill.pattern) setPattern(prefill.pattern);
    if (prefill.recurringPattern) setRecurringPattern(prefill.recurringPattern);
    if (prefill.daysServiceRequired != null) {
      setDaysServiceRequired(clampDaysServiceRequired(prefill.daysServiceRequired, 1));
    }
    if (prefill.targetDate) setTargetDate(prefill.targetDate);
    if (Array.isArray(prefill.selectedDates)) setSelectedDates(prefill.selectedDates);
    if (prefill.weeklyDayOfWeek != null) setWeeklyDayOfWeek(Number(prefill.weeklyDayOfWeek));
    const canUseSlot = includeSlot && !slotPrefillDiscardedRef.current && Boolean(prefill.selectedSlot);
    if (canUseSlot) {
      setSelectedSlot(prefill.selectedSlot);
      setAvailableSlots(
        Array.isArray(prefill.availableSlots) && prefill.availableSlots.length
          ? prefill.availableSlots
          : [prefill.selectedSlot]
      );
    } else if (includeSlot && Array.isArray(prefill.availableSlots) && !slotPrefillDiscardedRef.current) {
      setAvailableSlots(prefill.availableSlots);
    }
    return Boolean(prefill.skipShiftRedo && canUseSlot);
  }

  useEffect(() => {
    if (!open) {
      bookingInitSessionRef.current = "";
      carePassResumeHandledRef.current = "";
      return;
    }
    const caregiverId = String(caregiver?.id || caregiver?.caregiverId || "");
    const sessionKey = [
      caregiverId,
      // Key on the parent's shift only — an in-wizard shift change must not restart the hire.
      String(searchShiftProp || ""),
      // Do not key on initialPatientId — parent can fill it a tick later and must not wipe mid-hire.
      resumeDraft
        ? `r:${Boolean(resumeDraft.resumeAfterCarePass)}:${Boolean(resumeDraft.resumeAfterLogin)}:${resumeDraft.patientId || ""}:${resumeDraft.step || ""}`
        : "r:none",
    ].join("|");

    // Parent lat/lng/prefill churn must not wipe an in-progress hire (esp. after Care Pass pay).
    if (bookingInitSessionRef.current === sessionKey) {
      const pin = parseServiceCoords(lat, lng);
      if (pin) {
        setServiceCoords((prev) => prev || pin);
      }
      return;
    }
    bookingInitSessionRef.current = sessionKey;
    slotPrefillDiscardedRef.current = false;
    // Parent's explicit shift wins; else a shift the customer already chose in this hire's draft.
    // Never default to a shift the customer did not pick (was silently 24h live-in).
    const shiftDraft = readBookingDraft() || resumeDraft;
    const draftChosenShift =
      shiftDraft && String(shiftDraft.caregiverId) === caregiverId ? String(shiftDraft.searchShift || "").trim() : "";
    const onlyShiftOption = wizardShiftOptions.length === 1 ? wizardShiftOptions[0].value : "";
    const initShift = String(searchShiftProp || "").trim() || draftChosenShift || onlyShiftOption;
    setWizardShift(initShift);
    setShiftChosen(Boolean(initShift));
    setShiftPickerValue(initShift);
    setShiftPickerOpen(false);
    setShiftChangedInWizard(false);
    shiftRecheckRef.current = false;
    lastAvailabilityContextRef.current = { patientId: "", serviceKey: "" };
    slotsGenerationRef.current += 1;

    if (reselectBookingId && api.getToken?.()) {
      (async () => {
        setPatientsLoading(true);
        try {
          const res = await api.listCustomerPatients();
          const list = extractPatientsList(res);
          setPatients(list);
        } catch {
          /* patient list optional for reselect resume */
        } finally {
          setPatientsLoading(false);
        }
        await hydrateReselectCareHubBooking(reselectBookingId);
      })();
      return;
    }

    // Always drop previous request UI when (re)opening — acceptance must come from a live booking.
    setConfirmedBooking(null);
    setHoldCountdownLabel("");
    const draft = readBookingDraft() || resumeDraft;
    if (draft && String(draft.caregiverId) === caregiverId) {
      // Incomplete freemium gate drafts (no patient yet) must not skip patient select.
      const incompleteCarePassGate =
        (draft.resumeAfterCarePass || draft.resumeToPayment) &&
        !draft.patientId &&
        !draft.selectedSlot &&
        Number(draft.wizardVersion || 0) < 2;
      if (incompleteCarePassGate) {
        clearBookingDraft();
      } else {
        applyDraft(draft);
        const prefillFromDraft =
          !searchPrefill?.skipShiftRedo && draft.skipShiftRedo && draft.selectedSlot
            ? {
                packageKey: draft.packageKey,
                tierId: draft.tierId,
                pattern: draft.pattern,
                recurringPattern: draft.recurringPattern,
                daysServiceRequired: draft.daysServiceRequired,
                selectedDates: draft.selectedDates,
                targetDate: draft.targetDate,
                selectedSlot: draft.selectedSlot,
                availableSlots: draft.availableSlots,
                skipShiftRedo: true,
              }
            : null;
        const effectivePrefill = searchPrefill?.skipShiftRedo ? searchPrefill : prefillFromDraft;
        if (effectivePrefill?.skipShiftRedo) {
          applySearchPrefillToState(effectivePrefill);
        }
        const slotForMatch =
          draft.selectedSlot || effectivePrefill?.selectedSlot || searchPrefill?.selectedSlot || null;
        if (
          draft.selectedCaregiver &&
          slotForMatch &&
          !draft.slotValidatedFor?.caregiverId
        ) {
          markSelectionValidated(
            draft.selectedCaregiver.caregiverId || draft.selectedCaregiver.id,
            slotForMatch
          );
        }
        // Hourly marketplace search: restore match row so slot step shows the chosen caregiver.
        const midFlowResume =
          (draft.resumeAfterCarePass || draft.resumeToPayment || draft.resumeAfterLogin) &&
          (draft.selectedSlot ||
            Number(draft.wizardVersion) >= 2 ||
            Number(draft.step) >= CAREHUB_HIRE_STEP.PATIENT ||
            draft.patientId);
        const openHireResume = Boolean(draft.bookingId);
        const inProgressHire =
          Boolean(draft.patientId) &&
          Number(draft.step) >= CAREHUB_HIRE_STEP.SHIFT &&
          Number(draft.wizardVersion) >= 2;
        // Keep incomplete hire drafts (incl. post–Care Pass resume) until booking completes or user restarts.
        if (!midFlowResume && !openHireResume && !inProgressHire) clearBookingDraft();
        const resumeStep = migrateCareHubHireWizardStep(draft.step, draft.wizardVersion);
        const draftShift = String(draft.searchShift || "").toLowerCase();
        const wizardShift = String(initShift || "").toLowerCase();
        const draftLooksLiveIn =
          draftShift === "24h" ||
          draftShift.includes("24") ||
          draftShift.includes("live");
        const draftShiftMatches =
          !draft.bookingId ||
          !draftShift ||
          (Boolean(isLiveIn) === draftLooksLiveIn &&
            (!wizardShift ||
              !draftShift ||
              wizardShift === draftShift ||
              (!draftLooksLiveIn && wizardShift !== "24h" && !isLiveIn)));
        const resumeBookingId = draftShiftMatches ? draft.bookingId || null : null;
        if (api.getToken?.() && resumeStep >= CAREHUB_HIRE_STEP.PATIENT) {
          (async () => {
            try {
              const res = await api.listCustomerPatients();
              const list = extractPatientsList(res);
              setPatients(list);
              const pid = pickOwnedPatientId(list, draft.patientId);
              if (pid) {
                setSelectedPatientId(pid);
                await refreshPatientSubscription(pid);
              } else if (draft.patientId) {
                setSelectedPatientId("");
                setPatientSubscription(null);
              }
            } catch {
              /* patient step can reload */
            }
            await hydrateOpenCareHubBooking({
              resumeBookingId,
            }).catch(() => null);
          })();
        } else if (api.getToken?.()) {
          hydrateOpenCareHubBooking({
            resumeBookingId,
          }).catch(() => null);
        }
        return;
      }
    }
    setPackageKey("hygiene_personal");
    setTierId("t2");
    setProcedureId("injection");
    setProcedureQuery("");
    setSessionId("short_assessment");
    setPattern("single");
    setRecurringPattern("daily");
    setDaysServiceRequired(1);
    setTargetDate(todayIsoLocal());
    setSelectedDates([]);
    setWeeklyDayOfWeek(1);
    setCustomDateInput(todayIsoLocal());
    setSelectedSlot(null);
    setGuidelinesAcknowledged(false);
    setAgreementAcknowledged(false);
    setAgreementRef(generateAgreementRef());
    setMatches([]);
    setAvailableSlots([]);
    setServiceCoords(parseServiceCoords(lat, lng));
    setSelectedCaregiver(
      caregiver ? { ...caregiver, caregiverId: caregiver.id || caregiver.caregiverId } : null
    );
    setSlotValidatedFor(null);
    setConfirmedBooking(null);
    setHoldCountdownLabel("");
    setAcceptPollBusy(false);
    setOtpSent(false);
    setOtp("");
    setOtpToken(null);
    setEstimate(null);
    setSelectedPatientId("");
    setPatientSubscription(null);
    setPrvRequired(null);
    const skipShiftFromSearch = applySearchPrefillToState(searchPrefill);
    const preferredPatientId = String(initialPatientId || "").trim();
    const skipPatientFromMarketplaceSearch = Boolean(skipShiftFromSearch && preferredPatientId);
    if (skipPatientFromMarketplaceSearch) {
      setSelectedPatientId(preferredPatientId);
    }
    setStep(
      skipPatientFromMarketplaceSearch ? CAREHUB_HIRE_STEP.SLOTS : CAREHUB_HIRE_STEP.PATIENT
    );
    if (api.getToken?.()) {
      setPatientsLoading(true);
      (async () => {
        try {
          const res = await api.listCustomerPatients();
          const list = extractPatientsList(res);
          setPatients(list);
          const pid = pickOwnedPatientId(list, preferredPatientId);
          if (pid) {
            setSelectedPatientId(pid);
            let sub = await refreshPatientSubscription(pid);
            // Search already locked this patient — continue into shift / Care Pass / slots.
            if (preferredPatientId && pid === preferredPatientId) {
              let subOk = patientHasValidCarePassForShift(sub, initShift, 1);
              // Care Pass may activate a beat after checkout — brief retry before diverting.
              for (let attempt = 0; attempt < 4 && !subOk; attempt += 1) {
                await new Promise((r) => setTimeout(r, 350 * (attempt + 1)));
                sub = await refreshPatientSubscription(pid);
                subOk = patientHasValidCarePassForShift(sub, initShift, 1);
              }
              if (subOk) {
                setPrvRequired(null);
                setStep(skipShiftFromSearch ? CAREHUB_HIRE_STEP.SLOTS : CAREHUB_HIRE_STEP.SHIFT);
              } else {
                goToCarePassStep({
                  fromStep: skipShiftFromSearch ? CAREHUB_HIRE_STEP.SLOTS : CAREHUB_HIRE_STEP.SHIFT,
                  patientId: pid,
                });
              }
            }
          }
        } catch {
          /* PatientSelectStep can show empty / retry via Manage patients */
        } finally {
          setPatientsLoading(false);
        }
        // Fresh Hire Now: only resume a PENDING request for this shift (never a prior Accepted).
        await hydrateOpenCareHubBooking().catch(() => null);
      })();
    }
  }, [
    open,
    caregiver?.id,
    caregiver?.caregiverId,
    resumeDraft,
    lat,
    lng,
    searchShiftProp,
    searchPrefill?.packageKey,
    searchPrefill?.pattern,
    searchPrefill?.targetDate,
    searchPrefill?.skipShiftRedo,
    searchPrefill?.selectedSlot?.start,
    reselectBookingId,
    // initialPatientId / selectedSlot intentionally omitted — parent updates must not remount mid-hire.
  ]);

  useEffect(() => {
    if (!open) {
      lastAvailabilityContextRef.current = { patientId: "", serviceKey: "" };
      slotPrefillDiscardedRef.current = false;
      return;
    }
    const patientId = String(selectedPatientId || "");
    const prev = lastAvailabilityContextRef.current;
    const patientChanged = Boolean(prev.patientId && patientId && prev.patientId !== patientId);
    lastAvailabilityContextRef.current = {
      ...prev,
      patientId: patientId || prev.patientId,
    };
    if (!patientChanged) return;
    clearGeneratedAvailability();
    setStep((current) => (current === CAREHUB_HIRE_STEP.SLOTS ? CAREHUB_HIRE_STEP.SHIFT : current));
  }, [open, selectedPatientId]);

  useEffect(() => {
    if (!open) return;
    if (pattern === "instant") {
      setTargetDate(todayIsoLocal());
    } else if (!isLiveIn) {
      // Only correct an actually-past date — today itself is a valid target date.
      setTargetDate((prev) => (!prev || prev < todayIsoLocal() ? todayIsoLocal() : prev));
    }
  }, [open, pattern, isLiveIn]);

  useEffect(() => {
    if (!open || pattern !== "recurring") return;
    if (!recurringPatternNeedsDatePicker(recurringPattern)) {
      setSelectedDates([]);
    }
  }, [open, pattern, recurringPattern]);

  useEffect(() => {
    if (!open) {
      carePassResumeHandledRef.current = "";
      return;
    }
    const draft = readBookingDraft() || resumeDraft;
    if (!draft) return;
    if (!draft.resumeToPayment && !draft.resumeAfterCarePass && !draft.resumeAfterLogin) {
      return;
    }
    if (userWizardBackRef.current) return;
    // Run once per open+draft resume session — never re-enter when the user picks a slot.
    const resumeSessionKey = [
      String(caregiver?.id || caregiver?.caregiverId || ""),
      Boolean(draft.resumeAfterCarePass),
      Boolean(draft.resumeToPayment),
      Boolean(draft.resumeAfterLogin),
      String(draft.patientId || ""),
      String(draft.step || ""),
    ].join("|");
    if (carePassResumeHandledRef.current === resumeSessionKey) return;
    carePassResumeHandledRef.current = resumeSessionKey;

    // Freemium hire resume: reopen wizard at patient step for this caregiver only.
    if (
      draft.resumeAfterCarePass &&
      !draft.selectedSlot &&
      !draft.patientId &&
      migrateCareHubHireWizardStep(draft.step, draft.wizardVersion) <=
        CAREHUB_HIRE_STEP.CARE_PASS
    ) {
      setStep(CAREHUB_HIRE_STEP.PATIENT);
      clearBookingDraft();
      return;
    }
    let cancelled = false;
    (async () => {
      if (!api.getToken?.()) return;

      if (draft.resumeAfterCarePass && draft.patientId) {
        if (draft.selectedSlot) setSelectedSlot(draft.selectedSlot);
        if (draft.selectedCaregiver) {
          setSelectedCaregiver(draft.selectedCaregiver);
        }
        if (Array.isArray(draft.availableSlots) && draft.availableSlots.length) {
          setAvailableSlots(draft.availableSlots);
        }
        if (Array.isArray(draft.matches) && draft.matches.length) {
          setMatches(draft.matches);
        }
        const ownedId = await resolveOwnedPatientId(draft.patientId);
        if (cancelled) return;
        if (!ownedId.patientId) {
          showToast.error("Select a patient on this account to continue.");
          await openPatientStep();
          // Keep draft so selections can be restored after patient fix.
          return;
        }
        await resumeBookingAfterCarePassPurchase({ patientId: ownedId.patientId });
        return;
      }

      if (
        draft.resumeAfterLogin ||
        migrateCareHubHireWizardStep(draft.step, draft.wizardVersion) ===
          CAREHUB_HIRE_STEP.PATIENT
      ) {
        if (!cancelled) {
          await openPatientStep();
          // Keep draft until patient continues — do not wipe Care Pass resume fields.
          if (!draft.resumeAfterCarePass && !draft.resumeToPayment) {
            clearBookingDraft();
          }
        }
        return;
      }

      if (draft.patientId) {
        if (typeof draft.prvRequired === "boolean") setPrvRequired(draft.prvRequired);
        const ownedId = await resolveOwnedPatientId(draft.patientId);
        if (cancelled) return;
        if (!ownedId.patientId) {
          showToast.error("Select a patient on this account to continue.");
          await openPatientStep();
          return;
        }
        const sub = await refreshPatientSubscription(ownedId.patientId);
        if (cancelled) return;
        if (patientHasValidCarePassForShift(sub, searchShift, bookingCareDays)) {
          await resumeBookingAfterCarePassPurchase({ patientId: ownedId.patientId });
        } else {
          goToCarePassStep({
            fromStep: draft.resumeStepAfterCarePass || CAREHUB_HIRE_STEP.SHIFT,
            patientId: ownedId.patientId,
          });
        }
        return;
      }

      if (!cancelled) {
        await openPatientStep();
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally omit selectedSlot / selectedCaregiver — slot picks must not re-run resume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resumeDraft, caregiver?.id, caregiver?.caregiverId, searchShift]);

  const visibleCaregivers = useMemo(() => {
    if (!selectedSlot?.start) return [];

    const excludedIds = new Set(
      (confirmedBooking?.excludedCaregiverIds ||
        confirmedBooking?.booking?.details_json?.matching?.excludedCaregiverIds ||
        confirmedBooking?.booking?.details?.matching?.excludedCaregiverIds ||
        []).map(String)
    );

    if (confirmedBooking?.caregiverDeclined) {
      const pool = (replacementCaregivers.length ? replacementCaregivers : matches).filter((m) => {
        if (excludedIds.has(String(m.caregiverId))) return false;
        if (lockedCaregiverGender && resolveMatchGender(m) !== lockedCaregiverGender) {
          return false;
        }
        return caregiverHasSelectedSlot(m, selectedSlot);
      });
      return pool.slice(0, 3);
    }

    const preferredId = preferredHireCaregiver
      ? String(preferredHireCaregiver.id || preferredHireCaregiver.caregiverId || "")
      : "";
    const free = matches.filter((m) => {
      if (
        lockedCaregiverGender &&
        resolveMatchGender(m) !== lockedCaregiverGender
      ) {
        return false;
      }
      if (caregiverHasSelectedSlot(m, selectedSlot)) return true;
      return isOwnActiveBookingSlot(confirmedBooking, m.caregiverId, selectedSlot);
    });
    if (!free.length) return [];
    if (!preferredId) return free;
    const preferred = free.find((m) => String(m.caregiverId) === preferredId);
    if (preferred) return [preferred];
    return free;
  }, [
    matches,
    replacementCaregivers,
    selectedSlot,
    preferredHireCaregiver,
    confirmedBooking,
    lockedCaregiverGender,
  ]);

  const preferredUnavailableForSlot = useMemo(() => {
    if (!selectedSlot || !preferredHireCaregiver) return false;
    if (!Array.isArray(matches) || matches.length === 0) return false;
    const preferredId = String(
      preferredHireCaregiver.id || preferredHireCaregiver.caregiverId || ""
    );
    if (isOwnActiveBookingSlot(confirmedBooking, preferredId, selectedSlot)) return false;
    const preferredMatch = matches.find((m) => String(m.caregiverId) === preferredId);
    if (!preferredMatch) return true;
    return !caregiverHasSelectedSlot(preferredMatch, selectedSlot);
  }, [matches, selectedSlot, preferredHireCaregiver, confirmedBooking]);

  const caregiverRequestAccepted = Boolean(
    confirmedBooking?.caregiverAccepted &&
      selectedCaregiver &&
      selectedSlot &&
      String(confirmedBooking.requestedCaregiverId) === String(selectedCaregiver.caregiverId) &&
      confirmedBookingMatchesSlot(confirmedBooking, selectedSlot)
  );

  const careRequestPendingForSlot = Boolean(
    confirmedBooking?.awaitingCaregiverAcceptance &&
      selectedCaregiver &&
      selectedSlot &&
      String(confirmedBooking.requestedCaregiverId) === String(selectedCaregiver.caregiverId) &&
      confirmedBookingMatchesSlot(confirmedBooking, selectedSlot)
  );

  const canProceedFromSlots = useMemo(() => {
    if (!selectedSlot || !selectedCaregiver || validatingSlot || busy || paying) return false;
    return caregiverRequestAccepted;
  }, [
    selectedSlot,
    selectedCaregiver,
    validatingSlot,
    busy,
    paying,
    caregiverRequestAccepted,
  ]);

  // Keep the selected caregiver consistent with free caregivers for the slot.
  // Do not auto-pick a busy caregiver; clear validation when the pair is no longer free.
  // Own pending/accepted booking is an exception — keep that caregiver selected.
  useEffect(() => {
    if (!selectedSlot) return;
    // Resume path: match list may still be loading — keep draft caregiver until slots reload.
    if (!matches.length) return;

    setSelectedCaregiver((prev) => {
      if (!prev) return null;
      const prevId = String(prev.caregiverId ?? prev.id ?? "");
      if (isOwnActiveBookingSlot(confirmedBooking, prevId, selectedSlot)) {
        return prev;
      }
      const hit = visibleCaregivers.find((m) => String(m.caregiverId) === prevId);
      return hit || null;
    });
    setSlotValidatedFor((prev) => {
      if (!prev) return null;
      if (prev.start !== selectedSlot.start || prev.end !== selectedSlot.end) return null;
      if (isOwnActiveBookingSlot(confirmedBooking, prev.caregiverId, selectedSlot)) {
        return prev;
      }
      const stillVisible = visibleCaregivers.some(
        (m) => String(m.caregiverId) === String(prev.caregiverId)
      );
      return stillVisible ? prev : null;
    });
  }, [selectedSlot, visibleCaregivers, matches.length, confirmedBooking]);

  // If subscription becomes valid for this shift, resume the interrupted booking step.
  useEffect(() => {
    if (!open) return;
    if (step !== CAREHUB_HIRE_STEP.CARE_PASS) return;
    if (!patientHasValidCarePassForShift(patientSubscription, searchShift, bookingCareDays)) return;
    if (busy || paying) return;
    const draft = readBookingDraft() || {};
    // Only auto-advance when we were waiting on purchase / entitlement — not while user is picking a plan.
    if (!draft.resumeAfterCarePass && !draft.resumeToPayment) return;
    const resumeTo = resolveResumeStepAfterCarePass(
      draft.resumeStepAfterCarePass ||
        (draft.selectedSlot || selectedSlot ? CAREHUB_HIRE_STEP.SLOTS : CAREHUB_HIRE_STEP.SHIFT)
    );
    writeBookingDraft(
      buildDraft({
        step: resumeTo,
        resumeAfterCarePass: false,
        resumeToPayment: false,
        resumeStepAfterCarePass: null,
      })
    );
    setStep(resumeTo);
  }, [open, step, patientSubscription, searchShift, bookingCareDays, busy, paying]);

  // If booking duration grows beyond the current pass after schedule is chosen,
  // send them to plan selection — but never interrupt the SHIFT date-picker step.
  // Skipped once a booking already exists for this session: its Care Pass
  // requirement was already consumed/verified atomically at creation, so a live
  // balance that now reads "insufficient" just reflects that same consumption —
  // re-checking it here would wrongly bounce an already-paid booking back to purchase.
  useEffect(() => {
    if (!open) return;
    if (carePassAlreadySecuredForSession(confirmedBooking)) return;
    if (!patientSubscription?.active && !Array.isArray(patientSubscription?.passes)) return;
    if (bookingCareDays <= 1) return;
    if (patientHasValidCarePassForShift(patientSubscription, searchShift, bookingCareDays)) return;
    if (step !== CAREHUB_HIRE_STEP.SLOTS && step !== CAREHUB_HIRE_STEP.CONFIRM) {
      return;
    }
    goToCarePassStep({
      fromStep: step,
      message: carePassMissingForBookingMessage(patientSubscription, searchShift, bookingCareDays),
    });
  }, [open, step, patientSubscription, searchShift, bookingCareDays, confirmedBooking]);

  /**
   * Restore an in-progress CareHub hire for this caregiver.
   * - Fresh Hire Now: only PENDING (awaiting accept) for this shift — never borrow
     another booking's Accepted state; each booking must Send request → Accept → Confirm.
   - Resume: if draft.bookingId is set, restore that exact booking (incl. ACCEPTED)
     so the customer can continue Confirm / Agreement.
   */
  async function hydrateOpenCareHubBooking({ resumeBookingId = null, applySchedule = true } = {}) {
    if (!api.getToken?.()) return null;
    const preferredId = String(caregiver?.id || caregiver?.caregiverId || "").trim();
    if (!preferredId) return null;
    try {
      const res = await api.getCustomerBookings();
      const list = extractCustomerBookingsList(res);
      const wantId =
        resumeBookingId != null && resumeBookingId !== ""
          ? String(resumeBookingId)
          : "";
      const hit = list.find((b) => {
        const cgId = String(b?.caregiver_id ?? b?.caregiverId ?? "");
        if (cgId !== preferredId) return false;
        if (!openHireMatchesWizardContext(b, { isLiveIn, searchShift })) return false;
        if (wantId) {
          return String(b?.id || b?.bookingId || "") === wantId && isOpenCareHubHireBooking(b);
        }
        if (!isOpenCareHubHireBooking(b)) return false;
        // Fresh hire: only resume a waiting request, not a prior acceptance.
        const status = String(b.status || b.bookingStatus || "").toUpperCase();
        return status === "PENDING";
      });
      if (!hit) return null;
      const next = buildConfirmedBookingFromRow(hit, preferredId);
      if (!next?.bookingId) return null;
      setConfirmedBooking(next);
      if (applySchedule) {
        const hireDate = serviceDateToIsoDate(next.serviceDate);
        const minAllowed = todayIsoLocal();
        // Never pin the date picker to a past hire day.
        if (hireDate && hireDate >= minAllowed) {
          setTargetDate(hireDate);
        } else {
          setTargetDate(minAllowed);
        }
        if (next.startTime) {
          const end = next.endTime || next.startTime;
          const slot = {
            id: `${next.startTime}-${end}`,
            start: next.startTime,
            end,
            label: formatCareHubSlotLabel(next.startTime, end),
            available: true,
          };
          setSelectedSlot(slot);
          setSelectedCaregiver((prev) => {
            if (prev && String(prev.caregiverId) === preferredId) return prev;
            return {
              ...(caregiver || {}),
              caregiverId: preferredId,
              name: next.caregiverName || caregiver?.name || caregiver?.fullName || "Caregiver",
              estimatedCaregiverFeeInr:
                caregiver?.estimatedCaregiverFeeInr ?? caregiverFee ?? null,
            };
          });
          setSlotValidatedFor({
            caregiverId: preferredId,
            start: slot.start,
            end: slot.end,
          });
        }
      }
      return next;
    } catch {
      return null;
    }
  }

  async function hydrateReselectCareHubBooking(bookingId) {
    const bid = String(bookingId || "").trim();
    if (!bid || !api.getToken?.()) return null;
    try {
      const res = await api.getCustomerBookings();
      const list = extractCustomerBookingsList(res);
      const hit = list.find((b) => String(b?.id || b?.bookingId) === bid);
      if (!hit || !isOpenCareHubHireBooking(hit)) return null;
      const details = hit.details_json || hit.details || {};
      const reselectRequired =
        Boolean(details.careHubDispatch?.reselectRequired) ||
        (hit.caregiver_id == null && hit.caregiverId == null);
      if (!reselectRequired && !(details.matching?.excludedCaregiverIds || []).length) {
        return null;
      }
      const next = buildConfirmedBookingFromRow(hit);
      if (!next?.bookingId) return null;
      next.caregiverDeclined = true;
      next.reselectRequired = true;
      setConfirmedBooking(next);
      setReplacementCaregivers([]);
      const clientLocation = details.clientLocation || {};
      if (clientLocation.lat != null && clientLocation.lng != null) {
        setServiceCoords(parseServiceCoords(clientLocation.lat, clientLocation.lng));
      }
      if (next.startTime) {
        const end = next.endTime || next.startTime;
        const slot = {
          id: `${next.startTime}-${end}`,
          start: next.startTime,
          end,
          label: formatCareHubSlotLabel(next.startTime, end),
          available: true,
        };
        setSelectedSlot(slot);
        setAvailableSlots([slot]);
      }
      if (next.serviceDate) {
        setTargetDate(next.serviceDate);
      }
      const patientId = hit.patient_id || hit.patientId || details.patientId;
      if (patientId) setSelectedPatientId(String(patientId));
      setSelectedCaregiver(null);
      clearSelectionValidation();
      setStep(CAREHUB_HIRE_STEP.SLOTS);
      await loadReplacementSuggestions(next.bookingId);
      return next;
    } catch {
      return null;
    }
  }

  function onPatternChange(nextPattern) {
    setPattern(nextPattern);
    setAvailableSlots([]);
    setSelectedSlot(null);
    setMatches([]);
    setSelectedCaregiver(null);
    clearSelectionValidation();
    if (step === CAREHUB_HIRE_STEP.SLOTS) setStep(CAREHUB_HIRE_STEP.SHIFT);
    if (nextPattern === "instant") {
      setTargetDate(todayIsoLocal());
      setDaysServiceRequired(1);
    } else if (nextPattern === "recurring") {
      // Multi-day recurring defaults to 5 caregiver days; customer can adjust the counter.
      setDaysServiceRequired((prev) =>
        prev > 1 ? prev : CAREHUB_RECURRING_OCCURRENCE_COUNT
      );
      if (!isLiveIn && targetDate < todayIsoLocal()) {
        setTargetDate(todayIsoLocal());
      }
    } else {
      setDaysServiceRequired(1);
      if (!isLiveIn && targetDate < todayIsoLocal()) {
        setTargetDate(todayIsoLocal());
      }
    }
  }

  function bumpDaysServiceRequired(delta) {
    setDaysServiceRequired((prev) => {
      const next = clampDaysServiceRequired(Number(prev) + delta, 1);
      setSelectedDates((dates) => (dates.length > next ? dates.slice(0, next) : dates));
      return next;
    });
  }

  function addCustomDate(isoOverride = null) {
    const nextDate = String(isoOverride || customDateInput || "").slice(0, 10);
    if (!nextDate) return;
    if (!calendarLoaded || calendarError) {
      showToast.error(calendarError || "Wait for caregiver availability to load before selecting a date.");
      return;
    }
    if (
      !isCalendarDateSelectable(nextDate, {
        daysByDate: calendarDaysByDate,
        minDate: minTargetDate,
        maxDate: maxWindowDate,
        availabilityLoaded: calendarLoaded,
      })
    ) {
      showToast.error("That date is unavailable for this caregiver. Choose another date.");
      return;
    }
    setSelectedDates((prev) => {
      if (prev.includes(nextDate)) return prev;
      const maxDays = clampDaysServiceRequired(daysServiceRequired, 1);
      if (prev.length >= maxDays) {
        showToast.info(`You selected ${maxDays} day${maxDays === 1 ? "" : "s"} of service. Remove a date to add another.`);
        return prev;
      }
      const next = [...prev, nextDate].sort();
      setTargetDate(next[0]);
      return next;
    });
  }

  function toggleMultiCalendarDate(iso) {
    const date = String(iso || "").slice(0, 10);
    if (!date) return;
    if (selectedDates.includes(date)) {
      removeCustomDate(date);
      return;
    }
    addCustomDate(date);
  }

  function selectSingleCalendarDate(iso) {
    const date = String(iso || "").slice(0, 10);
    if (!date) return;
    if (
      !isCalendarDateSelectable(date, {
        daysByDate: calendarDaysByDate,
        minDate: minTargetDate,
        maxDate: maxTargetDate,
        availabilityLoaded: calendarLoaded,
      })
    ) {
      showToast.error("That date is unavailable for this caregiver. Choose another date.");
      return;
    }
    if (date !== targetDate) {
      setAvailableSlots([]);
      setSelectedSlot(null);
      setMatches([]);
      setSelectedCaregiver(null);
      clearSelectionValidation();
      if (step === CAREHUB_HIRE_STEP.SLOTS) setStep(CAREHUB_HIRE_STEP.SHIFT);
    }
    setTargetDate(date);
  }

  function removeCustomDate(iso) {
    setSelectedDates((prev) => {
      const next = prev.filter((d) => d !== iso);
      if (next.length) setTargetDate([...next].sort()[0]);
      return next;
    });
  }

  function validateSchedulingStep() {
    if (calendarLoading) {
      showToast.info("Still checking caregiver availability…");
      return false;
    }
    if (!calendarLoaded || calendarError) {
      showToast.error(calendarError || "Caregiver availability could not be loaded. Retry before booking.");
      return false;
    }
    const usesListedDates =
      pattern === "recurring" && recurringPatternNeedsDatePicker(recurringPattern);
    if (usesListedDates) {
      if (!selectedDates.length) {
        showToast.error("Select at least one date for this recurring pattern.");
        return false;
      }
      const invalid = selectedDates.find(
        (iso) =>
          !isCalendarDateSelectable(iso, {
            daysByDate: calendarDaysByDate,
            minDate: minTargetDate,
            maxDate: maxWindowDate,
            availabilityLoaded: calendarLoaded,
          })
      );
      if (invalid) {
        showToast.error(`This caregiver is unavailable on ${formatDisplayDate(invalid)}.`);
        return false;
      }
      const first = [...selectedDates].sort()[0];
      if (first < todayIsoLocal()) {
        showToast.error("Please pick today or a future date.");
        return false;
      }
      if (first !== targetDate) setTargetDate(first);
      return true;
    }
    if (!targetDate) {
      showToast.error("Select a target date.");
      return false;
    }
    if (targetDate < todayIsoLocal()) {
      showToast.error("Please pick today or a future date.");
      return false;
    }
    // Daily / weekdays / weekly: every auto-built occurrence must be free — not only the start day.
    // (Leave on day 3 of a 5-day series was previously skipped here, so single-day worked but recurring failed.)
    if (pattern === "recurring") {
      const occurrenceDates = resolveHourlyMarketplaceDates({
        pattern,
        recurringPattern,
        searchDate: targetDate,
        selectedDates,
        daysServiceRequired,
        weeklyDayOfWeek,
      });
      const invalid = occurrenceDates.find(
        (iso) =>
          !isCalendarDateSelectable(iso, {
            daysByDate: calendarDaysByDate,
            minDate: minTargetDate,
            maxDate: maxWindowDate,
            availabilityLoaded: calendarLoaded,
          })
      );
      if (invalid) {
        showToast.error(
          `This caregiver is unavailable on ${formatDisplayDate(invalid)}. Change the start date, reduce days, or pick custom dates that skip leave/blocked days.`
        );
        return false;
      }
      return true;
    }
    if (
      !isCalendarDateSelectable(targetDate, {
        daysByDate: calendarDaysByDate,
        minDate: minTargetDate,
        maxDate: maxTargetDate,
        availabilityLoaded: calendarLoaded,
      })
    ) {
      showToast.error("This caregiver is unavailable on the selected date. Choose another date.");
      return false;
    }
    return true;
  }

  async function fetchAvailableMatchesForBooking(slot, occurrenceRows = null) {
    if (!slot?.start) return [];
    const pin =
      parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng);
    if (!pin) return [];
    const rows =
      Array.isArray(occurrenceRows) && occurrenceRows.length
        ? occurrenceRows
        : bookingOccurrences.filter((o) => o?.serviceDate && o?.startTime);
    if (!rows.length) return [];
    const patientGender =
      caregiverGenderFilterFromPatient(
        patients.find((p) => String(p.patientId || "") === String(selectedPatientId || ""))
      ) || null;
    const excludeBookingId =
      confirmedBooking?.bookingId || readBookingDraft()?.bookingId || undefined;
    return fetchMatchesAvailableForOccurrences({
      listCareHubMatchmaker: (params) =>
        api.listCareHubMatchmaker({
          ...params,
          excludeBookingId,
        }),
      occurrences: rows,
      matchmakerBaseParams: {
        lat: pin.lat,
        lng: pin.lng,
        serviceRole: serviceSelection.serviceRole,
        serviceId: serviceSelection.serviceId,
        duration: serviceSelection.durationMinutes,
        instantOnly: pattern === "instant",
        ...(patientGender ? { gender: patientGender } : {}),
      },
      slot,
      genderFilter: lockedCaregiverGender || patientGender,
      filterPackage: (m) =>
        caregiverOffersAttendantPackage(m, {
          packageKey,
          serviceId: serviceSelection.serviceId,
        }),
    });
  }

  async function refreshAvailableMatchesForSelectedSlot() {
    if (!selectedSlot?.start) {
      await generateSlots();
      return;
    }
    const pin =
      parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng);
    if (!pin) return;
    setLoadingSlots(true);
    try {
      const occurrenceRows =
        pattern === "recurring"
          ? buildRecurringOccurrences({
              recurringPattern,
              startDate: targetDate,
              startTime: selectedSlot.start,
              endTime: selectedSlot.end,
              selectedDates,
              weeklyDayOfWeek,
              count: daysServiceRequired,
            })
          : [
              {
                serviceDate: targetDate,
                startTime: selectedSlot.start,
                endTime: selectedSlot.end,
              },
            ];
      const filtered = await fetchAvailableMatchesForBooking(selectedSlot, occurrenceRows);
      setMatches(filtered);
      setSelectedCaregiver((prev) => {
        if (!prev) return null;
        return filtered.some((m) => String(m.caregiverId) === String(prev.caregiverId))
          ? prev
          : null;
      });
      if (!filtered.length) {
        showToast.info(
          "No caregivers are available for this date and time. Please select another date or time."
        );
      }
    } catch (err) {
      showToast.error(err?.message || "Unable to refresh caregiver availability.");
    } finally {
      setLoadingSlots(false);
    }
  }

  useEffect(() => {
    if (!open) {
      marketplaceHireValidatedRef.current = "";
    }
  }, [open]);

  // Marketplace hire: re-validate prefilled caregiver against all booking dates on open.
  useEffect(() => {
    if (
      !open ||
      !searchPrefill?.skipShiftRedo ||
      step !== CAREHUB_HIRE_STEP.SLOTS ||
      !selectedSlot?.start ||
      !selectedCaregiver?.caregiverId
    ) {
      return undefined;
    }
    const key = [
      String(selectedCaregiver.caregiverId),
      selectedSlot.start,
      selectedSlot.end || selectedSlot.start,
      bookingOccurrences.map((o) => o.serviceDate).join(","),
    ].join("|");
    if (marketplaceHireValidatedRef.current === key) return undefined;

    let cancelled = false;
    (async () => {
      setValidatingSlot(true);
      try {
        const occurrenceRows = bookingOccurrences.filter((o) => o?.serviceDate && o?.startTime);
        const filtered = await fetchAvailableMatchesForBooking(selectedSlot, occurrenceRows);
        if (cancelled || slotPrefillDiscardedRef.current) return;
        const cgId = String(selectedCaregiver.caregiverId);
        if (!filtered.some((m) => String(m.caregiverId) === cgId)) {
          setSelectedCaregiver(null);
          clearSelectionValidation();
          setMatches(filtered);
          showToast.error(
            "This caregiver is not available for your selected dates and time. Please choose another."
          );
          marketplaceHireValidatedRef.current = key;
          return;
        }
        const pin =
          parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) ||
          parseServiceCoords(lat, lng);
        await validateSlotAvailability({
          caregiverId: selectedCaregiver.caregiverId,
          slot: selectedSlot,
          serviceDate: occurrenceRows[0]?.serviceDate || targetDate,
        });
        if (occurrenceRows.length > 1 && pin) {
          const recurringCheck = unwrapRecurringAvailabilityResult(
            await api.validateRecurringAvailability({
              caregiverId: selectedCaregiver.caregiverId,
              occurrences: occurrenceRows,
              durationMinutes: serviceSelection.durationMinutes,
              serviceId: serviceSelection.serviceId,
              lat: pin.lat,
              lng: pin.lng,
              excludeBookingId:
              confirmedBooking?.bookingId || readBookingDraft()?.bookingId || undefined,
            })
          );
          if (!recurringCheck.ok) {
            throw Object.assign(
              new Error(
                recurringCheck.conflicts[0]?.message ||
                  "This caregiver is not available for all selected dates."
              ),
              { code: recurringCheck.conflicts[0]?.code || "RECURRENCE_CONFLICT" }
            );
          }
        }
        if (cancelled) return;
        markSelectionValidated(cgId, selectedSlot);
        setMatches(filtered);
        marketplaceHireValidatedRef.current = key;
      } catch (err) {
        if (!cancelled) {
          setSelectedCaregiver(null);
          clearSelectionValidation();
          showToast.error(
            hireAvailabilityErrorMessage(
              err,
              "This caregiver is no longer available for your selected dates and time."
            )
          );
        }
      } finally {
        if (!cancelled) setValidatingSlot(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validate once per marketplace hire session key
  }, [
    open,
    searchPrefill?.skipShiftRedo,
    step,
    selectedSlot?.start,
    selectedSlot?.end,
    selectedCaregiver?.caregiverId,
    bookingOccurrences,
  ]);

  async function generateSlots() {
    if (!validateSchedulingStep()) return;
    const pin = parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng);
    if (!pin) {
      showToast.error("Set your location on Find Caregivers before generating slots.");
      return;
    }
    if (selectedPatientId) {
      const sub = await refreshPatientSubscription(selectedPatientId);
      if (!patientHasValidCarePassForShift(sub, searchShift, bookingCareDays)) {
        goToCarePassStep({
          fromStep: CAREHUB_HIRE_STEP.SHIFT,
          message: carePassMissingForBookingMessage(sub, searchShift, bookingCareDays),
          patientId: selectedPatientId,
        });
        return;
      }
    }
    setServiceCoords(pin);
    setLoadingSlots(true);
    const generation = ++slotsGenerationRef.current;
    const patientAtStart = String(selectedPatientId || "");
    const stillCurrentGeneration = () =>
      generation === slotsGenerationRef.current &&
      (!patientAtStart || patientAtStart === String(selectedPatientId || ""));
    // Keep the customer's open hire in memory so we exclude it from availability checks.
    let openHire = confirmedBooking;
    if (!openHire?.bookingId && api.getToken?.()) {
      const draftId = readBookingDraft()?.bookingId || null;
      openHire = await hydrateOpenCareHubBooking({
        resumeBookingId: draftId,
        applySchedule: false,
      }).catch(() => null);
    }
    if (!stillCurrentGeneration()) {
      setLoadingSlots(false);
      return;
    }
    const preserveOwn =
      openHire?.bookingId &&
      (openHire.caregiverAccepted || openHire.awaitingCaregiverAcceptance);
    if (!preserveOwn) {
      setSelectedSlot(null);
      clearSelectionValidation();
    }
    setMatches([]);
    try {
      const preferredCaregiverId = caregiver?.id || caregiver?.caregiverId;
      let preferredDayBlocked = false;
      let explainedEmpty = false;
      let radiusBlocked = false;
      const excludeBookingId = openHire?.bookingId || confirmedBooking?.bookingId || undefined;
      const slotQueryDuration = serviceSelection.durationMinutes;
      // Alternate/custom: generate slots for the earliest listed booking date.
      const queryDate =
        pattern === "recurring" &&
        recurringPatternNeedsDatePicker(recurringPattern) &&
        selectedDates.length
          ? [...selectedDates].sort()[0]
          : targetDate;
      if (queryDate && queryDate !== targetDate) setTargetDate(queryDate);
      const bookingDates =
        pattern === "recurring"
          ? resolveHourlyMarketplaceDates({
              pattern,
              recurringPattern,
              searchDate: queryDate,
              selectedDates,
              daysServiceRequired,
              weeklyDayOfWeek,
            })
          : [queryDate];
      const datesToCheck = (bookingDates.length ? bookingDates : [queryDate]).filter(Boolean);
      if (preferredCaregiverId) {
        try {
          // Hard-block all-day leave on ANY occurrence — not only the start date.
          await assertCaregiverAvailableOnDates(api, {
            caregiverId: preferredCaregiverId,
            serviceDates: datesToCheck,
            leaveOnly: true,
          });
        } catch (blockedErr) {
          if (
            blockedErr?.code === "CAREGIVER_BLOCKED_ON_DATE" ||
            /unavailable on .*blocked|leave/i.test(String(blockedErr?.message || ""))
          ) {
            preferredDayBlocked = true;
            explainedEmpty = true;
            showToast.info(
              blockedErr?.message ||
                "This caregiver has leave or a blocked day in your recurring schedule. Change dates or pick another caregiver."
            );
          } else {
            console.warn("Unavailable-on-date check failed:", blockedErr?.message || blockedErr);
          }
        }
      }
      const patientGender =
        caregiverGenderFilterFromPatient(
          patients.find((p) => String(p.patientId || "") === String(selectedPatientId || ""))
        ) || null;
      const matchmakerBaseParams = {
        lat: pin.lat,
        lng: pin.lng,
        serviceRole: serviceSelection.serviceRole,
        serviceId: serviceSelection.serviceId,
        duration: slotQueryDuration,
        instantOnly: pattern === "instant",
        excludeBookingId,
        ...(patientGender ? { gender: patientGender } : {}),
      };
      const filterPackageFn = (m) =>
        caregiverOffersAttendantPackage(m, {
          packageKey,
          serviceId: serviceSelection.serviceId,
        });
      const matchmakerResult =
        pattern === "recurring" && bookingDates.length > 1
          ? await fetchHourlySlotsForAllDates({
              listCareHubMatchmaker: (params) => api.listCareHubMatchmaker(params),
              dates: bookingDates,
              matchmakerBaseParams,
              durationMinutes: slotQueryDuration,
              preferRawSlots: pattern === "instant",
              filterPackage: filterPackageFn,
              genderFilter: patientGender,
            })
          : await (async () => {
              const res = await api.listCareHubMatchmaker({
                date: queryDate,
                ...matchmakerBaseParams,
              });
              const rows = (Array.isArray(res?.matches) ? res.matches : [])
                .map((match) => ({
                  ...match,
                  gender: resolveMatchGender(match) || null,
                }))
                .filter((match) => {
                  if (patientGender && resolveMatchGender(match) !== patientGender) return false;
                  return filterPackageFn(match);
                });
              return {
                matches: rows,
                slots: collectSlotsFromMatches(rows, {
                  serviceRole: serviceSelection.serviceRole,
                  durationMinutes: slotQueryDuration,
                  searchShift,
                  preferRawSlots: pattern === "instant",
                  preferredCaregiverId: null,
                }),
              };
            })();
      const list = (matchmakerResult.matches || [])
        .map((match) => ({
          ...match,
          gender: resolveMatchGender(match) || null,
        }))
        .filter((match) => {
          if (patientGender && resolveMatchGender(match) !== patientGender) return false;
          return filterPackageFn(match);
        });
      const preferredId = String(caregiver.id || caregiver.caregiverId || "");
      const preferredGenderOk =
        !patientGender ||
        resolveMatchGender(caregiver) === patientGender ||
        list.some(
          (m) =>
            String(m.caregiverId) === preferredId &&
            resolveMatchGender(m) === patientGender
        );
      const preferredOffersPackage = caregiverOffersAttendantPackage(caregiver, {
        packageKey,
        serviceId: serviceSelection.serviceId,
      });
      let preferred =
        preferredDayBlocked || !preferredOffersPackage || !preferredGenderOk
          ? null
          : list.find((m) => String(m.caregiverId) === preferredId);
      const rest = list.filter((m) => String(m.caregiverId) !== preferredId);

      if (patientGender && !preferredGenderOk) {
        explainedEmpty = true;
        showToast.info(
          `Only ${patientGender} caregivers are shown for this patient. Showing other matches when available.`
        );
      }

      if (!preferredOffersPackage && !preferredDayBlocked && preferredGenderOk) {
        explainedEmpty = true;
        showToast.info(
          "Selected caregiver does not offer this care package. Showing other matches when available."
        );
      }

      if (
        !preferredDayBlocked &&
        preferredOffersPackage &&
        preferredGenderOk &&
        !preferred?.slots?.length &&
        // Multi-day recurring must come from matchmaker intersection across ALL dates.
        // A single-day direct lookup previously faked availability and failed at confirm.
        !(pattern === "recurring" && bookingDates.length > 1)
      ) {
        try {
          const direct = await api.listAvailableSlots({
            caregiverId: caregiver.id || caregiver.caregiverId,
            date: queryDate,
            duration: slotQueryDuration,
            serviceRole: serviceSelection.serviceRole,
            serviceId: serviceSelection.serviceId,
            lat: pin.lat,
            lng: pin.lng,
            instantOnly: pattern === "instant",
            excludeBookingId,
          });
          const directSlots = Array.isArray(direct?.slots) ? direct.slots : [];
          if (directSlots.length) {
            if (preferred) {
              preferred = { ...preferred, slots: directSlots, isPreferredFallback: false };
            } else {
              preferred = buildPreferredMatchEntry(caregiver, caregiverFee, directSlots, searchShift);
            }
          } else if (direct?.excludedReason) {
            const reason = String(direct.excludedReason);
            if (reason === "PACKAGE_NOT_OFFERED") {
              explainedEmpty = true;
              preferred = null;
              showToast.info(
                "Selected caregiver does not offer this care package. Showing other matches when available."
              );
            } else if (reason === "RADIUS_EXCEEDED") {
              radiusBlocked = true;
              explainedEmpty = true;
              showToast.error(
                "This caregiver is outside their working radius for your pin. Try another caregiver nearby."
              );
            } else if (reason === "CAREGIVER_ENGAGED" || reason === "SESSION_OVERLAP") {
              // Own open hire should already be excluded — only warn for true third-party conflicts.
              if (!excludeBookingId) {
                explainedEmpty = true;
                showToast.info(
                  reason === "SESSION_OVERLAP"
                    ? "Selected caregiver already has a booking covering this live-in day. Pick another start time or caregiver below."
                    : direct.excludedMessage ||
                        "Selected caregiver is on another booking. Try Instant later or pick a different caregiver."
                );
              }
            } else if (reason === "INSTANT_BOOKING_CLOSED") {
              explainedEmpty = true;
              showToast.info(
                "Instant booking is closed for this caregiver right now. Turn on Available Now on their side, or book a scheduled slot."
              );
            } else if (reason === "ON_LEAVE") {
              // Full-day leave — omit this caregiver; keep other matches.
              explainedEmpty = true;
              preferred = null;
              showShiftOverlapPreferencePopup();
            } else if (reason === "NO_WORKING_HOURS") {
              explainedEmpty = true;
              preferred = null;
              showToast.info(
                "Selected caregiver has no hours this day. Showing other matches when available."
              );
            }
          }
        } catch {
          /* matchmaker results still usable when direct lookup fails */
        }
      }

      // Only list caregivers with at least one open slot from matchmaker (or direct slot lookup).
      const ordered = preferred?.slots?.length ? [preferred, ...rest] : rest;
      if (!ordered.length && !radiusBlocked && !explainedEmpty && !preferredDayBlocked) {
        showToast.info(
          pattern === "recurring" && bookingDates.length > 1
            ? "No caregivers are free for every date in this recurring schedule. Try fewer days, skip leave days with custom dates, or choose another start day."
            : preferredOffersPackage
              ? "No caregivers free for this pin and date. Try another day or location."
              : "No caregivers offer this care package for your pin and date. Try Hygiene Care or another day."
        );
      }
      const slotList =
        pattern === "recurring" &&
        bookingDates.length > 1 &&
        Array.isArray(matchmakerResult.slots) &&
        matchmakerResult.slots.length
          ? matchmakerResult.slots
          : collectSlotsFromMatches(ordered, {
              serviceRole: serviceSelection.serviceRole,
              durationMinutes: serviceSelection.durationMinutes,
              searchShift,
              preferRawSlots: pattern === "instant",
              preferredCaregiverId: preferred?.slots?.length || preserveOwn
                ? preferred?.caregiverId || preferredCaregiverId
                : null,
            });
      const overrideSource = openHire || confirmedBooking;
      const overridden = applyOwnBookingSlotOverride(slotList, ordered, overrideSource, { isLiveIn });
      const preferredShiftPricing =
        caregiver?.shiftPricing || caregiver?.profileExtended?.shiftPricing || null;
      const sanitizedMatches = (overridden.matches || []).map((m) => {
        const samePreferred =
          preferredShiftPricing &&
          caregiver &&
          String(m.caregiverId) === String(caregiver.id || caregiver.caregiverId);
        const shiftPricing = m.shiftPricing || (samePreferred ? preferredShiftPricing : null);
        const next = { ...m, shiftPricing: shiftPricing || m.shiftPricing || null };
        const fee = resolveCaregiverVisitFeeInr(next, searchShift, null);
        return {
          ...next,
          estimatedCaregiverFeeInr: fee,
          feeSource: fee != null ? "shiftPricing" : m.feeSource || null,
        };
      });
      if (!stillCurrentGeneration()) return;
      setMatches(sanitizedMatches);
      setAvailableSlots(overridden.slots);
      const keepSelection =
        overrideSource?.bookingId &&
        (selectedSlot || overrideSource.startTime) &&
        isOwnActiveBookingSlot(
          overrideSource,
          overrideSource.requestedCaregiverId,
          selectedSlot || {
            start: overrideSource.startTime,
            end: overrideSource.endTime || overrideSource.startTime,
          }
        );
      if (!keepSelection && !preserveOwn) {
        setSelectedCaregiver(null);
        clearSelectionValidation();
      } else if (preserveOwn && overrideSource?.startTime) {
        const end = overrideSource.endTime || overrideSource.startTime;
        const slot = {
          id: `${overrideSource.startTime}-${end}`,
          start: overrideSource.startTime,
          end,
          label: formatCareHubSlotLabel(overrideSource.startTime, end),
          available: true,
        };
        setSelectedSlot(slot);
        setSelectedCaregiver((prev) => {
          if (prev && String(prev.caregiverId) === String(overrideSource.requestedCaregiverId)) {
            return prev;
          }
          const fromMatches = sanitizedMatches.find(
            (m) => String(m.caregiverId) === String(overrideSource.requestedCaregiverId)
          );
          return (
            fromMatches || {
              ...(caregiver || {}),
              caregiverId: overrideSource.requestedCaregiverId,
              name: overrideSource.caregiverName || caregiver?.name || "Caregiver",
              shiftPricing:
                caregiver?.shiftPricing || caregiver?.profileExtended?.shiftPricing || null,
              estimatedCaregiverFeeInr: resolveCaregiverVisitFeeInr(
                caregiver,
                searchShift,
                null
              ),
            }
          );
        });
        markSelectionValidated(overrideSource.requestedCaregiverId, slot);
      } else if (selectedCaregiver && selectedSlot) {
        markSelectionValidated(selectedCaregiver.caregiverId, selectedSlot);
      }
      setStep(CAREHUB_HIRE_STEP.SLOTS);
      const anyOpen =
        overridden.slots.some((slot) => slot.available !== false) ||
        Boolean(
          overrideSource?.startTime &&
            (overrideSource.caregiverAccepted || overrideSource.awaitingCaregiverAcceptance)
        );
      if (!anyOpen && !explainedEmpty && !radiusBlocked) {
        showToast.info("No open slots for this date. Try another day or adjust your service.");
      } else if (!anyOpen && explainedEmpty && isLiveIn) {
        /* overlap/engaged toast already shown — keep start times visible as unavailable */
      }
      // Keep schedule design — scroll to inline slots instead of swapping screens.
      window.setTimeout(() => {
        document.getElementById("cabd-slots")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } catch (err) {
      if (stillCurrentGeneration()) {
        showToast.error(err?.message || "Unable to generate slots.");
      }
    } finally {
      if (generation === slotsGenerationRef.current) setLoadingSlots(false);
    }
  }

  function resolveCaregiverForSlot(slot) {
    const freeMatches = matches.filter((m) => caregiverHasSelectedSlot(m, slot));
    const preferredId = String(caregiver.id || caregiver.caregiverId || "");
    const preferredFree = freeMatches.find((m) => String(m.caregiverId) === preferredId);
    if (preferredFree) {
      return { match: preferredFree, notice: null, freeMatches };
    }
    if (freeMatches.length) {
      return {
        match: freeMatches[0],
        notice: "Selected caregiver is busy for this slot. Showing available caregivers with the same skills.",
        freeMatches,
      };
    }
    return { match: null, notice: null, freeMatches: [] };
  }

  function markSlotUnavailable(slot) {
    if (!slot) return;
    setAvailableSlots((prev) =>
      (prev || []).map((s) =>
        s.id === slot.id || (s.start === slot.start && s.end === slot.end)
          ? { ...s, available: false }
          : s
      )
    );
  }

  function isSlotConflictError(err) {
    const code = String(err?.code || "");
    return (
      code === "SLOT_LOCKED" ||
      code === "CARE_GIVER_SLOT_UNAVAILABLE" ||
      code === "SLOT_LOCK_FAILED" ||
      code === "SLOT_LOCK_EXPIRED" ||
      code === "SESSION_OVERLAP" ||
      code === "TRAVEL_CONFLICT" ||
      code === "PAST_TIME" ||
      code === "OUTSIDE_WORKING_HOURS" ||
      code === "CAREGIVER_ON_LEAVE" ||
      /pick another slot|overlaps an existing|travel time|already passed|does not work|on leave|no longer available for the selected time|booking hold has expired/i.test(
        String(err?.message || "")
      )
    );
  }

  async function validateSlotAvailability({ caregiverId, slot, serviceDate }) {
    const pin =
      parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng);
    let excludeBookingId = confirmedBooking?.bookingId || null;
    // If state has not hydrated yet, resolve the open CareHub hire so we never
    // validate the caregiver against the customer's own booking.
    if (!excludeBookingId && api.getToken?.()) {
      try {
        const draftId = readBookingDraft()?.bookingId || null;
        const openHire = await hydrateOpenCareHubBooking({
          resumeBookingId: draftId,
          applySchedule: false,
        }).catch(() => null);
        excludeBookingId = openHire?.bookingId || confirmedBooking?.bookingId || null;
      } catch {
        /* continue without exclude */
      }
    }
    return api.validateAvailabilitySlot({
      caregiverId,
      serviceDate: serviceDate || confirmedBooking?.serviceDate || targetDate,
      startTime: slot.start,
      endTime: slot.end,
      durationMinutes: serviceSelection.durationMinutes,
      serviceId: serviceSelection.serviceId,
      lat: pin?.lat,
      lng: pin?.lng,
      excludeBookingId,
    });
  }

  async function syncAcceptedBookingToSlot(slot) {
    if (!confirmedBooking?.bookingId || !slot) return false;
    if (!confirmedBooking.caregiverAccepted && !confirmedBooking.awaitingCaregiverAcceptance) {
      return false;
    }
    if (confirmedBookingMatchesSlot(confirmedBooking, slot)) return true;
    const serviceDate =
      confirmedBooking.serviceDate ||
      bookingOccurrences[0]?.serviceDate ||
      targetDate;
    const endTime = isLiveIn ? slot.start : slot.end;
    try {
      const res = await api.rescheduleBooking(confirmedBooking.bookingId, {
        serviceDate,
        startTime: slot.start,
        endTime,
      });
      const updated = res?.booking || res?.data?.booking || res;
      const times = readBookingTimesFromRow(updated);
      setConfirmedBooking((prev) =>
        prev
          ? {
              ...prev,
              booking: updated || prev.booking,
              startTime: times.startTime || slot.start,
              endTime: times.endTime || endTime,
              serviceDate: times.serviceDate || serviceDate,
              timeLabel: slot.label || `Start ${formatCareHubTime(slot.start)}`,
            }
          : prev
      );
      showToast.success("Start time updated on your accepted booking.");
      return true;
    } catch (err) {
      const code = String(err?.code || "");
      // Self-overlap during CareHub pre-deploy reschedule must not block the customer.
      if (
        code === "SESSION_OVERLAP" ||
        /overlaps an existing/i.test(String(err?.message || ""))
      ) {
        setConfirmedBooking((prev) =>
          prev
            ? {
                ...prev,
                startTime: slot.start,
                endTime: endTime,
                serviceDate,
                timeLabel: slot.label || `Start ${formatCareHubTime(slot.start)}`,
              }
            : prev
        );
        return true;
      }
      showToast.error(
        err?.message || "Could not move the accepted booking to this start time. Try another slot."
      );
      return false;
    }
  }

  function markSelectionValidated(caregiverId, slot) {
    setSlotValidatedFor({
      caregiverId: String(caregiverId),
      start: slot.start,
      end: slot.end,
    });
  }

  function clearSelectionValidation() {
    setSlotValidatedFor(null);
  }

  /** Live-check caregivers for this slot; only accept a free caregiver. */
  async function onPickSlot(slot) {
    if (!slot || validatingSlot) return;
    const ownSlot = isOwnActiveBookingSlot(
      confirmedBooking,
      confirmedBooking?.requestedCaregiverId,
      slot
    );
    if (slot.available === false && !ownSlot) {
      showToast.error("This slot is no longer available. Pick another time.");
      return;
    }

    const { match, notice, freeMatches } = resolveCaregiverForSlot(slot);
    let candidates = match
      ? [match, ...freeMatches.filter((m) => String(m.caregiverId) !== String(match.caregiverId))]
      : freeMatches;

    if (ownSlot && confirmedBooking?.requestedCaregiverId) {
      const ownMatch =
        matches.find(
          (m) => String(m.caregiverId) === String(confirmedBooking.requestedCaregiverId)
        ) ||
        (caregiver &&
        String(caregiver.id || caregiver.caregiverId) ===
          String(confirmedBooking.requestedCaregiverId)
          ? buildPreferredMatchEntry(caregiver, caregiverFee, [
              { startTime: slot.start, endTime: slot.end, start: slot.start, end: slot.end },
            ], searchShift)
          : null);
      if (ownMatch) {
        candidates = [
          ownMatch,
          ...candidates.filter((m) => String(m.caregiverId) !== String(ownMatch.caregiverId)),
        ];
      }
    }

    if (!candidates.length) {
      setSelectedSlot(null);
      setSelectedCaregiver(null);
      clearSelectionValidation();
      if (!ownSlot) markSlotUnavailable(slot);
      showToast.error("No caregivers are free for this slot. Try another time.");
      return;
    }

    setValidatingSlot(true);
    clearSelectionValidation();
    try {
      const pin =
        parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng);
      const occurrenceRows =
        pattern === "recurring"
          ? buildRecurringOccurrences({
              recurringPattern,
              startDate: targetDate,
              startTime: slot.start,
              endTime: slot.end,
              selectedDates,
              weeklyDayOfWeek,
              count: daysServiceRequired,
            })
          : [{ serviceDate: targetDate, startTime: slot.start, endTime: slot.end }];

      if (pin) {
        const filtered = await fetchAvailableMatchesForBooking(slot, occurrenceRows);
        const allowed = new Set(
          filtered.map((m) => String(m.caregiverId || m.id || "")).filter(Boolean)
        );
        candidates = candidates.filter((c) => allowed.has(String(c.caregiverId || c.id || "")));
        if (filtered.length) {
          setMatches((prev) => {
            const own = (prev || []).filter((m) =>
              isOwnActiveBookingSlot(confirmedBooking, m.caregiverId, slot)
            );
            const merged = [...filtered];
            for (const row of own) {
              if (!merged.some((m) => String(m.caregiverId) === String(row.caregiverId))) {
                merged.push(row);
              }
            }
            return merged;
          });
        } else {
          setMatches([]);
        }
        if (!candidates.length) {
          setSelectedSlot(null);
          setSelectedCaregiver(null);
          clearSelectionValidation();
          if (!ownSlot) markSlotUnavailable(slot);
          showToast.error(
            occurrenceRows.length > 1
              ? "No caregivers are available for all selected dates and this time. Try another date or time."
              : "No caregivers are available for this date and time. Try another date or time."
          );
          return;
        }
      }

      let chosen = null;
      let lastError = null;

      for (const candidate of candidates) {
        try {
          if (isOwnActiveBookingSlot(confirmedBooking, candidate.caregiverId, slot)) {
            chosen = candidate;
            break;
          }
          await validateSlotAvailability({
            caregiverId: candidate.caregiverId,
            slot,
            serviceDate: occurrenceRows[0]?.serviceDate || targetDate,
          });
          if (occurrenceRows.length > 1 && pin) {
            const recurringCheck = unwrapRecurringAvailabilityResult(
              await api.validateRecurringAvailability({
                caregiverId: candidate.caregiverId,
                occurrences: occurrenceRows,
                durationMinutes: serviceSelection.durationMinutes,
                serviceId: serviceSelection.serviceId,
                lat: pin.lat,
                lng: pin.lng,
                excludeBookingId:
              confirmedBooking?.bookingId || readBookingDraft()?.bookingId || undefined,
              })
            );
            if (!recurringCheck.ok) {
              throw Object.assign(
                new Error(
                  recurringCheck.conflicts[0]?.message ||
                    "One or more dates are not available for this caregiver."
                ),
                { code: recurringCheck.conflicts[0]?.code || "RECURRENCE_CONFLICT" }
              );
            }
          }
          chosen = candidate;
          break;
        } catch (err) {
          lastError = err;
        }
      }

      if (!chosen) {
        setSelectedSlot(null);
        setSelectedCaregiver(null);
        clearSelectionValidation();
        if (!ownSlot) markSlotUnavailable(slot);
        showToast.error(
          lastError?.message || "This slot is no longer available. Please pick another time."
        );
        return;
      }

      setSelectedSlot(slot);
      setSelectedCaregiver(chosen);
      markSelectionValidated(chosen.caregiverId, slot);
      if (
        confirmedBooking?.bookingId &&
        (confirmedBooking.caregiverAccepted || confirmedBooking.awaitingCaregiverAcceptance) &&
        String(confirmedBooking.requestedCaregiverId) === String(chosen.caregiverId) &&
        !confirmedBookingMatchesSlot(confirmedBooking, slot)
      ) {
        const moved = await syncAcceptedBookingToSlot(slot);
        if (!moved) {
          setSelectedSlot(null);
          setSelectedCaregiver(null);
          clearSelectionValidation();
          return;
        }
      }
      if (notice && String(chosen.caregiverId) !== String(match?.caregiverId)) {
        showToast.info(notice);
      } else if (
        notice &&
        String(chosen.caregiverId) !== String(caregiver.id || caregiver.caregiverId || "")
      ) {
        showToast.info(notice);
      }
    } finally {
      setValidatingSlot(false);
    }
  }

  async function selectCaregiverForSlot(match) {
    if (!match) return;
    if (
      confirmedBooking?.awaitingCaregiverAcceptance &&
      String(confirmedBooking.requestedCaregiverId) !== String(match.caregiverId)
    ) {
      showToast.info("A care request is already pending. Cancel it first to choose another caregiver.");
      return;
    }
    if (!selectedSlot) {
      setSelectedCaregiver(match);
      clearSelectionValidation();
      return;
    }
    if (!caregiverHasSelectedSlot(match, selectedSlot)) {
      showToast.error("This caregiver is not free for the selected slot.");
      return;
    }
    if (validatingSlot) return;

    setValidatingSlot(true);
    clearSelectionValidation();
    try {
      // Own open CareHub hire already holds this caregiver — do not treat it as overlap.
      if (isOwnActiveBookingSlot(confirmedBooking, match.caregiverId, selectedSlot)) {
        setSelectedCaregiver(match);
        markSelectionValidated(match.caregiverId, selectedSlot);
        return;
      }
      if (
        confirmedBooking?.bookingId &&
        (confirmedBooking.caregiverAccepted || confirmedBooking.awaitingCaregiverAcceptance) &&
        String(confirmedBooking.requestedCaregiverId) === String(match.caregiverId)
      ) {
        setSelectedCaregiver(match);
        markSelectionValidated(match.caregiverId, selectedSlot);
        if (!confirmedBookingMatchesSlot(confirmedBooking, selectedSlot)) {
          const moved = await syncAcceptedBookingToSlot(selectedSlot);
          if (!moved) {
            setSelectedCaregiver(null);
            clearSelectionValidation();
          }
        }
        return;
      }

      const pin =
        parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng);
      const occurrenceRows = bookingOccurrences.filter((o) => o?.serviceDate && o?.startTime);
      const filtered = await fetchAvailableMatchesForBooking(selectedSlot, occurrenceRows);
      if (
        !filtered.some((m) => String(m.caregiverId || m.id) === String(match.caregiverId || match.id))
      ) {
        showToast.error(
          occurrenceRows.length > 1
            ? "This caregiver is not available for all selected dates and this time."
            : "This caregiver is not available for the selected date and time."
        );
        setSelectedCaregiver(null);
        clearSelectionValidation();
        if (filtered.length) setMatches(filtered);
        return;
      }

      await validateSlotAvailability({
        caregiverId: match.caregiverId,
        slot: selectedSlot,
        serviceDate: occurrenceRows[0]?.serviceDate || targetDate,
      });

      if (occurrenceRows.length > 1 && pin) {
        const recurringCheck = unwrapRecurringAvailabilityResult(
          await api.validateRecurringAvailability({
            caregiverId: match.caregiverId,
            occurrences: occurrenceRows,
            durationMinutes: serviceSelection.durationMinutes,
            serviceId: serviceSelection.serviceId,
            lat: pin.lat,
            lng: pin.lng,
            excludeBookingId:
              confirmedBooking?.bookingId || readBookingDraft()?.bookingId || undefined,
          })
        );
        if (!recurringCheck.ok) {
          showToast.error(
            hireAvailabilityErrorMessage({
              code: recurringCheck.conflicts[0]?.code || "RECURRENCE_CONFLICT",
              message:
                recurringCheck.conflicts[0]?.message ||
                "This caregiver is not available for all selected dates.",
            })
          );
          setSelectedCaregiver(null);
          clearSelectionValidation();
          return;
        }
      }

      setSelectedCaregiver(match);
      markSelectionValidated(match.caregiverId, selectedSlot);
      if (filtered.length) setMatches(filtered);
    } catch (err) {
      showToast.error(
        err?.message || "This caregiver is not available for the selected slot."
      );
    } finally {
      setValidatingSlot(false);
    }
  }

  async function ensureProfileComplete() {
    try {
      const profileRes = await api.getCustomerProfile().catch(() => null);
      const profile = {
        ...(api.getStoredCustomerProfile?.() || {}),
        ...(profileRes?.user || profileRes || {}),
      };
      if (isCustomerProfileComplete(profile)) return true;
      writeBookingDraft(
        buildDraft({
          step: Math.max(Number(step) || 4, 4),
          resumeToPayment: true,
          resumeAfterLogin: true,
          guidelinesAcknowledged: true,
          caregiverName: caregiver?.name || caregiver?.fullName || "",
        })
      );
      onClose?.();
      navigate("/profile", {
        state: {
          bookingResume: true,
          loginMessage:
            "Add your full name and mobile on your profile, then we will return you to finish this booking.",
        },
      });
      showToast.info("Full name and mobile are needed before confirming payment.");
      return false;
    } catch {
      return true;
    }
  }

  /** Once a booking exists for this session, its Care Pass requirement was already
   * consumed/verified atomically at creation on the backend — re-checking balance
   * later just double-counts that same consumption. Only check balance BEFORE a
   * booking exists. */
  function carePassAlreadySecuredForSession(booking) {
    return Boolean(booking?.bookingId);
  }

  async function proceedFromSlots() {
    if (!selectedSlot || !selectedCaregiver) {
      showToast.error("Pick a slot and caregiver first.");
      return;
    }
    if (careRequestPendingForSlot) {
      showToast.info("Waiting for the caregiver to accept your request.");
      return;
    }
    if (!caregiverRequestAccepted) {
      showToast.info("Send a care request to the caregiver first, then continue after they accept.");
      return;
    }
    if (!canProceedFromSlots) {
      showToast.error("Confirm a free caregiver for this slot before continuing.");
      return;
    }
    if (!parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng)) {
      showToast.error("Set your location on Find Caregivers before continuing.");
      return;
    }
    if (pattern === "recurring" && recurringPatternNeedsDatePicker(recurringPattern) && !selectedDates.length) {
      showToast.error("Go back and select your booking dates.");
      setStep(CAREHUB_HIRE_STEP.SHIFT);
      return;
    }

    if (!isLoggedIn) {
      redirectToCustomerRegister();
      return;
    }
    if (!selectedPatientId) {
      showToast.error("Select a patient to continue.");
      await openPatientStep();
      return;
    }

    setBusy(true);
    try {
      // Once a booking already exists for this session, its Care Pass requirement
      // was already consumed/verified atomically at creation time on the backend —
      // re-checking live balance here just double-counts that same consumption and
      // wrongly re-prompts a purchase for a customer who bought exactly enough passes.
      if (!carePassAlreadySecuredForSession(confirmedBooking)) {
        const sub = await refreshPatientSubscription(selectedPatientId);
        if (!patientHasValidCarePassForShift(sub, searchShift, bookingCareDays)) {
          goToCarePassStep({
            fromStep: CAREHUB_HIRE_STEP.SLOTS,
            message: carePassMissingForBookingMessage(sub, searchShift, bookingCareDays),
            patientId: selectedPatientId,
          });
          return;
        }
      }
      if (
        selectedSlot &&
        !confirmedBookingMatchesSlot(confirmedBooking, selectedSlot)
      ) {
        await syncAcceptedBookingToSlot(selectedSlot);
      }
      setStep(CAREHUB_HIRE_STEP.AGREEMENT);
    } finally {
      setBusy(false);
    }
  }

  async function resolveOwnedPatientId(preferredId) {
    const res = await api.listCustomerPatients();
    const list = extractPatientsList(res);
    setPatients(list);
    const nextId = pickOwnedPatientId(list, preferredId);
    setSelectedPatientId(nextId);
    if (!nextId) setPatientSubscription(null);
    return { patientId: nextId, patients: list };
  }

  async function openPatientStep() {
    if (!isLoggedIn && !api.getToken?.()) {
      redirectToCustomerRegister();
      return;
    }
    setPatientsLoading(true);
    setStep(CAREHUB_HIRE_STEP.PATIENT);
    try {
      const { patientId: nextId } = await resolveOwnedPatientId(
        resumeDraft?.patientId || selectedPatientId || initialPatientId
      );
      if (nextId) {
        await refreshPatientSubscription(nextId);
      }
    } catch (err) {
      showToast.error(err?.message || "Unable to load patients.");
    } finally {
      setPatientsLoading(false);
    }
  }

  async function refreshPatientSubscription(patientId) {
    if (!patientId) {
      setPatientSubscription(null);
      return null;
    }
    try {
      const res = await api.getPatientSubscriptionStatus(patientId);
      // Prefer status DTO (active/planId/planCode), not nested DB subscription row.
      const status = unwrapPatientSubscriptionStatus(res);
      setPatientSubscription(status);
      return status;
    } catch (err) {
      // Do not mask ownership failures as "Inactive" — that hides stale draft IDs.
      if (err?.code === "PATIENT_NOT_FOUND" || err?.status === 404) {
        setPatientSubscription(null);
        return null;
      }
      setPatientSubscription({ active: false, status: "NONE" });
      return null;
    }
  }

  function hasValidPassForCurrentShift(sub = patientSubscription) {
    return patientHasValidCarePassForShift(sub, searchShift, bookingCareDays);
  }

  async function continueFromPatientStep() {
    userWizardBackRef.current = false;
    if (!selectedPatientId) {
      showToast.error("Select a patient to continue.");
      return;
    }
    if (!isLoggedIn && !api.getToken?.()) {
      redirectToCustomerRegister();
      return;
    }
    if (!isCareTypeBookingEnabled(careRole)) {
      showToast.info("This service is Coming Soon and cannot be booked yet.");
      return;
    }
    setBusy(true);
    try {
      const sub = await refreshPatientSubscription(selectedPatientId);
      if (!patientHasValidCarePassForShift(sub, searchShift, bookingCareDays)) {
        const skipShiftFromSearch = Boolean(searchPrefill?.skipShiftRedo && searchPrefill?.selectedSlot);
        goToCarePassStep({
          fromStep: skipShiftFromSearch ? CAREHUB_HIRE_STEP.SLOTS : CAREHUB_HIRE_STEP.SHIFT,
          message: carePassMissingForBookingMessage(sub, searchShift, bookingCareDays),
          patientId: selectedPatientId,
        });
        return;
      }
      setPrvRequired(null);
      const patientChangedFromSearch =
        slotPrefillDiscardedRef.current ||
        (Boolean(String(initialPatientId || "").trim()) &&
          String(selectedPatientId || "") !== String(initialPatientId || ""));
      if (patientChangedFromSearch) {
        applySearchPrefillToState(searchPrefill, { includeSlot: false });
        clearGeneratedAvailability();
        setStep(CAREHUB_HIRE_STEP.SHIFT);
        return;
      }
      const skipShiftFromSearch = applySearchPrefillToState(searchPrefill);
      setStep(skipShiftFromSearch ? CAREHUB_HIRE_STEP.SLOTS : CAREHUB_HIRE_STEP.SHIFT);
    } finally {
      setBusy(false);
    }
  }

  async function continueFromCarePassStep({ planId, pack, renewal }) {
    userWizardBackRef.current = false;
    const activePatientId = String(
      selectedPatientId || selectedPatient?.patientId || selectedPatient?.patient_id || ""
    ).trim();
    const priorDraft = readBookingDraft() || {};
    const resumeTo = resolveResumeStepAfterCarePass(
      priorDraft.resumeStepAfterCarePass ||
        (selectedSlot || priorDraft.selectedSlot
          ? CAREHUB_HIRE_STEP.SLOTS
          : CAREHUB_HIRE_STEP.SHIFT)
    );
    writeBookingDraft(
      buildDraft({
        step: CAREHUB_HIRE_STEP.CARE_PASS,
        resumeStepAfterCarePass: resumeTo,
        resumeToPayment: true,
        resumeAfterCarePass: true,
        patientId: activePatientId,
        guidelinesAcknowledged: true,
        caregiverName: caregiver?.name || caregiver?.fullName || "",
      })
    );
    if (!activePatientId) {
      showToast.error("Select a patient for this Care Pass.");
      return;
    }
    if (!planId) {
      showToast.error("Choose a Care Pass to continue.");
      return;
    }
    setBusy(true);
    try {
      const paid = await payCarePass({
        patientId: activePatientId,
        planId,
        packId: pack,
        renewal,
      });
      if (paid?.pendingConfirmation) {
        showToast.info("Payment received. Care Pass activation may take a moment.");
      } else {
        showToast.success("Care Pass activated.");
      }
      await resumeBookingAfterCarePassPurchase({ patientId: activePatientId });
    } catch (err) {
      // Keep booking selections + draft so cancel/fail can retry without restarting.
      if (/sign in|login|unauthorized|401/i.test(String(err?.message || ""))) {
        showToast.error("Please sign in to complete your Care Pass purchase.");
        redirectToCustomerRegister();
      } else if (!/cancelled/i.test(String(err?.message || ""))) {
        showToast.error(err?.message || "Unable to complete Care Pass payment.");
      }
    } finally {
      setBusy(false);
    }
  }

  function redirectToCustomerRegister() {
    writeBookingDraft(
      buildDraft({
        step: CAREHUB_HIRE_STEP.PATIENT,
        resumeAfterLogin: true,
        resumeToPayment: true,
        guidelinesAcknowledged: true,
        caregiverName: caregiver?.name || caregiver?.fullName || "",
      })
    );
    const caregiverId = caregiver?.id || caregiver?.caregiverId;
    const currentPath = window.location.pathname || "/";
    const overlayBase =
      currentPath === "/marketplace" || currentPath.startsWith("/marketplace/") ? "/" : currentPath;
    onClose?.();
    navigate(
      buildAuthOverlayUrl(overlayBase, {
        returnTo: "/marketplace",
        tab: "create",
        role: "customer",
        action: AUTH_ACTIONS.BOOK,
        caregiverId,
      }),
      {
        replace: true,
        state: {
          loginMessage: "Create your account to continue this booking.",
        },
      }
    );
  }

  function buildCustomerConfirmDetails(caregiverOverride = null) {
    const cg = caregiverOverride || selectedCaregiver;
    const visitFee = resolveCaregiverVisitFeeInr(
      {
        ...cg,
        estimatedCaregiverFeeInr:
          estimate?.invoice?.caregiverFeeInr ?? cg?.estimatedCaregiverFeeInr,
      },
      searchShift,
      null
    );
    const days = Math.max(1, bookingCareDays);
    const visit = visitFee != null ? Math.round(Number(visitFee) || 0) : null;
    const total = visit != null ? visit * days : null;
    return {
      heading: "Shift terms of care attendant",
      billingLabel: "Billing terms with care attendant",
      rateLabel:
        total == null
          ? "As set by caregiver"
          : days > 1
            ? `${formatInr(total)} total (${formatInr(visit)}/day × ${days} days)`
            : formatInr(visit),
      rateInr: total,
      shiftTiming:
        selectedSlot?.label ||
        serviceSelection.serviceLabel ||
        searchShift ||
        "Selected duty shift",
      startDate: formatDisplayDate(bookingOccurrences[0]?.serviceDate || targetDate),
      startDateIso: String(bookingOccurrences[0]?.serviceDate || targetDate || "").slice(0, 10),
      daysRequired: String(days),
    };
  }

  async function loadEstimateAndGoPay(caregiverOverride = null) {
    const estimateCaregiver = caregiverOverride || selectedCaregiver;
    if (!estimateCaregiver || !selectedSlot) {
      showToast.error("Pick a slot and caregiver first.");
      return;
    }
    if (caregiverOverride) setSelectedCaregiver(caregiverOverride);
    setBusy(true);
    try {
      const pin =
        parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng);
      if (!pin) {
        throw new Error("Set your location on Find Caregivers before continuing.");
      }
      setServiceCoords(pin);

      const firstOccurrence = bookingOccurrences[0] || {
        serviceDate: targetDate,
        startTime: selectedSlot.start,
        endTime: selectedSlot.end,
      };
      const liveInTimes = isLiveInClockTimes(
        firstOccurrence.startTime,
        firstOccurrence.endTime,
        isLiveIn
      );

      await assertCaregiverAvailableOnDates(api, {
        caregiverId: estimateCaregiver.caregiverId,
        serviceDates:
          pattern === "recurring" && bookingOccurrences.length
            ? bookingOccurrences.map((o) => o.serviceDate)
            : [firstOccurrence.serviceDate],
        leaveOnly: true,
      });

      if (pattern === "recurring" && bookingOccurrences.length > 1) {
        const validation = unwrapRecurringAvailabilityResult(
          await api.validateRecurringAvailability({
            caregiverId: estimateCaregiver.caregiverId,
            occurrences: bookingOccurrences,
            durationMinutes: serviceSelection.durationMinutes,
            serviceId: serviceSelection.serviceId,
            lat: pin.lat,
            lng: pin.lng,
            excludeBookingId:
              confirmedBooking?.bookingId || readBookingDraft()?.bookingId || undefined,
          })
        );
        if (!validation.ok) {
          const conflict = validation.conflicts[0];
          throw Object.assign(
            new Error(
              conflict?.message ||
                "One or more recurring dates are not available. Adjust your pattern and try again."
            ),
            { code: conflict?.code || "RECURRENCE_CONFLICT" }
          );
        }
      }

      const est = await api.estimateCareHubGuestBooking({
        caregiverId: estimateCaregiver.caregiverId,
        serviceRole: serviceSelection.serviceRole,
        serviceId: serviceSelection.serviceId,
        durationMinutes: serviceSelection.durationMinutes,
        serviceDate: firstOccurrence.serviceDate,
        startTime: firstOccurrence.startTime,
        ...(liveInTimes ? {} : { endTime: firstOccurrence.endTime }),
        lat: pin.lat,
        lng: pin.lng,
        caregiverFeeInr: estimateCaregiver.estimatedCaregiverFeeInr ?? caregiverFee,
        searchShift: searchShift || undefined,
        // Re-validating after caregiver accept must exclude THIS booking or it
        // self-conflicts ("This live-in interval overlaps an existing booking.").
        excludeBookingId:
          confirmedBooking?.bookingId || readBookingDraft()?.bookingId || undefined,
      });
      setEstimate(est);
      setStep(CAREHUB_HIRE_STEP.CONFIRM);
      } catch (err) {
      const code = err?.code || err?.errorCode || err?.data?.code;
      const msg = String(err?.message || "");
      if (code === "RADIUS_EXCEEDED" || /working range/i.test(msg)) {
        showToast.error(
          "Your pin is outside this caregiver's working range. Go back and choose a nearby caregiver."
        );
        setStep(CAREHUB_HIRE_STEP.SLOTS);
      } else if (
        code === "BOOKING_DATE_OUTSIDE_WINDOW" ||
        /within the next \d+ days|outside this window|Unable to book outside/i.test(msg)
      ) {
        showToast.error(
          "Service date is outside the 7-day booking window. Please choose a date within the next 7 days."
        );
      } else if (isSlotConflictError(err)) {
        markSlotUnavailable(selectedSlot);
        setSelectedSlot(null);
        setSelectedCaregiver(null);
        clearSelectionValidation();
        setEstimate(null);
        setStep(CAREHUB_HIRE_STEP.SLOTS);
        showToast.error(
          err?.message || "This slot is no longer available. Please pick another slot."
        );
      } else if (
        code === "CAREGIVER_OVERLAP" ||
        code === "SESSION_OVERLAP" ||
        code === "RECURRENCE_CONFLICT" ||
        /overlaps an existing|overlapping/i.test(msg)
      ) {
        showToast.error(hireAvailabilityErrorMessage(err));
        setSelectedCaregiver(null);
        setEstimate(null);
        clearSelectionValidation();
        setStep(CAREHUB_HIRE_STEP.SLOTS);
        await refreshAvailableMatchesForSelectedSlot().catch(() => null);
      } else if (
        code === "CAREGIVER_BLOCKED_ON_DATE" ||
        /unavailable on .*blocked|leave day|not rostered|already booked/i.test(msg)
      ) {
        showShiftOverlapPreferencePopup();
        setStep(CAREHUB_HIRE_STEP.SHIFT);
      } else {
        showToast.error(err?.message || "Unable to load booking estimate.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function sendOtp() {
    if (!name.trim() || !phone.trim()) {
      showToast.error("Enter your name and phone.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.sendPhoneOtp({
        mobile: phone.trim(),
        purpose: "REGISTER",
        name: name.trim(),
      });
      setOtpToken(res?.verificationToken || res?.token || null);
      setOtpSent(true);
      if (res?.devOtp || res?.otp) setOtp(String(res.devOtp || res.otp));
      showToast.success("OTP sent.");
    } catch (err) {
      showToast.error(err?.message || "Unable to send OTP.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtpAndContinue() {
    setBusy(true);
    try {
      let verified = null;
      try {
        verified = await api.loginWithPhoneOtp({
          mobile: phone.trim(),
          otp: otp.trim(),
          verificationToken: otpToken,
          name: name.trim(),
          role: "CUSTOMER",
        });
      } catch {
        verified = await api.verifyPhoneOtp({
          mobile: phone.trim(),
          otp: otp.trim(),
          verificationToken: otpToken,
        });
        if (verified?.token) api.setToken(verified.token);
        else {
          const reg = await api.createUser({
            name: name.trim(),
            mobile: phone.trim(),
            phoneOtpVerificationToken: otpToken || verified?.verificationToken,
            role: "CUSTOMER",
          });
          if (reg?.token) api.setToken(reg.token);
        }
      }
      if (verified?.token) api.setToken(verified.token);
      if (!(await ensureProfileComplete())) return;
      await loadEstimateAndGoPay();
    } catch (err) {
      showToast.error(err?.message || "OTP verification failed.");
      setBusy(false);
    }
  }

  async function cancelPendingCareRequest() {
    const bookingId = confirmedBooking?.bookingId;
    if (!bookingId) return;
    if (
      !window.confirm(
        "Cancel this care request? The Care Attendant will be released and you can send a new request."
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.cancelBooking(bookingId, {
        reason: "Customer cancelled pending care request",
      });
      setConfirmedBooking(null);
      clearSelectionValidation();
      setSelectedCaregiver(null);
      clearBookingDraft();
      showToast.success("Request cancelled. You can choose another start time or Care Attendant.");
    } catch (err) {
      showToast.error(err?.message || "Unable to cancel the request.");
    } finally {
      setBusy(false);
    }
  }

  async function sendRequestForCaregiver(match) {
    if (!match || !selectedSlot) {
      showToast.error("Pick a time slot and caregiver first.");
      return;
    }
    if (paying || busy) return;
    if (
      confirmedBooking?.awaitingCaregiverAcceptance &&
      String(confirmedBooking.requestedCaregiverId) !== String(match.caregiverId)
    ) {
      showToast.info("A care request is already pending. Cancel it first to request another caregiver.");
      return;
    }
    if (
      confirmedBooking?.awaitingCaregiverAcceptance &&
      String(confirmedBooking.requestedCaregiverId) === String(match.caregiverId) &&
      confirmedBookingMatchesSlot(confirmedBooking, selectedSlot)
    ) {
      showToast.info("Request already sent. Waiting for caregiver response — or cancel to try another.");
      return;
    }
    if (
      confirmedBooking?.caregiverAccepted &&
      String(confirmedBooking.requestedCaregiverId) === String(match.caregiverId) &&
      confirmedBookingMatchesSlot(confirmedBooking, selectedSlot)
    ) {
      showToast.info("This caregiver already accepted for this start time. Use Proceed to continue.");
      return;
    }
    if (!caregiverHasSelectedSlot(match, selectedSlot)) {
      showToast.error("This caregiver is not free for the selected slot.");
      return;
    }
    if (!parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng)) {
      showToast.error("Set your location on Find Caregivers before sending a request.");
      return;
    }
    if (!isLoggedIn) {
      redirectToCustomerRegister();
      return;
    }
    if (!selectedPatientId) {
      showToast.error("Select a patient before sending a request.");
      await openPatientStep();
      return;
    }

    setBusy(true);
    try {
      const sub = await refreshPatientSubscription(selectedPatientId);
      if (!patientHasValidCarePassForShift(sub, searchShift, bookingCareDays)) {
        goToCarePassStep({
          fromStep: CAREHUB_HIRE_STEP.SLOTS,
          message: carePassMissingForBookingMessage(sub, searchShift, bookingCareDays),
          patientId: selectedPatientId,
        });
        return;
      }
      const pin =
        parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng);
      const occurrenceRows = bookingOccurrences.filter(
        (o) => o?.serviceDate && o?.startTime && o?.endTime
      );
      const excludeBookingId = confirmedBooking?.bookingId || readBookingDraft()?.bookingId || null;
      await validateCaregiverAvailableForOccurrences({
        validateAvailabilitySlot: (payload) =>
          api.validateAvailabilitySlot({
            ...payload,
            excludeBookingId: payload.excludeBookingId ?? excludeBookingId ?? undefined,
          }),
        validateRecurringAvailability: (payload) => api.validateRecurringAvailability(payload),
        caregiverId: match.caregiverId,
        occurrences: occurrenceRows,
        durationMinutes: serviceSelection.durationMinutes,
        serviceId: serviceSelection.serviceId,
        lat: pin?.lat,
        lng: pin?.lng,
        excludeBookingId,
      });
      markSelectionValidated(match.caregiverId, selectedSlot);
      setSelectedCaregiver(match);
    } catch (err) {
      const msg = hireAvailabilityErrorMessage(err, "Unable to send care request.");
      showToast.error(msg);
      if (isRemappedSlotTakenError(err)) {
        setSelectedCaregiver(null);
        clearSelectionValidation();
        await refreshAvailableMatchesForSelectedSlot().catch(() => null);
      }
      return;
    } finally {
      setBusy(false);
    }

    await payAndConfirm(match);
  }

  async function payAndConfirm(caregiverOverride = null) {
    const requestCaregiver = caregiverOverride || selectedCaregiver;
    if (!requestCaregiver || !selectedSlot) return;
    let acquiredLockToken = null;
    setPaying(true);
    try {
      // Re-validate ownership — draft/resume can keep a stale Patient ID that still renders in the UI.
      let patientIdForBooking = selectedPatientId;
      let patientsForBooking = patients;
      try {
        const owned = await resolveOwnedPatientId(selectedPatientId);
        patientIdForBooking = owned.patientId;
        patientsForBooking = owned.patients;
      } catch {
        /* fall through to empty check */
      }
      if (!patientIdForBooking) {
        showToast.error("Select a patient on this account before confirming.");
        await openPatientStep();
        return;
      }

      const pin =
        parseServiceCoords(serviceCoords?.lat, serviceCoords?.lng) || parseServiceCoords(lat, lng);
      if (!pin) {
        throw new Error("Set your location on Find Caregivers before confirming.");
      }

      const firstOccurrence = bookingOccurrences[0] || {
        serviceDate: targetDate,
        startTime: selectedSlot.start,
        endTime: selectedSlot.end,
      };
      const liveInTimes = isLiveInClockTimes(
        firstOccurrence.startTime,
        firstOccurrence.endTime,
        isLiveIn
      );

      await assertCaregiverAvailableOnDates(api, {
        caregiverId: requestCaregiver.caregiverId,
        serviceDates:
          pattern === "recurring" && bookingOccurrences.length
            ? bookingOccurrences.map((o) => o.serviceDate)
            : [firstOccurrence.serviceDate],
        leaveOnly: true,
      });

      await validateSlotAvailability({
        caregiverId: requestCaregiver.caregiverId,
        slot: selectedSlot,
        serviceDate: firstOccurrence.serviceDate,
      });

      if (pattern === "recurring" && bookingOccurrences.length > 1) {
        const recurringCheck = unwrapRecurringAvailabilityResult(
          await api.validateRecurringAvailability({
            caregiverId: requestCaregiver.caregiverId,
            occurrences: bookingOccurrences,
            durationMinutes: serviceSelection.durationMinutes,
            serviceId: serviceSelection.serviceId,
            lat: pin.lat,
            lng: pin.lng,
            excludeBookingId:
              confirmedBooking?.bookingId || readBookingDraft()?.bookingId || undefined,
          })
        );
        if (!recurringCheck.ok) {
          const conflict = recurringCheck.conflicts[0];
          throw Object.assign(
            new Error(
              conflict?.message ||
                "This caregiver is no longer available for all selected dates."
            ),
            { code: conflict?.code || "RECURRENCE_CONFLICT" }
          );
        }
      }

      const bookingDetails =
        confirmedBooking?.booking?.details_json ||
        confirmedBooking?.booking?.details ||
        {};
      const needsReselect =
        confirmedBooking?.bookingId &&
        (confirmedBooking?.caregiverDeclined ||
          confirmedBooking?.reselectRequired ||
          bookingDetails?.careHubDispatch?.reselectRequired);

      if (needsReselect) {
        const res = await api.reselectBookingCaregiver(confirmedBooking.bookingId, {
          caregiverId: requestCaregiver.caregiverId,
        });
        const booking = res?.booking || res;
        const bookingStatus = String(booking?.status || "").toUpperCase();
        const autoAccepted = ACCEPTED_BOOKING_STATUSES.has(bookingStatus);
        const nextBookingId = booking?.id || confirmedBooking.bookingId;
        const bookingDetailsNext =
          booking?.details_json || booking?.details || bookingDetails;
        const acceptDeadline =
          bookingDetailsNext?.holdExpiresAt ||
          bookingDetailsNext?.acceptDeadlineAt ||
          confirmedBooking.holdExpiresAt ||
          null;
        const patientRow = patientsForBooking.find((p) => p.patientId === patientIdForBooking);
        setReplacementCaregivers([]);
        setConfirmedBooking({
          booking,
          bookingId: nextBookingId,
          requestedCaregiverId: requestCaregiver.caregiverId,
          caregiverName: requestCaregiver?.name,
          patientName: patientRow?.name || patientIdForBooking,
          dateLabel: formatDisplayDate(firstOccurrence.serviceDate),
          timeLabel: selectedSlot?.label || firstOccurrence.startTime,
          startTime: firstOccurrence.startTime,
          endTime: liveInTimes ? firstOccurrence.startTime : firstOccurrence.endTime,
          serviceDate: firstOccurrence.serviceDate,
          searchShift: searchShift || null,
          serviceId: serviceSelection.serviceId || null,
          durationMinutes: serviceSelection.durationMinutes || null,
          subscriptionLabel: patientSubscription?.planCode || "Service subscription",
          prvInitiated: false,
          awaitingCaregiverAcceptance: !autoAccepted && bookingStatus === "PENDING",
          caregiverAccepted: autoAccepted,
          caregiverDeclined: false,
          reselectRequired: false,
          excludedCaregiverIds: bookingDetailsNext?.matching?.excludedCaregiverIds || [],
          holdExpiresAt: acceptDeadline,
        });
        writeBookingDraft(
          buildDraft({
            bookingId: nextBookingId,
            step: CAREHUB_HIRE_STEP.SLOTS,
            selectedSlot,
            selectedCaregiver: requestCaregiver,
            patientId: patientIdForBooking,
            searchShift: searchShift || null,
          })
        );
        setAgreementAcknowledged(false);
        setDeployAddressOk(false);
        setStep(CAREHUB_HIRE_STEP.SLOTS);
        showToast.success(
          autoAccepted
            ? "Caregiver accepted your request. You can proceed."
            : "Care request sent. Waiting for caregiver response."
        );
        return;
      }

      const locked = await api.lockAvailabilitySlot({
        caregiverId: requestCaregiver.caregiverId,
        serviceDate: firstOccurrence.serviceDate,
        startTime: firstOccurrence.startTime,
        endTime: liveInTimes ? firstOccurrence.startTime : firstOccurrence.endTime,
        durationMinutes: serviceSelection.durationMinutes,
        serviceId: serviceSelection.serviceId,
        lat: pin.lat,
        lng: pin.lng,
        excludeBookingId:
          confirmedBooking?.bookingId || readBookingDraft()?.bookingId || undefined,
      });
      acquiredLockToken = locked?.lockToken || null;
      const confirmedTerms = buildCustomerConfirmDetails(requestCaregiver);
      const res = await api.confirmCareHubGuestBooking({
        caregiverId: requestCaregiver.caregiverId,
        serviceRole: serviceSelection.serviceRole,
        serviceId: serviceSelection.serviceId,
        durationMinutes: serviceSelection.durationMinutes,
        serviceDate: firstOccurrence.serviceDate,
        startTime: firstOccurrence.startTime,
        ...(liveInTimes ? {} : { endTime: firstOccurrence.endTime }),
        lat: pin.lat,
        lng: pin.lng,
        address: addressLabel || "Client address",
        lockToken: acquiredLockToken,
        caregiverFeeInr: requestCaregiver.estimatedCaregiverFeeInr ?? caregiverFee,
        schedulingPattern: pattern,
        recurringPattern: pattern === "recurring" ? recurringPattern : undefined,
        recurrenceOccurrences:
          pattern === "recurring" && bookingOccurrences.length ? bookingOccurrences : undefined,
        patientId: patientIdForBooking,
        policeVerification: offerPoliceVerification
          ? { deferred: true, option: "STANDARD_PRV" }
          : { required: false, deferred: false, option: "SKIPPED_HOURLY" },
        searchShift: searchShift || undefined,
        excludeBookingId:
          confirmedBooking?.bookingId || readBookingDraft()?.bookingId || undefined,
        customerConfirmedTerms: confirmedTerms,
        agreement: {
          ...digitalAgreement,
          // Customer accepts Digital Agreement after caregiver accept (duty deployment).
          // PRV is offered only for 12h / 24h shifts after agreement.
          acknowledgedAt: null,
          deferredToDutyDeployment: true,
        },
      });
      const patientRow = patientsForBooking.find((p) => p.patientId === patientIdForBooking);
      const bookingStatus = String(res?.booking?.status || res?.status || "").toUpperCase();
      const autoAccepted = ACCEPTED_BOOKING_STATUSES.has(bookingStatus);
      const nextBookingId = res?.booking?.id || res?.bookingId || res?.id;
      const nextConfirmed = {
        booking: res?.booking || res,
        bookingId: nextBookingId,
        requestedCaregiverId: requestCaregiver.caregiverId,
        caregiverName: requestCaregiver?.name,
        patientName: patientRow?.name || patientIdForBooking,
        dateLabel: formatDisplayDate(firstOccurrence.serviceDate),
        timeLabel: selectedSlot?.label || firstOccurrence.startTime,
        startTime: firstOccurrence.startTime,
        endTime: liveInTimes ? firstOccurrence.startTime : firstOccurrence.endTime,
        serviceDate: firstOccurrence.serviceDate,
        searchShift: searchShift || null,
        serviceId: serviceSelection.serviceId || null,
        durationMinutes: serviceSelection.durationMinutes || null,
        subscriptionLabel: patientSubscription?.planCode || "Service subscription",
        prvInitiated: false,
        awaitingCaregiverAcceptance: !autoAccepted && bookingStatus === "PENDING",
        caregiverAccepted: autoAccepted,
        caregiverDeclined: false,
        holdExpiresAt:
          res?.holdExpiresAt ||
          res?.booking?.holdExpiresAt ||
          res?.booking?.acceptDeadlineAt ||
          res?.booking?.details_json?.holdExpiresAt ||
          locked?.holdExpiresAt ||
          locked?.expiresAt ||
          null,
      };
      setConfirmedBooking(nextConfirmed);
      // Keep this booking's id so reopen resumes Accept → Confirm (not a different hire).
      writeBookingDraft(
        buildDraft({
          bookingId: nextBookingId,
          step: CAREHUB_HIRE_STEP.SLOTS,
          selectedSlot,
          selectedCaregiver: requestCaregiver,
          patientId: patientIdForBooking,
          searchShift: searchShift || null,
        })
      );
      setAgreementAcknowledged(false);
      setDeployAddressOk(false);
      setStep(CAREHUB_HIRE_STEP.SLOTS);
      showToast.success(
        autoAccepted
          ? "Caregiver accepted your request. You can proceed."
          : "Care request sent. Waiting for caregiver response."
      );
    } catch (err) {
      if (acquiredLockToken) {
        await api.releaseAvailabilityLock(acquiredLockToken).catch(() => {});
      }
      if (isDuplicateActiveEngagementError(err)) {
        setDuplicateEngagement({
          existingBookingId: err.existingBookingId || err?.data?.existingBookingId || null,
        });
        return;
      }
      if (err?.code === "PATIENT_NOT_FOUND" || /patient not found/i.test(String(err?.message || ""))) {
        showToast.error("That patient is not linked to this account. Select your patient and try again.");
        await openPatientStep();
        return;
      }
      if (
        err?.code === "PATIENT_SUBSCRIPTION_REQUIRED" ||
        err?.code === "CARE_PASS_TYPE_MISMATCH" ||
        err?.code === "CARE_PASS_DAYS_EXHAUSTED" ||
        err?.code === "CARE_PASS_BOOKINGS_EXHAUSTED" ||
        err?.status === 402
      ) {
        showToast.info(
          err?.code === "CARE_PASS_DAYS_EXHAUSTED" || err?.code === "CARE_PASS_BOOKINGS_EXHAUSTED"
            ? carePassMissingForBookingMessage(patientSubscription, searchShift, bookingCareDays) ||
                formatUserFacingError(err, USER_FACING_ERRORS[err?.code]?.message)
            : formatUserFacingError(
                err,
                USER_FACING_ERRORS.PATIENT_SUBSCRIPTION_REQUIRED.message
              )
        );
        goToCarePassStep({
          fromStep: CAREHUB_HIRE_STEP.CONFIRM,
          patientId: selectedPatientId,
        });
        return;
      }
      if (err?.code === "SERVICE_COMING_SOON" || err?.code === "INVALID_SHIFT_WINDOW") {
        notifyUserFacingError(err, "Unable to complete this booking.");
        return;
      }
      if (
        err?.code === "BOOKING_START_TIME_PAST" ||
        err?.code === "PAST_TIME" ||
        /already started or passed|cannot be in the past|time has already passed/i.test(
          String(err?.message || "")
        )
      ) {
        showToast.error(hireAvailabilityErrorMessage(err));
        return;
      }
      if (
        err?.code === "BOOKING_DATE_OUTSIDE_WINDOW" ||
        /within the next \d+ days|outside this window|Unable to book outside/i.test(String(err?.message || ""))
      ) {
        showToast.error(
          "Service date is outside the 7-day booking window. Please choose a date within the next 7 days."
        );
        return;
      }
      if (
        err?.code === "CAREGIVER_BLOCKED_ON_DATE" ||
        /unavailable on .*blocked|leave day|not rostered|already booked/i.test(
          String(err?.message || "")
        )
      ) {
        showShiftOverlapPreferencePopup();
        setStep(CAREHUB_HIRE_STEP.SHIFT);
        return;
      }
      if (
        err?.code === "CAREGIVER_OVERLAP" ||
        err?.code === "SESSION_OVERLAP" ||
        err?.code === "TEMPORARILY_RESERVED" ||
        err?.code === "RECURRENCE_CONFLICT" ||
        err?.code === "CAREGIVER_UNAVAILABLE" ||
        /overlaps an existing|overlapping|unavailable on .*(pick another date|already booked)/i.test(
          String(err?.message || "")
        )
      ) {
        showToast.error(
          "That caregiver is already booked for this time. Showing other attendants who are free."
        );
        setSelectedCaregiver(null);
        setEstimate(null);
        clearSelectionValidation();
        setStep(CAREHUB_HIRE_STEP.SLOTS);
        try {
          await refreshAvailableMatchesForSelectedSlot();
        } catch {
          /* ignore */
        }
        return;
      }
      if (isSlotConflictError(err)) {
        markSlotUnavailable(selectedSlot);
        setSelectedSlot(null);
        setSelectedCaregiver(null);
        clearSelectionValidation();
        setEstimate(null);
        setStep(CAREHUB_HIRE_STEP.SLOTS);
        showToast.error(
          err?.code === "CARE_GIVER_SLOT_UNAVAILABLE" || err?.code === "SLOT_LOCKED"
            ? "Sorry, this caregiver is no longer available for the selected time. Please select another caregiver."
            : err?.code === "SLOT_LOCK_EXPIRED"
              ? "Your booking hold has expired. Please select the caregiver and time again."
              : err?.message || "This slot is no longer available. Please pick another slot."
        );
        try {
          await refreshAvailableMatchesForSelectedSlot();
        } catch {
          /* ignore */
        }
        return;
      }
      notifyUserFacingError(err, "We could not complete your booking. Please check your details and try again.");
    } finally {
      setPaying(false);
    }
  }

  const slotHint = getSlotHint(careRole, searchShift);
  const showDatePicker = recurringPatternNeedsDatePicker(recurringPattern);
  const showWeeklyDay = recurringPatternNeedsWeeklyDay(recurringPattern);
  const autoRecurring = recurringPatternIsAuto(recurringPattern);
  const includeCarePassStep = !hasValidPassForCurrentShift();
  const hireWizardProgress = getCareHubHireWizardProgress(step, {
    includePrv: offerPoliceVerification,
    includeCarePass: includeCarePassStep,
  });
  const skipShiftFromMarketplace = Boolean(
    !shiftChangedInWizard &&
    (searchPrefill?.skipShiftRedo || resumeDraft?.skipShiftRedo) &&
      (selectedSlot?.start ||
        searchPrefill?.selectedSlot?.start ||
        resumeDraft?.selectedSlot?.start ||
        resumeDraft?.selectedSlot?.startTime)
  );
  const hireFromMarketplaceSearch = Boolean(skipShiftFromMarketplace && step === CAREHUB_HIRE_STEP.SLOTS);

  function returnToHourlyPreSearch() {
    if (typeof onBackToHourlySearch === "function") {
      onBackToHourlySearch();
      return true;
    }
    onClose?.();
    return true;
  }

  // Shift can change until the caregiver request is sent (no live booking yet).
  const canChangeShift =
    shiftChosen &&
    !confirmedBooking &&
    !reselectBookingId &&
    step <= CAREHUB_HIRE_STEP.CONFIRM &&
    wizardShiftOptions.length > 1;
  const showShiftPicker =
    (!shiftChosen || shiftPickerOpen) &&
    !confirmedBooking &&
    !reselectBookingId &&
    step <= CAREHUB_HIRE_STEP.CONFIRM;
  const currentShiftMeta = shiftChoiceMeta(
    searchShift,
    wizardShiftOptions.find((o) => o.value === searchShift)?.label
  );

  function openShiftPicker() {
    if (busy || paying || deployBusy) return;
    setShiftPickerValue(searchShift);
    setShiftPickerOpen(true);
  }

  function applyShiftChoice(nextShift) {
    const next = String(nextShift || "").trim();
    if (!next) {
      showToast.error("Select the type of care you need to continue.");
      return;
    }
    if (shiftChosen && next === searchShift) {
      setShiftPickerOpen(false);
      return;
    }
    // Hourly on the marketplace needs its date/time search first — hand back to the page.
    if (next === "hourly" && typeof onChooseHourlyShift === "function") {
      setShiftPickerOpen(false);
      onChooseHourlyShift(next);
      return;
    }
    const hadChoice = shiftChosen;
    // Slots, matches, pricing and Care Pass depend on the shift — recompute; keep patient/address.
    clearGeneratedAvailability();
    setEstimate(null);
    setSelectedCaregiver(
      caregiver ? { ...caregiver, caregiverId: caregiver.id || caregiver.caregiverId } : null
    );
    setWizardShift(next);
    setShiftChosen(true);
    setShiftPickerOpen(false);
    if (hadChoice) setShiftChangedInWizard(true);
    if (selectedPatientId && step > CAREHUB_HIRE_STEP.PATIENT) {
      shiftRecheckRef.current = true;
    }
    if (hadChoice) {
      showToast.success(`Service changed to ${shiftChoiceMeta(next).label}. Please choose your date and time again.`);
    }
  }

  // After a shift change, re-run the patient → Care Pass → schedule gate for the new shift.
  useEffect(() => {
    if (!open || !shiftRecheckRef.current) return;
    shiftRecheckRef.current = false;
    continueFromPatientStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, searchShift]);

  function goHireWizardBack() {
    if (busy || paying || deployBusy) return;
    if (showShiftPicker) {
      if (shiftChosen) setShiftPickerOpen(false);
      else onClose?.();
      return;
    }
    userWizardBackRef.current = true;
    switch (step) {
      case CAREHUB_HIRE_STEP.PATIENT:
        onClose?.();
        break;
      case CAREHUB_HIRE_STEP.CARE_PASS:
        // Care Pass follows Patient in the wizard — back always returns to patient select.
        leaveCarePassStep({ resumeTo: CAREHUB_HIRE_STEP.PATIENT });
        break;
      case CAREHUB_HIRE_STEP.SHIFT: {
        if (skipShiftFromMarketplace) {
          returnToHourlyPreSearch();
          break;
        }
        const draft = readBookingDraft() || {};
        // If they left Care Pass to edit dates, don't bounce them straight back into Care Pass.
        if (draft.resumeStepAfterCarePass || !includeCarePassStep) {
          clearGeneratedAvailability();
          setStep(CAREHUB_HIRE_STEP.PATIENT);
        } else {
          setStep(CAREHUB_HIRE_STEP.CARE_PASS);
        }
        break;
      }
      case CAREHUB_HIRE_STEP.SLOTS:
        if (skipShiftFromMarketplace) {
          returnToHourlyPreSearch();
          break;
        }
        // Same screen as SHIFT — back just clears generated slots so the schedule UI stays.
        setAvailableSlots([]);
        setSelectedSlot(null);
        setMatches([]);
        clearSelectionValidation();
        setStep(CAREHUB_HIRE_STEP.SHIFT);
        break;
      case CAREHUB_HIRE_STEP.CONFIRM:
        setStep(CAREHUB_HIRE_STEP.SLOTS);
        break;
      case CAREHUB_HIRE_STEP.AGREEMENT:
        setStep(CAREHUB_HIRE_STEP.SLOTS);
        break;
      case CAREHUB_HIRE_STEP.PRV:
        setStep(CAREHUB_HIRE_STEP.AGREEMENT);
        break;
      case CAREHUB_HIRE_STEP.DONE:
        setStep(offerPoliceVerification ? CAREHUB_HIRE_STEP.PRV : CAREHUB_HIRE_STEP.AGREEMENT);
        break;
      default:
        onClose?.();
        break;
    }
  }

  if (!open || !caregiver) return null;

  return createPortal(
    <>
    <DuplicateActiveEngagementModal
      open={Boolean(duplicateEngagement)}
      existingBookingId={duplicateEngagement?.existingBookingId}
      onClose={() => setDuplicateEngagement(null)}
    />
    <div className="cabd-backdrop" role="dialog" aria-modal="true" aria-label={serviceStepTitle}>
      <div
        className={`cabd${
          careRole === "nurses" ||
          step === CAREHUB_HIRE_STEP.CARE_PASS ||
          step === CAREHUB_HIRE_STEP.CONFIRM ||
          step === CAREHUB_HIRE_STEP.AGREEMENT ||
          step === CAREHUB_HIRE_STEP.PRV
            ? " cabd--wide"
            : ""
        }${step === CAREHUB_HIRE_STEP.CARE_PASS ? " cabd--carepass" : ""}${
          step >= CAREHUB_HIRE_STEP.CONFIRM ? " cabd--flow" : ""
        }`}
      >
        <button
          type="button"
          className="cabd__back"
          onClick={goHireWizardBack}
          disabled={busy || paying || deployBusy}
          aria-label="Back to previous screen"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          <span>Back</span>
        </button>
        <button type="button" className="cabd__close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        {showShiftPicker ? (
          <div className="cabd__body cabd__body--shift-picker">
            <div className="cabd__step-head">
              <h2 className="cabd__title cabd__title--step">
                {shiftChosen ? "Change service type" : "Select service & shift type"}
              </h2>
            </div>
            <p className="cabd__shift-picker-lead">
              {shiftChosen
                ? "Choose the care you need. Your patient and address stay the same — you will pick the date and time again."
                : "What type of care do you need? You can change this later, before you confirm the booking."}
            </p>
            <div className="cabd__shift-options" role="radiogroup" aria-label="Service and shift type">
              {wizardShiftOptions.map((opt) => {
                const meta = shiftChoiceMeta(opt.value, opt.label);
                const selected = shiftPickerValue === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`cabd__shift-option${selected ? " is-selected" : ""}`}
                    onClick={() => setShiftPickerValue(opt.value)}
                  >
                    <span className="cabd__shift-option-icon">{meta.icon}</span>
                    <span className="cabd__shift-option-text">
                      <span className="cabd__shift-option-title">{meta.label}</span>
                      {meta.hint ? <span className="cabd__shift-option-hint">{meta.hint}</span> : null}
                    </span>
                    <span className="cabd__shift-option-check" aria-hidden="true">
                      {selected ? (
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="3">
                          <path d="M5 12l5 5L20 7" />
                        </svg>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="cabd__shift-picker-actions">
              {shiftChosen ? (
                <button
                  type="button"
                  className="cabd__btn cabd__btn--ghost"
                  onClick={() => setShiftPickerOpen(false)}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  <span>Keep {currentShiftMeta.label}</span>
                </button>
              ) : null}
              <button
                type="button"
                className="cabd__btn cabd__btn--primary"
                disabled={!shiftPickerValue || busy}
                onClick={() => applyShiftChoice(shiftPickerValue)}
              >
                <span>Continue</span>
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
            {!shiftPickerValue ? (
              <p className="cabd__shift-picker-note" role="status">
                Select a service type to continue.
              </p>
            ) : null}
          </div>
        ) : null}

        {!showShiftPicker ? (
        <>
        <div className="cabd__wizard-progress" aria-label="Booking workflow progress">
          <div className="cabd__wizard-top">
            <p className="cabd__wizard-counter">
              Step {hireWizardProgress.step} of {hireWizardProgress.total}
            </p>
            <p className="cabd__wizard-current" aria-current="step">
              {hireWizardProgress.steps.find((s) => s.state === "current")?.label || ""}
            </p>
          </div>
          <div
            className="cabd__wizard-bar"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={hireWizardProgress.total}
            aria-valuenow={hireWizardProgress.step}
            aria-label="Booking progress"
          >
            <span
              className="cabd__wizard-bar-fill"
              style={{
                width: `${Math.max(
                  8,
                  (hireWizardProgress.step / hireWizardProgress.total) * 100
                )}%`,
              }}
            />
          </div>
          <ol
            className="cabd__wizard-steps"
            style={{ "--wizard-step-count": hireWizardProgress.steps.length }}
          >
            {hireWizardProgress.steps.map((s, index) => (
              <li
                key={s.id}
                className={`cabd__wizard-step cabd__wizard-step--${s.state}`}
                aria-current={s.state === "current" ? "step" : undefined}
              >
                <span className="cabd__wizard-step-dot" aria-hidden="true">
                  {s.state === "done" ? (
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M5 12l5 5L20 7" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="cabd__wizard-step-label">{s.label}</span>
              </li>
            ))}
          </ol>
        </div>

        {canChangeShift ? (
          <div className="cabd__shift-summary">
            <span className="cabd__shift-summary-icon">{currentShiftMeta.icon}</span>
            <span className="cabd__shift-summary-text">
              <span className="cabd__shift-summary-label">Service type</span>
              <strong className="cabd__shift-summary-value">{currentShiftMeta.label}</strong>
            </span>
            <button
              type="button"
              className="cabd__btn cabd__btn--outline cabd__shift-summary-change"
              onClick={openShiftPicker}
              disabled={busy || paying || deployBusy}
              aria-label={`Change service type (currently ${currentShiftMeta.label})`}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
              </svg>
              <span>Change</span>
            </button>
          </div>
        ) : null}

        {step === CAREHUB_HIRE_STEP.SHIFT || step === CAREHUB_HIRE_STEP.SLOTS ? (
          <div className="cabd__body">
            <div className="cabd__step-head">
              <h2 className="cabd__title cabd__title--step">
                {hireFromMarketplaceSearch ? "Confirm your Care Attendant" : serviceStepTitle}
              </h2>
            </div>
            {hireFromMarketplaceSearch ? (
              <p className="cabd__info" role="status">
                Your service package, date, and time are already set from search. Review the Care
                Attendant below and continue.
              </p>
            ) : null}
            {!hireFromMarketplaceSearch ? (
              <>
            {showHourlyServicePackages || careRole === "physio" ? (
              <CareHubBookingServiceStep
                role={careRole}
                caregiver={caregiver}
                packageKey={packageKey}
                onPackageKeyChange={(key) => {
                  setPackageKey(key);
                  clearGeneratedAvailability();
                  setStep(CAREHUB_HIRE_STEP.SHIFT);
                }}
                tierId={tierId}
                onTierIdChange={setTierId}
                procedureId={procedureId}
                onProcedureIdChange={(id) => {
                  setProcedureId(id);
                  clearGeneratedAvailability();
                  setStep(CAREHUB_HIRE_STEP.SHIFT);
                }}
                procedureQuery={procedureQuery}
                onProcedureQueryChange={setProcedureQuery}
                sessionId={sessionId}
                onSessionIdChange={(id) => {
                  setSessionId(id);
                  clearGeneratedAvailability();
                  setStep(CAREHUB_HIRE_STEP.SHIFT);
                }}
              />
            ) : (
              <div className="cabd__service-step">
                <p className="cabd__instant-note cabd__instant-note--muted">
                  {serviceSelection.serviceLabel}. Choose a scheduling pattern and date, then generate
                  slots.
                </p>
              </div>
            )}

            <h3 className="cabd__section-label">Scheduling pattern</h3>
            <div className="cabd__patterns">
              {PATTERNS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`cabd__pattern${pattern === p.id ? " is-selected" : ""}`}
                  onClick={() => onPatternChange(p.id)}
                >
                  <span className={`cabd__pattern-badge cabd__pattern-badge--${p.badgeTone}`}>{p.badge}</span>
                  <strong>{p.title}</strong>
                  <span>{p.description}</span>
                </button>
              ))}
            </div>

            {pattern === "recurring" ? (
              <div className="cabd__recurring">
                <h3 className="cabd__section-label">No. of days service required</h3>
                <div className="cabd__days-required" role="group" aria-label="Number of days service required">
                  <p className="cabd__days-required-hint">
                    How many caregiver days do you need for this recurring booking?
                    {daysServiceRequired > 30
                      ? " Continuous care beyond 30 days needs a Quarterly Care Pass."
                      : ""}
                  </p>
                  <div className="cabd__days-counter">
                    <button
                      type="button"
                      className="cabd__days-counter-btn"
                      aria-label="Decrease days"
                      disabled={daysServiceRequired <= DAYS_SERVICE_REQUIRED_MIN}
                      onClick={() => bumpDaysServiceRequired(-1)}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      className="cabd__days-counter-input"
                      min={DAYS_SERVICE_REQUIRED_MIN}
                      max={DAYS_SERVICE_REQUIRED_MAX}
                      value={daysServiceRequired}
                      aria-label="Days of service required"
                      onChange={(e) =>
                        setDaysServiceRequired(
                          clampDaysServiceRequired(e.target.value, daysServiceRequired)
                        )
                      }
                    />
                    <button
                      type="button"
                      className="cabd__days-counter-btn"
                      aria-label="Increase days"
                      disabled={daysServiceRequired >= DAYS_SERVICE_REQUIRED_MAX}
                      onClick={() => bumpDaysServiceRequired(1)}
                    >
                      +
                    </button>
                    <span className="cabd__days-counter-unit">
                      day{daysServiceRequired === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>

                <h3 className="cabd__section-label cabd__section-label--accent">Recurring settings</h3>
                <div className="cabd__recurring-opts">
                  {RECURRING_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`cabd__recurring-opt${recurringPattern === opt.id ? " is-selected" : ""}`}
                      onClick={() => setRecurringPattern(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {autoRecurring ? (
                  <p className="cabd__recurring-hint">
                    {recurringPattern === "daily"
                      ? `System will auto-reserve ${daysServiceRequired} consecutive daily slot${
                          daysServiceRequired === 1 ? "" : "s"
                        } starting from your chosen date.`
                      : `System will auto-reserve the next ${daysServiceRequired} weekday slot${
                          daysServiceRequired === 1 ? "" : "s"
                        } (Mon–Fri) starting from your chosen date.`}
                  </p>
                ) : null}

                {showWeeklyDay ? (
                  <div className="cabd__recurring-block">
                    <h4 className="cabd__recurring-sub">Which day of the week?</h4>
                    <div className="cabd__weekday-row" role="group" aria-label="Weekly booking day">
                      {WEEKDAY_NAMES.map((label, dow) => (
                        <button
                          key={label}
                          type="button"
                          className={`cabd__weekday${weeklyDayOfWeek === dow ? " is-selected" : ""}`}
                          onClick={() => setWeeklyDayOfWeek(dow)}
                        >
                          {label.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                    <p className="cabd__recurring-hint">
                      System will reserve 5 weekly visits on {WEEKDAY_NAMES[weeklyDayOfWeek]}s from your start date.
                    </p>
                  </div>
                ) : null}

                {showDatePicker ? (
                  <div className="cabd__recurring-block">
                    <h4 className="cabd__recurring-sub">
                      {recurringPattern === "alternate" ? "Select alternate dates" : "Select custom dates"}
                    </h4>
                    {isLiveIn ? (
                      <p className="cabd__instant-note cabd__instant-note--muted">
                        Today is available when a caregiver is free for the next full 24 hours.
                      </p>
                    ) : (
                      <p className="cabd__instant-note cabd__instant-note--muted">
                        Today is available — only time slots that have already passed are disabled.
                      </p>
                    )}
                    <BookingAvailabilityCalendar
                      minDate={minTargetDate}
                      maxDate={maxWindowDate}
                      multi
                      selectedDates={selectedDates}
                      daysByDate={calendarDaysByDate}
                      loading={calendarLoading}
                      error={calendarError}
                      onSelect={toggleMultiCalendarDate}
                      onRetry={() => setCalendarReloadKey((k) => k + 1)}
                      ariaLabel="Select custom booking dates"
                    />
                    {selectedDates.length ? (
                      <div className="cabd__date-chips">
                        {selectedDates.map((iso) => (
                          <span key={iso} className="cabd__date-chip">
                            {formatDisplayDate(iso)}
                            <button type="button" aria-label={`Remove ${iso}`} onClick={() => removeCustomDate(iso)}>
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="cabd__recurring-hint">
                        Tap available dates below — up to {daysServiceRequired} day
                        {daysServiceRequired === 1 ? "" : "s"} for this booking pattern.
                      </p>
                    )}
                    <div className="cabd__date-row">
                      <button
                        type="button"
                        className="cabd__btn cabd__btn--primary"
                        disabled={
                          loadingSlots ||
                          !selectedDates.length ||
                          calendarLoading ||
                          !calendarLoaded ||
                          Boolean(calendarError)
                        }
                        onClick={generateSlots}
                      >
                        {loadingSlots ? "Generating…" : "Generate slots"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {!(pattern === "recurring" && showDatePicker) ? (
              <>
                <h3 className="cabd__section-label">Select date</h3>
                {pattern === "instant" ? (
                  <p className="cabd__instant-note">Express dispatch uses today. Future dates are blocked for instant booking.</p>
                ) : (
                  <p className="cabd__instant-note cabd__instant-note--muted">
                    {isLiveIn
                      ? "Today is available when a caregiver is free for the next full 24 hours."
                      : "Today is available — only time slots that have already passed are disabled."}
                  </p>
                )}
                <BookingAvailabilityCalendar
                  minDate={minTargetDate}
                  maxDate={maxTargetDate}
                  value={targetDate}
                  daysByDate={calendarDaysByDate}
                  loading={calendarLoading}
                  error={calendarError}
                  disabled={pattern === "instant"}
                  onSelect={selectSingleCalendarDate}
                  onRetry={() => setCalendarReloadKey((k) => k + 1)}
                  ariaLabel="Select booking date"
                />
                <div className="cabd__date-row">
                  <button
                    type="button"
                    className="cabd__btn cabd__btn--primary"
                    disabled={
                      loadingSlots ||
                      calendarLoading ||
                      !calendarLoaded ||
                      Boolean(calendarError)
                    }
                    onClick={generateSlots}
                  >
                    {loadingSlots ? "Generating…" : "Generate slots"}
                  </button>
                </div>
              </>
            ) : null}
              </>
            ) : null}

            {/* Same schedule screen — slots appear inline after Generate (no layout swap). */}
            {step === CAREHUB_HIRE_STEP.SLOTS || slots.length > 0 ? (
              <div className="cabd__slots-inline" id="cabd-slots">
                <h3 className="cabd__section-label">
                  {hireFromMarketplaceSearch ? "Your booking" : "Select time"}
                </h3>
                {!hireFromMarketplaceSearch ? <div className="cabd__info">{slotHint}</div> : null}
                {occurrenceSummary && bookingCareDays > 1 ? (
                  <p className="cabd__occurrence-summary">
                    {occurrenceSummary}
                    {carePassDaysRemaining != null ? (
                      <>
                        {" "}
                        · Care Pass: {carePassDaysRemaining} day
                        {carePassDaysRemaining === 1 ? "" : "s"} left
                      </>
                    ) : null}
                  </p>
                ) : null}
                {occurrenceSummary && bookingCareDays > 1 && !carePassCoversBooking ? (
                  <p className="cabd__info cabd__info--warn" role="status">
                    {carePassMissingForBookingMessage(
                      patientSubscription,
                      searchShift,
                      bookingCareDays
                    )}
                  </p>
                ) : null}

                {hireFromMarketplaceSearch && selectedSlot ? (
                  <p className="cabd__occurrence-summary" role="status">
                    {formatDisplayDate(targetDate || searchPrefill?.targetDate || "")} ·{" "}
                    <strong>
                      {selectedSlot.label ||
                        `${selectedSlot.start}–${selectedSlot.end}`}
                    </strong>
                  </p>
                ) : null}

                {!hireFromMarketplaceSearch && isLiveIn ? (
                  <div className="cabd__livein-tabs" role="tablist" aria-label="Live-in booking">
                    <button
                      type="button"
                      className="cabd__livein-tab is-active"
                      role="tab"
                      aria-selected="true"
                    >
                      24 hours live-in
                    </button>
                    <p className="cabd__livein-tab-hint">Select a start time (8:00 AM – 6:00 PM)</p>
                  </div>
                ) : null}

                {!hireFromMarketplaceSearch ? (
                <>
                <div className="cabd__slots">
                  {slots.length > 0 ? (
                    slots.map((slot) => {
                      const selected =
                        selectedSlot?.id === slot.id ||
                        (selectedSlot?.start === slot.start && selectedSlot?.end === slot.end);
                      const ownBookedSlot = isOwnActiveBookingSlot(
                        confirmedBooking,
                        confirmedBooking?.requestedCaregiverId,
                        slot
                      );
                      const hasAvailability = slot.available !== false || ownBookedSlot;
                      return (
                        <button
                          key={slot.id}
                          type="button"
                          className={`cabd__slot${selected ? " is-selected" : ""}${
                            !hasAvailability ? " is-unavailable" : ""
                          }${
                            ownBookedSlot && confirmedBooking?.caregiverAccepted
                              ? " is-booked-ok"
                              : ""
                          }`}
                          disabled={validatingSlot || !hasAvailability}
                          onClick={() => onPickSlot(slot)}
                          title={
                            ownBookedSlot
                              ? confirmedBooking?.caregiverAccepted
                                ? "Your caregiver accepted this time — continue with Proceed."
                                : "Your care request is held on this time."
                              : hasAvailability
                                ? undefined
                                : "No caregiver is free for this start time. Try another time or caregiver."
                          }
                        >
                          {slot.label}
                        </button>
                      );
                    })
                  ) : (
                    <p className="cabd__empty">
                      No open slots for this date. Try another day, a different procedure, or adjust
                      your service above.
                    </p>
                  )}
                </div>
                {slots.length > 0 &&
                !slots.some(
                  (s) =>
                    s.available !== false ||
                    isOwnActiveBookingSlot(
                      confirmedBooking,
                      confirmedBooking?.requestedCaregiverId,
                      s
                    )
                ) ? (
                  <p className="cabd__empty" style={{ marginTop: "0.75rem" }}>
                    {confirmedBooking?.caregiverAccepted
                      ? "Your caregiver already accepted this request. Use Proceed to continue, or pick another start time to move the booking."
                      : isLiveIn
                        ? "No caregiver is free for a full 24-hour live-in on this date. Try another day or go back and pick a different caregiver."
                        : "All listed times are taken. Try another day or caregiver."}
                  </p>
                ) : null}
                {validatingSlot ? (
                  <p className="cabd__muted" style={{ marginTop: "0.65rem" }} role="status">
                    Checking caregiver availability…
                  </p>
                ) : null}
                </>
                ) : null}

                {selectedSlot ? (
                  <>
                    {confirmedBooking?.caregiverDeclined ? (
                      <p className="cabd__info cabd__info--warn" role="status">
                        Your Care Attendant declined this request for{" "}
                        {confirmedBooking.dateLabel || formatDisplayDate(confirmedBooking.serviceDate)}{" "}
                        at {confirmedBooking.timeLabel || selectedSlot.label}. Your date, shift, and
                        time stay the same — send a request to one of the alternatives below.
                      </p>
                    ) : preferredUnavailableForSlot ? (
                      <p className="cabd__info cabd__info--warn" role="status">
                        Your selected Care Attendant is not free for this start time
                        {preferredHireCaregiver?.name ? ` (${preferredHireCaregiver.name})` : ""}. Other available attendants
                        are listed below — or pick another start time.
                      </p>
                    ) : null}
                    <h3 className="cabd__section-label">
                      {confirmedBooking?.caregiverDeclined
                        ? "Alternative Care Attendants"
                        : preferredUnavailableForSlot
                          ? "Other available caregivers"
                          : preferredHireCaregiver
                            ? "Selected Care Attendant"
                            : "Available caregivers"}
                    </h3>
                    {loadingReplacements ? (
                      <p className="cabd__muted" role="status">
                        Finding alternative Care Attendants for your booking…
                      </p>
                    ) : null}
                    {confirmedBooking?.awaitingCaregiverAcceptance ? (
                      <div className="cabd__pending-bar" role="status">
                        <p>
                          Request sent — waiting for the Care Attendant to accept duty on their
                          dashboard. WhatsApp is not required.
                        </p>
                        {holdCountdownLabel ? (
                          <p className="cabd__hold-timer" aria-live="polite">
                            Booking held for {holdCountdownLabel}
                          </p>
                        ) : null}
                        <button
                          type="button"
                          className="cabd__btn cabd__btn--ghost"
                          disabled={busy || paying}
                          onClick={cancelPendingCareRequest}
                        >
                          {busy ? "Cancelling…" : "Cancel request"}
                        </button>
                      </div>
                    ) : null}
                    {visibleCaregivers.length === 0 ? (
                      <p className="cabd__empty">
                        {confirmedBooking?.caregiverDeclined
                          ? loadingReplacements
                            ? "Searching for alternatives…"
                            : "No other Care Attendants are available for this date and time. Try another slot or contact support."
                          : "No caregivers free for this slot. Try another time."}
                      </p>
                    ) : (
                      <div className="cabd__caregivers">
                        {visibleCaregivers.map((m) => {
                          const selected =
                            String(selectedCaregiver?.caregiverId) === String(m.caregiverId);
                          const visitFee = resolveCaregiverVisitFeeInr(m, searchShift, null);
                          const packageFeeTotal =
                            visitFee != null ? visitFee * bookingCareDays : null;
                          const skills = Array.isArray(m.skills)
                            ? m.skills
                            : Array.isArray(m.certifications)
                              ? m.certifications
                              : ["Home Health Aide (HHA)", "First Aid Certified"];
                          const requestForThis =
                            confirmedBooking &&
                            String(confirmedBooking.requestedCaregiverId) === String(m.caregiverId);
                          const requestAccepted = Boolean(
                            requestForThis &&
                              confirmedBooking.caregiverAccepted &&
                              confirmedBookingMatchesSlot(confirmedBooking, selectedSlot)
                          );
                          const requestDeclined = Boolean(
                            requestForThis && confirmedBooking.caregiverDeclined
                          );
                          const requestPending = Boolean(
                            requestForThis &&
                              confirmedBooking.awaitingCaregiverAcceptance &&
                              confirmedBookingMatchesSlot(confirmedBooking, selectedSlot)
                          );
                          return (
                            <article
                              key={m.caregiverId}
                              className={`cabd__cg${selected ? " is-selected" : ""}${
                                requestAccepted ? " is-accepted" : ""
                              }${requestDeclined ? " is-declined" : ""}${
                                requestPending ? " is-pending" : ""
                              }`}
                              onClick={() => selectCaregiverForSlot(m)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  selectCaregiverForSlot(m);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <div className="cabd__cg-left">
                                <ProfileAvatar
                                  name={m.name}
                                  imageUrl={m.profileImageUrl}
                                  style={{ width: 56, height: 56, fontSize: "1rem" }}
                                />
                                <div>
                                  <strong>{m.name}</strong>
                                  <span className="cabd__cg-id">
                                    {formatNcidForDisplay(resolveNpidCode(m.ncid || m.npid)) || `INC-${m.caregiverId}`}
                                  </span>
                                  <div className="cabd__cg-meta">
                                    ★ {Number(m.rating || 0).toFixed(1)}
                                    {m.distanceKm != null
                                      ? ` · ${Number(m.distanceKm).toFixed(1)} km away`
                                      : ""}
                                    <em>Travel Buffer: {m.travelBufferMin || 25} mins</em>
                                  </div>
                                  <div className="cabd__cg-tags">
                                    {skills.slice(0, 2).map((s) => (
                                      <span key={String(s)}>
                                        {typeof s === "string" ? s : s.label || s.name}
                                      </span>
                                    ))}
                                  </div>
                                  {requestForThis ? (
                                    <p
                                      className={`cabd__cg-status${
                                        requestAccepted
                                          ? " is-accepted"
                                          : requestDeclined
                                            ? " is-declined"
                                            : " is-pending"
                                      }`}
                                    >
                                      {requestAccepted
                                        ? "Caregiver accepted"
                                        : requestDeclined
                                          ? "Caregiver rejected"
                                          : confirmedBooking?.caregiverAccepted &&
                                              selectedSlot &&
                                              !confirmedBookingMatchesSlot(
                                                confirmedBooking,
                                                selectedSlot
                                              )
                                            ? `Accepted for ${
                                                confirmedBooking.timeLabel ||
                                                confirmedBooking.startTime ||
                                                "another start"
                                              } — pick that time or a new start to move it`
                                            : acceptPollBusy
                                              ? "Checking response…"
                                              : "Request sent · awaiting response"}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                              <div className="cabd__cg-right">
                                <div className="cabd__cg-fee">
                                  {packageFeeTotal != null ? formatInr(packageFeeTotal) : "—"}
                                </div>
                                <span>
                                  {packageFeeTotal == null
                                    ? "Rate set by caregiver"
                                    : bookingCareDays > 1
                                      ? `${formatInr(visitFee)}/day × ${bookingCareDays} days · Pay at home`
                                      : "Caregiver Fee (Pay at home)"}
                                </span>
                                {requestAccepted ? (
                                  <span className="cabd__cg-select is-accepted" aria-live="polite">
                                    Accepted
                                  </span>
                                ) : requestDeclined ? (
                                  <button
                                    type="button"
                                    className="cabd__cg-select is-declined"
                                    disabled={validatingSlot || busy || paying}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setConfirmedBooking(null);
                                      selectCaregiverForSlot(m);
                                    }}
                                  >
                                    Rejected · Pick again
                                  </button>
                                ) : requestPending ? (
                                  <div className="cabd__cg-pending-actions">
                                    <span className="cabd__cg-select is-pending" aria-live="polite">
                                      Awaiting accept…
                                    </span>
                                    <button
                                      type="button"
                                      className="cabd__cg-select is-cancel"
                                      disabled={busy || paying}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        cancelPendingCareRequest();
                                      }}
                                    >
                                      Cancel request
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className={`cabd__cg-select${selected ? " is-on" : ""}`}
                                    disabled={validatingSlot || busy || paying}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      sendRequestForCaregiver(m);
                                    }}
                                  >
                                    {paying ? "Sending…" : busy ? "Please wait…" : "Send request"}
                                  </button>
                                )}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    )}

                    <div className="cabd__footer">
                      <button
                        type="button"
                        className="cabd__btn cabd__btn--primary"
                        disabled={!canProceedFromSlots}
                        onClick={proceedFromSlots}
                      >
                        {paying
                          ? "Sending request…"
                          : busy || validatingSlot
                            ? "Please wait…"
                            : careRequestPendingForSlot
                              ? "Waiting for acceptance…"
                              : caregiverRequestAccepted
                                ? "Proceed to agreement"
                                : "Send request above to continue"}
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {step === CAREHUB_HIRE_STEP.PATIENT ? (
          <div className="cabd__body cabd__body--narrow">
            <PatientSelectStep
              patients={patients}
              selectedPatientId={selectedPatientId}
              loading={patientsLoading}
              subscription={patientSubscription}
              searchShift={searchShift}
              busy={busy}
              onSelect={async (p) => {
                const id = String(p?.patientId || p?.patient_id || "").trim();
                if (!id) {
                  showToast.error("This patient record is missing a Patient ID. Re-open Manage patients.");
                  return;
                }
                const nextGender = caregiverGenderFilterFromPatient(p);
                setSelectedPatientId(id);
                if (id !== String(selectedPatientId || "")) {
                  clearGeneratedAvailability();
                }
                if (
                  nextGender &&
                  caregiver &&
                  resolveMatchGender(caregiver) &&
                  resolveMatchGender(caregiver) !== nextGender
                ) {
                  setSelectedCaregiver(null);
                  clearSelectionValidation();
                }
                await refreshPatientSubscription(id);
              }}
              onContinue={continueFromPatientStep}
            />
          </div>
        ) : null}

        {step === CAREHUB_HIRE_STEP.CARE_PASS ? (
          <div className="cabd__body cabd__body--carepass">
            <BookingCarePassStep
              patientName={selectedPatient?.name || selectedPatientId}
              searchShift={searchShift}
              bookingDays={bookingCareDays}
              busy={busy}
              onChoosePlan={continueFromCarePassStep}
            />
          </div>
        ) : null}

        {step === CAREHUB_HIRE_STEP.CONFIRM ? (
          <div className="cabd__body cabd__body--flow">
            <BookingSummaryStep
              caregiverName={selectedCaregiver?.name}
              caregiverFeeInr={totalCaregiverFeeInr}
              visitFeeInr={visitCaregiverFeeInr}
              bookingDays={bookingCareDays}
              patientName={selectedPatient?.name || selectedPatientId}
              patientMeta={selectedPatient?.age ? `Age ${selectedPatient.age}` : null}
              patientAddress={
                addressLabel ||
                selectedPatient?.address ||
                selectedPatient?.serviceAddress ||
                ""
              }
              subscriptionLabel={patientSubscription?.planCode || "Service subscription"}
              subscriptionActive={Boolean(patientSubscription?.active)}
              subscriptionUntil={subscriptionUntilLabel}
              prvDeferred={offerPoliceVerification}
              prvRequired={offerPoliceVerification ? null : false}
              offerPoliceVerification={offerPoliceVerification}
              platformFeeInr={platformFee}
              gstInr={platformGst}
              totalPayNowInr={totalPayNow}
              confirmDetails={buildCustomerConfirmDetails()}
              onBack={() => setStep(CAREHUB_HIRE_STEP.SLOTS)}
              onConfirm={() => payAndConfirm()}
              confirming={paying}
              confirmLabel="Confirm & send request"
            />
          </div>
        ) : null}

        {step === CAREHUB_HIRE_STEP.AGREEMENT && confirmedBooking ? (
          <div className="cabd__body cabd__body--flow">
            <div className="pbs__head" style={{ marginBottom: "0.75rem" }}>
              <h2 className="pbs__title">Digital Care Agreement</h2>
              <p className="pbs__sub">
                Caregiver accepted. Confirm the service address and accept the agreement to continue.
              </p>
            </div>
            <DigitalCareAgreementPreview
              agreement={digitalAgreement}
              showActions={false}
            />
            <label className="cabd__ack-row">
              <input
                type="checkbox"
                checked={deployAddressOk}
                onChange={(e) => setDeployAddressOk(e.target.checked)}
              />
              <span>I confirm this service address is correct: {addressLabel || "Address on booking"}</span>
            </label>
            <label className="cabd__ack-row">
              <input
                type="checkbox"
                checked={agreementAcknowledged}
                onChange={(e) => setAgreementAcknowledged(e.target.checked)}
              />
              <span>
                I accept the NEMA care engagement e-agreement for this booking (customer, caregiver, date, and
                shift).
              </span>
            </label>
            <div className="bfl-prv__actions" style={{ marginTop: "1rem" }}>
              <button
                type="button"
                className="bfl-btn bfl-btn--ghost"
                onClick={() => setStep(CAREHUB_HIRE_STEP.SLOTS)}
              >
                Back
              </button>
              <button
                type="button"
                className="bfl-btn bfl-btn--primary"
                disabled={deployBusy || !agreementAcknowledged || !deployAddressOk}
                onClick={handleAgreementContinue}
              >
                {deployBusy ? "Confirming…" : "Accept & continue"}
              </button>
            </div>
          </div>
        ) : null}

        {step === CAREHUB_HIRE_STEP.PRV && confirmedBooking && offerPoliceVerification ? (
          <div className="cabd__body cabd__body--flow">
            <div className="pbs__head" style={{ marginBottom: "0.75rem" }}>
              <h2 className="pbs__title">Police Record Verification</h2>
              <p className="pbs__sub">
                Optional trust check after agreement. Purchase or skip to complete hiring.
              </p>
            </div>
            <PoliceRecordVerificationCard
              bookingId={confirmedBooking.bookingId}
              mode="deployment"
              deployment={confirmedBooking.booking}
              onChanged={(prv) => {
                setConfirmedBooking((prev) =>
                  prev
                    ? {
                        ...prev,
                        prvInitiated: Boolean(prv) || prev.prvInitiated,
                      }
                    : prev
                );
                clearBookingDraft();
                setStep(CAREHUB_HIRE_STEP.DONE);
              }}
            />
            <div className="bfl-prv__actions" style={{ marginTop: "1rem" }}>
              <button
                type="button"
                className="bfl-btn bfl-btn--ghost"
                onClick={() => setStep(CAREHUB_HIRE_STEP.AGREEMENT)}
              >
                Back
              </button>
              <button
                type="button"
                className="bfl-btn bfl-btn--primary"
                onClick={() => {
                  clearBookingDraft();
                  setStep(CAREHUB_HIRE_STEP.DONE);
                }}
              >
                Continue to Done
              </button>
            </div>
          </div>
        ) : null}

        {step === CAREHUB_HIRE_STEP.DONE && confirmedBooking ? (
          <div className="cabd__body">
            <BookingConfirmedStep
              bookingId={confirmedBooking.bookingId}
              caregiverName={confirmedBooking.caregiverName}
              patientName={confirmedBooking.patientName}
              dateLabel={confirmedBooking.dateLabel}
              timeLabel={confirmedBooking.timeLabel}
              subscriptionLabel={confirmedBooking.subscriptionLabel}
              prvInitiated={confirmedBooking.prvInitiated}
              offerPoliceVerification={offerPoliceVerification}
              awaitingCaregiverAcceptance={false}
              onDashboard={() => {
                onClose?.();
                navigate("/dashboard", {
                  replace: true,
                  state: { booking: confirmedBooking.booking },
                });
              }}
              onViewBooking={() => {
                onClose?.();
                navigate("/my-bookings", {
                  replace: true,
                  state: { booking: confirmedBooking.booking },
                });
              }}
            />
          </div>
        ) : null}
        </>
        ) : null}
      </div>
    </div>
    <SorryMessageModal
      open={shiftOverlapModalOpen}
      elevated
      title="We're sorry!"
      message={SHIFT_OVERLAP_PREFERENCE_MESSAGE}
      actionLabel="Select another Care Attendant"
      onClose={() => closeShiftOverlapPreferencePopup()}
      onAction={() => closeShiftOverlapPreferencePopup({ returnToPreferences: true })}
    />
    </>,
    document.body
  );
}
